// Neue Komponente: NewPackageModal.js

import { set } from 'idb-keyval';
import { useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { FaCamera, FaEye, FaEyeSlash, FaTrash } from 'react-icons/fa';
import Tesseract from 'tesseract.js';
import { useTranslation } from './hooks/useTranslation'; // 🌐 Mehrsprachigkeit
import RegionSelector from './components/RegionSelector';
import PackageThumbnail from './components/PackageThumbnail'; // 🖼️ gespeichertes Foto beim Bearbeiten
import ShelfQrScanner, { isShelfQrScanEnabled, setShelfQrScanEnabled } from './components/ShelfQrScanner'; // 📷 Regal-QR während der KI-Analyse
import SideBarcodeScanner from './components/SideBarcodeScanner'; // 📷 Nachscan für Etikett um die Ecke (Rot = Leitcode, Grün = Sendungsnummer)
import { usePremium } from './contexts/PremiumContext'; // ⭐ für KI-Tageslimit (Free vs. Premium)
import { kiScanAllowed, noteKiScanUsed, FREE_KI_DAILY_LIMIT } from './services/kiQuota';
import { Capacitor } from '@capacitor/core';
import { nativeCameraPermission } from './services/stationCamera'; // 📷 still verweigerte Kamera-Berechtigung erkennen
import InAppCamera, { isInAppCameraEnabled, setInAppCameraEnabled } from './components/InAppCamera';
import { isAutoRegalLetter, setAutoRegalLetter, getCarrierSortMode, getExplicitCarrierSort, getCarrierSortMap, extractHausnummer, isForwardingAddress } from './services/printerService'; // 🔤 Fach = Nachname-Buchstabe / 🚚 pro Paketdienst
import { autoRegalFach } from './services/regalUnterfach'; // 🔤 … bei großen Buchstaben mit Unterfach („B4")
import { isCaptureSenderOn } from './services/captureOptions'; // 📥 Absender mit speichern (Option)
import { reportError } from './services/errorReport'; // 🧯 Erkennungs-Probleme fürs Admin-Board
import { hasCustomFeature } from './services/customFeatures'; // 🧩 A–Z nur für freigeschaltete Konten
import { lookupName, platzhalterName, platzhalterCode, brauchtVintedNachfrage, ausVintedBildschirm, rememberPairs,
  isVintedSortOn, vintedFach, vintedCodeAusTracking, glsInVintedCode } from './services/vintedNames'; // 📦 Vinted-Namen
import { dpdAktiv, dpdAnnahmeAktiv, dpdAnnahme, dpdLookup, dpdCodeAusTracking, bekannterDpdName, vorladenStart as dpdVorladenStart } from './services/dpd'; // 📦 DPD Pickup-Namen & Annahme
import { barcodesAusFoto } from './services/barcodeDecode'; // 📋 Barcode-Stapel (Sonderfunktion)
import { addToBatch, dedupeCodes, doppelabgleichSendung, ergaenzeStrichcode, disableBatchBarcodesFeature, listBatch, klassifiziereScanCodes, isBatchCarrierEnabled, toggleBatchCarrierEnabled, normalisiereStapelDienst } from './services/barcodeBatch';
import SchadenErfassen from './components/SchadenErfassen'; // ⚠️ Schadensdoku (nur Bearbeiten)
import { loescheFoto } from './services/mlService';
import {
  translatePackageColor,
  bereinigeEmpfaengerName,
  berechneRegalSchnellwahl,
  regalGleich,
  normalizeRegalplatz as normalizeRegalplatzHelper,
  SUCH_SCAN_FORMATE,
  scanZuSuchtext,
} from './utils/packageMatchingHelpers'; // 🎨 Paketfarbe, Empfänger-Filter & Regal-Schnellwahl
import { seriesAllowed, isSeriesOn, setSeriesOn, seriesKameraFehlgeschlagen, merkeKameraFehler, darfAutoSpeichern, AUTO_SAVE_SEKUNDEN } from './services/seriesMode'; // ⚡ Serienmodus // 📷 Ausweich-Kamera (Galerie-statt-Kamera-Geräte)
import {
  searchAddressBook,
  findBestAddressBookMatch,
  getListNotifyMode,
  getNextAutoNumber,
  commitAutoNumberIfUsed,
  isAutoNumberEnabled,
  isIdFirstEnabled,
  isPrivacyConsentEnabled,
  getPrivacyConsentText,
} from './services/addressBookService'; // 📒 Adressbuch, #001 Nummerierung & Datenschutz
import { identifyForPickup } from './services/pickupIdentify'; // 🪪 Ausweis-Erkennung
import { useIntegrationIntake } from './integrations'; // 🧩 Modul-Architektur für Sonderkunden (z.B. Grenzpaket)
import { erkennePaketdienst } from './services/turboPaketPilot'; // 🚚 gemeinsame Paketdienst-Erkennung
import { grenzpaketAktiv } from './services/grenzpaket'; // 🇨🇭 Grenzpaket Schnelltipp-Leiste statt Regalfach-Textfeld
import { istDhlPaket } from './services/dhlBridge';
import {
  isWiesnAccount,
  isDhlStickEnabled,
  isPhotoSchnelleinlagerungEnabled,
  enqueueDhlStickSchnelleinlagerung,
  removeLastDhlStickItem,
  safeHomeDhlStick,
} from './services/dhlUsbStick';

/**
 * Verkleinert übergroße Fotos der System-Kamera (z. B. 12–20 MP / 3–6 MB) vor dem Upload
 * auf maxKante = 2600px (JPEG 0.86, ~450–750 KB).
 * - Fotos der In-App-Kamera (bereits <= 1.1 MB / <= 2560px) werden sofort unverändert durchgereicht (0ms).
 * - Nutzt synchrones canvas.toDataURL statt canvas.toBlob (vermeidet ältere Samsung-WebView-Hänger).
 * - Fällt bei jedem Fehler automatisch auf das Original-File zurück.
 */
async function verkleinereFuerUpload(rawFile, maxKante = 2600) {
  if (!rawFile || typeof rawFile.size !== 'number' || rawFile.size <= 1100 * 1024) {
    return rawFile;
  }
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    return rawFile;
  }
  let bmp = null;
  try {
    bmp = await createImageBitmap(rawFile);
    const w = bmp.width || 0;
    const h = bmp.height || 0;
    const groesste = Math.max(w, h);
    if (!groesste || (groesste <= maxKante && rawFile.size <= 1600 * 1024)) {
      if (bmp.close) bmp.close();
      return rawFile;
    }
    const scale = Math.min(1, maxKante / groesste);
    const nw = Math.max(1, Math.round(w * scale));
    const nh = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = nw;
    canvas.height = nh;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      if (bmp.close) bmp.close();
      return rawFile;
    }
    ctx.drawImage(bmp, 0, 0, nw, nh);
    if (bmp.close) bmp.close();
    bmp = null;

    const dataUrl = canvas.toDataURL('image/jpeg', 0.86);
    canvas.width = 1;
    canvas.height = 1;
    const commaIdx = dataUrl.indexOf(',');
    if (commaIdx < 0) return rawFile;
    const bstr = atob(dataUrl.slice(commaIdx + 1));
    let n = bstr.length;
    const u8 = new Uint8Array(n);
    while (n--) u8[n] = bstr.charCodeAt(n);
    const verkleinert = new Blob([u8], { type: 'image/jpeg' });
    if (verkleinert.size > 1024 && verkleinert.size < rawFile.size) {
      console.log(`⚡ Upload-Optimierung: ${w}x${h} (${Math.round(rawFile.size / 1024)}KB) → ${nw}x${nh} (${Math.round(verkleinert.size / 1024)}KB)`);
      return verkleinert;
    }
    return rawFile;
  } catch (err) {
    try { if (bmp && bmp.close) bmp.close(); } catch { /* ignore */ }
    return rawFile;
  }
}

function NewPackageModal({
  onClose,
  onSave,
  onDelete, // 🗑️ Neue Prop für Löschen-Funktion
  onDraftChange, // Neue Prop für Draft-Kommunikation
  onNeedPremium, // ⭐ öffnet die Paywall (KI-Tageslimit erreicht)
  checkRegalConflict, // ⚠️ prüft: gleiche Hausnummer im Nachbarfach? (liefert {hausnummer,regalplatz,name}|null)
  findSameRecipient, // 📦 hat dieser Empfänger schon Pakete im Regal? ({regalplatz,anzahl,name}|null)
  findSameTracking, // ⚠️ gleiche Sendungsnummer schon im Bestand? ({regalplatz,name}|null) — nur Hinweis
  forceNewPackage = false, // 🆕 Wenn true, wird Entwurf ignoriert
  colors,
  lang,
  texts,
  pakete = [], // 🆕 Paketliste für Löschen-Funktion
  // Existierende Paketdaten
  initialName = '',
  initialStreetAndNumber = '',
  initialPostalAndCity = '',
  initialRegalplatz = '',
  initialTrackingNumber = '',
  initialDatum = new Date().toISOString().slice(0, 10),
  initialAusgegebenAm = '',
  initialZurueckgeschickt = false,
  initialPaketAnzahl = 1, // 📦 Anzahl der Pakete (1-999)
  initialPalettenTyp = '', // 🪵 Paletten-/Gebinde-Typ (z. B. EU-Palette, Einwegpalette)
  initialBetrag = '', // 💰 Nachnahme/Geldbetrag bei Ausgabe
  initialFarbe = '', // 🎨 Paketfarbe
  initialGewicht = '', // ⚖️ Paketgewicht
  initialInternational = false, // 🌍 Internationales Paket
  initialSchaden = null, // ⚠️ Schadensdoku des bearbeiteten Pakets ({beschaedigt, notiz, fotos}|null)
  initialNotizen = '', // 📝 Notizen / Grenzpaket-Metadaten
  paketId = null, // ID des zu bearbeitenden Pakets
  // Neue Props für Favoriten
  favoriten = [],
  setFavoriten,
  // ... weitere Props
}) {
  // 🌐 Mehrsprachigkeits-Hook
  const { t, language } = useTranslation();
  const tr = (de, en, nl) => (language === 'nl' ? nl : language === 'en' ? en : de);
  
  // � DEBUG: Track mount/unmount und warum
  const mountIdRef = useRef(Math.random().toString(36).substring(7));
  useEffect(() => {
    console.log(`🆕 [DEBUG] NewPackageModal MOUNTED (ID: ${mountIdRef.current})`);
    console.log(`   forceNewPackage=${forceNewPackage}, paketId=${paketId}`);
    console.log(`   initialName="${initialName}", Foto vorhanden: ${!!initialTrackingNumber}`);
    
    return () => {
      console.log(`❌ [DEBUG] NewPackageModal UNMOUNTED (ID: ${mountIdRef.current})`);
      console.trace('   Stack trace:');
    };
  }, []);
  
  // �📝 DRAFT SYSTEM - State-Variablen
  const [hasDraft, setHasDraft] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const autoSaveTimerRef = useRef(null);

  // �️ DOPPEL-SPEICHERN VERHINDERN
  const [isSaving, setIsSaving] = useState(false);

  // ⭐ Premium-Status → entscheidet über das KI-Tageslimit (Free: begrenzt, Premium: frei)
  const { isPremium, recheckPremium } = usePremium();

  // 📱 ORIENTIERUNGSSPERRE - Hochformat immer gesperrt (kein UI-Schalter mehr nötig)
  const [lockPortraitMode] = useState(true);
  const [isCameraActive, setIsCameraActive] = useState(false);

  // 🤖 KI ist der Standard-Erfassungsmodus. Manuell (Tesseract + Canvas) läuft nur
  // noch automatisch als Fallback (offline, ML-Dienst aus, oder Free-Tageslimit erreicht).
  // Deshalb KEIN Umschalter und KEIN persistierter ocr_mode mehr.
  const [kiRemaining, setKiRemaining] = useState(() => kiScanAllowed(false).remaining);

  // ⚠️ Pflichtfeld-Hervorhebung (Name + Regalfach). Ersetzt die graue Browser-Sprechblase
  // durch ein sichtbares Highlight direkt am Feld — die KI füllt alles außer dem Fach,
  // deshalb ist das leere Fach der eine Schritt, der noch fehlt.
  const [invalidFields, setInvalidFields] = useState({});

  // State mit Initial-Werten setzen
  const [name, setName] = useState(initialName);
  const [streetAndNumber, setStreetAndNumber] = useState(initialStreetAndNumber);
  const [postalAndCity, setPostalAndCity] = useState(initialPostalAndCity);
  const [regalplatz, setRegalplatz] = useState(initialRegalplatz);
  const [trackingNumber, setTrackingNumber] = useState(initialTrackingNumber);
  const [camPermHint, setCamPermHint] = useState(false); // 📷 Kamera öffnet nicht → Berechtigungs-Hinweis
  const photoArrivedRef = useRef(false);
  const [inAppCam, setInAppCam] = useState(isInAppCameraEnabled()); // pro Gerät
  const [showInAppCam, setShowInAppCam] = useState(false);
  // ⚡ Serienmodus (Sonderfunktion): Kamera öffnet selbst, eindeutige Pakete speichern sich selbst
  const [seriesOn, setSeriesOnState] = useState(() => isSeriesOn());
  const seriesAutoCamRef = useRef(false);   // Kamera wurde vom Serienmodus geöffnet (nicht vom Nutzer)
  const serieAngefasstRef = useRef(false);  // Nutzer hat im Formular etwas berührt → kein Auto-Speichern
  const [autoSaveIn, setAutoSaveIn] = useState(null); // Rest-Sekunden oder null
  // Berührungen zählen erst, wenn das erkannte Ergebnis sichtbar ist (nicht der Auslöser-Tipp)
  const serieStoppen = () => { if (quickConfirm && !serieAngefasstRef.current) { serieAngefasstRef.current = true; setAutoSaveIn(null); } };
  const [datum, setDatum] = useState(initialDatum);
  const [ausgegebenAm, setAusgegebenAm] = useState(initialAusgegebenAm);
  const [zurueckgeschickt, setZurueckgeschickt] = useState(initialZurueckgeschickt);
  
  // 📦 MEHRFACH-PAKETE: Anzahl der Pakete für diese Person (1-999)
  const [paketAnzahl, setPaketAnzahl] = useState(initialPaketAnzahl || 1);
  const [paketAnzahlInput, setPaketAnzahlInput] = useState(() => (initialPaketAnzahl > 5 ? String(initialPaketAnzahl) : ''));
  // 🪵 PALETTEN-/GEBINDE-TYP (z. B. EU-Palette, Einwegpalette, Halbpalette, Gitterbox)
  const [palettenTyp, setPalettenTyp] = useState(initialPalettenTyp || '');
  const [showPaletteBar, setShowPaletteBar] = useState(() => {
    try {
      return Boolean(initialPalettenTyp) || hasCustomFeature('palettenvertrieb') || localStorage.getItem('kiosk_capture_palette') === '1';
    } catch {
      return Boolean(initialPalettenTyp) || hasCustomFeature('palettenvertrieb');
    }
  });
  
  // 💰🎨⚖️ ZUSATZFELDER (wie bei Quest VR)
  const [betrag, setBetrag] = useState(initialBetrag || ''); // Nachnahme/Geldbetrag
  const [farbe, setFarbe] = useState(initialFarbe || ''); // Paketfarbe
  const [gewicht, setGewicht] = useState(initialGewicht || ''); // Paketgewicht
  const [international, setInternational] = useState(initialInternational || false); // 🌍 Internationales Paket
  // ⚠️ Schadensdoku (nur Bearbeiten): mitgespeichert wird sie nur, wenn sie angefasst wurde
  const [schaden, setSchaden] = useState(initialSchaden || null);
  const schadenAngefasstRef = useRef(false);
  const schadenFotosNeuRef = useRef(new Set()); // in diesem Formular hochgeladen
  const schadenGespeichertRef = useRef(null);   // beim Speichern übernommene Fotos (null = nicht gespeichert)
  useEffect(() => () => {
    // Formular zu: hochgeladene, aber nicht gespeicherte Fotos wieder wegräumen
    const behalten = new Set(schadenGespeichertRef.current || []);
    for (const id of schadenFotosNeuRef.current) if (!behalten.has(id)) loescheFoto(id);
  }, []);

  // 🧩 Modul-Hook für Sonderkunden (z.B. Grenzpaket Kundenabgleich)
  const notizen = initialNotizen || '';
  const integrationIntake = useIntegrationIntake({
    initialName,
    initialStreetAndNumber,
    initialNotizen,
    name,
    setName,
    regalplatz,
    setRegalplatz,
    onStopSeries: () => { serieAngefasstRef.current = true; setAutoSaveIn(null); },
    onClearNameInvalid: () => setInvalidFields((f) => (f.name ? { ...f, name: false } : f)),
    onClearShelfInvalid: () => setInvalidFields((f) => (f.regalplatz ? { ...f, regalplatz: false } : f)),
  });
  
  // Weitere benötigte State-Variablen
  const [imageUrlInternal, setImageUrlInternal] = useState(null);
  
  // 🐛 DEBUG: Wrapper um setImageUrl zu tracken
  const setImageUrl = (value) => {
    const hasValue = !!value;
    const hadValue = !!imageUrlInternal;
    if (hadValue && !hasValue) {
      console.warn(`❌ [DEBUG] imageUrl wird GELÖSCHT!`);
      console.trace('Stack trace:');
    } else if (!hadValue && hasValue) {
      console.log(`📸 [DEBUG] imageUrl GESETZT (Foto aufgenommen)`);
    }
    setImageUrlInternal(value);
  };
  const imageUrl = imageUrlInternal;
  
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(2);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState(''); // Diese Zeile hinzufügen
  const paketPhotoInputRef = useRef(null);
  const streetInputRef = useRef(null); // Füge diese Zeile oben bei den anderen useRef hinzu

  // State für einklappbare Abschnitte
  const [visibleSections, setVisibleSections] = useState({
    addressSection: true,     // Straße
    postalSection: false,     // PLZ und Ort
    shelfSection: true,       // Regalplatz - Pflichtfeld, muss sichtbar sein
    trackingSection: false,   // Tracking-Nummer
    extrasSection: false,     // 💰🎨⚖️ Betrag, Farbe, Gewicht
    dateSection: false,       // Datum und Status
    favoritesList: true       // Favoriten-Liste - umbenannt von favoritesSection
  });
  
  // State für Doppelklick-Erkennung
  const [lastTapTimestamp, setLastTapTimestamp] = useState({});
  
  // 📝 DRAFT SYSTEM - Funktionen
  const isDraftMeaningful = () => {
    // Prüfe ob genug Daten vorhanden sind um als "Entwurf" zu gelten
    const hasName = name && name.trim().length > 2;
    const hasAddress = (streetAndNumber && streetAndNumber.trim().length > 2) || 
                       (postalAndCity && postalAndCity.trim().length > 2);
    const hasTracking = trackingNumber && trackingNumber.trim().length > 3;
    const hasShelf = regalplatz && regalplatz.trim().length > 0;
    const hasPhoto = imageUrl;
    
    // Mindestens 2 der wichtigen Felder müssen ausgefüllt sein
    const meaningfulFields = [hasName, hasAddress, hasTracking, hasShelf, hasPhoto].filter(Boolean).length;
    return meaningfulFields >= 2;
  };

  const saveDraft = () => {
    if (!isDraftMeaningful()) return;
    
    // 🔧 PRÜFE OB DATEN SICH GEÄNDERT HABEN (nur bei Bearbeitung)
    const isEditMode = !!(initialName || initialStreetAndNumber || initialTrackingNumber);
    
    if (isEditMode) {
      // Vergleiche aktuelle Daten mit Original-Daten
      const hasChanges = 
        name !== (initialName || '') ||
        streetAndNumber !== (initialStreetAndNumber || '') ||
        postalAndCity !== (initialPostalAndCity || '') ||
        trackingNumber !== (initialTrackingNumber || '') ||
        regalplatz !== (initialRegalplatz || '');
      
      if (!hasChanges) {
        console.log('⏸️ Keine Änderungen erkannt - Entwurf NICHT gespeichert');
        return; // Speichere KEINEN Entwurf, wenn nichts geändert wurde
      }
      
      console.log('📝 Änderungen erkannt, speichere Entwurf');
    }
    
    const currentData = {
      name,
      streetAndNumber,
      postalAndCity,
      trackingNumber,
      regalplatz,
      imageUrl,
      // 🛡️ auch die Extras sichern — sonst geht ein erfasster Nachnahme-Betrag beim Wiederherstellen verloren
      betrag, gewicht, farbe, international, paketAnzahl, palettenTyp, datum,
      timestamp: new Date().toISOString()
    };
    
    try {
      localStorage.setItem('paket_entwurf', JSON.stringify(currentData));
      console.log('📝 Entwurf automatisch gespeichert');
      
      // 🔧 WICHTIG: setHasDraft auf true setzen!
      setHasDraft(true);
      
      // Benachrichtige die Hauptkomponente direkt über die Prop
      if (onDraftChange) {
        onDraftChange(true, currentData);
        console.log('📝 Hauptkomponente über Entwurf informiert');
      }
      
      // Benachrichtige die Hauptkomponente über den neuen Entwurf (Fallback)
      window.dispatchEvent(new CustomEvent('draftSaved', { 
        detail: { hasDraft: true, data: currentData }
      }));
      
      // Kurze visuelle Bestätigung
      setIsAutoSaving(true);
      setTimeout(() => setIsAutoSaving(false), 1000);
      
    } catch (error) {
      console.error('Fehler beim Speichern des Entwurfs:', error);
    }
  };

  const loadDraft = () => {
    try {
      const saved = localStorage.getItem('paket_entwurf');
      if (saved) {
        const draftData = JSON.parse(saved);
        
        // Prüfe ob Entwurf nicht zu alt ist (24 Stunden)
        const draftAge = Date.now() - new Date(draftData.timestamp).getTime();
        const maxAge = 24 * 60 * 60 * 1000; // 24 Stunden
        
        if (draftAge < maxAge) {
          // Lade Entwurf in die Felder
          setName(draftData.name || '');
          setStreetAndNumber(draftData.streetAndNumber || '');
          setPostalAndCity(draftData.postalAndCity || '');
          setTrackingNumber(draftData.trackingNumber || '');
          setRegalplatz(draftData.regalplatz || '');
          setImageUrl(draftData.imageUrl || null);
          // 🛡️ Extras wiederherstellen (siehe saveDraft)
          if (draftData.betrag !== undefined) setBetrag(draftData.betrag || '');
          if (draftData.gewicht !== undefined) setGewicht(draftData.gewicht || '');
          if (draftData.farbe !== undefined) setFarbe(draftData.farbe || '');
          if (draftData.international !== undefined) setInternational(!!draftData.international);
          if (draftData.paketAnzahl !== undefined) {
            const anz = Math.min(999, Math.max(1, parseInt(draftData.paketAnzahl, 10) || 1));
            setPaketAnzahl(anz);
            setPaketAnzahlInput(anz > 5 ? String(anz) : '');
          }
          if (draftData.palettenTyp !== undefined) {
            setPalettenTyp(draftData.palettenTyp || '');
            if (draftData.palettenTyp) setShowPaletteBar(true);
          }
          if (draftData.datum) setDatum(draftData.datum);
          
          setHasDraft(true);
          console.log('📝 Entwurf vom', new Date(draftData.timestamp).toLocaleString(), 'geladen');
          return true;
        } else {
          // Alter Entwurf → löschen
          clearDraft();
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden des Entwurfs:', error);
      clearDraft();
    }
    return false;
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem('paket_entwurf');
      setHasDraft(false);
      console.log('🗑️ Entwurf gelöscht');
      
      // Benachrichtige die Hauptkomponente direkt über die Prop
      if (onDraftChange) {
        onDraftChange(false, null);
        console.log('📝 Hauptkomponente über Entwurf-Löschung informiert');
      }
      
      // Benachrichtige die Hauptkomponente über das Löschen des Entwurfs (Fallback)
      window.dispatchEvent(new CustomEvent('draftCleared', { 
        detail: { hasDraft: false }
      }));
      
    } catch (error) {
      console.error('Fehler beim Löschen des Entwurfs:', error);
    }
  };

  // Auto-Save Setup
  const startAutoSave = () => {
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
    }
    
    // Alle 10 Sekunden auto-save
    autoSaveTimerRef.current = setInterval(saveDraft, 10000);
  };

  const stopAutoSave = () => {
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  };

  // 📱 ORIENTIERUNGSSPERRE - Hochformat ist beim Fotografieren immer aktiv (kein Schalter mehr)
  const lockOrientation = async () => {
    try {
      if (screen.orientation && screen.orientation.lock) {
        await screen.orientation.lock('portrait');
        console.log('📱 Bildschirm auf Hochformat gesperrt');
      }
    } catch (error) {
      console.log('⚠️ Orientierungssperre nicht unterstützt:', error.message);
    }
  };

  const unlockOrientation = async () => {
    try {
      if (screen.orientation && screen.orientation.unlock) {
        screen.orientation.unlock();
        console.log('📱 Bildschirmsperre aufgehoben');
      }
    } catch (error) {
      console.log('⚠️ Entsperren der Orientierung fehlgeschlagen:', error.message);
    }
  };

  const handleCameraActivation = async () => {
    setCamPermHint(false);
    // 📷 In-App-Kamera (Opt-in): für Geräte, auf denen die System-Kamera nicht startet (Galerie)
    if (inAppCam || (seriesOn && !paketId && !seriesKameraFehlgeschlagen())) { setShowInAppCam(true); return; }
    // 📷 Ist die Kamera-Berechtigung der App verweigert, passiert beim Antippen sonst NICHTS
    // (Capacitor bricht still ab). Neuere APKs: exakt prüfen/anfragen. Alte APKs → null.
    const perm = await nativeCameraPermission();
    if (perm === 'denied') { setCamPermHint(true); return; }

    setIsCameraActive(true);
    
    if (lockPortraitMode) {
      await lockOrientation();
    }
    
    // Trigger camera input
    photoArrivedRef.current = false;
    document.getElementById('paket-photo-input').click();

    // Alte APK ohne Prüf-Plugin: öffnet sich die Kamera nicht (App bleibt sichtbar, kein
    // Foto), nach ein paar Sekunden einen Hinweis zeigen. Nur nativ — im Browser öffnet
    // der Dateidialog ohne Sichtbarkeitswechsel.
    if (perm === null && Capacitor.isNativePlatform()) {
      let weg = false;
      const onVis = () => { if (document.hidden) weg = true; };
      document.addEventListener('visibilitychange', onVis);
      setTimeout(() => {
        document.removeEventListener('visibilitychange', onVis);
        if (!weg && !photoArrivedRef.current) setCamPermHint(true);
      }, 3500);
    }
  };

  const handleCameraDeactivation = async () => {
    setIsCameraActive(false);
    
    if (lockPortraitMode) {
      await unlockOrientation();
    }
  };

  // 🗑️ PAKET LÖSCHEN (nur im Bearbeitungs-Modus)
  const handleDeletePackage = async () => {
    if (!paketId) {
      console.warn('⚠️ Kein Paket zum Löschen ausgewählt');
      return;
    }

    const confirmDelete = window.confirm(
      t('packages.reallyDelete')
    );

    if (!confirmDelete) return;

    try {
      // Rufe die Lösch-Funktion der Hauptkomponente auf
      if (onDelete) {
        const paketToDelete = pakete.find(p => p.id === paketId);
        if (paketToDelete) {
          // skipConfirm=true: hier wurde bereits bestätigt, sonst fragt
          // handleDelete in der Hauptkomponente ein zweites Mal nach.
          await onDelete(paketToDelete, undefined, true);
          console.log('✅ Paket gelöscht:', paketId);
          onClose(); // Modal schließen nach erfolgreichem Löschen
        }
      }
    } catch (error) {
      console.error('❌ Fehler beim Löschen:', error);
      alert(t('errors.deleteError') + ': ' + error.message);
    }
  };

  // 📝 DRAFT SYSTEM - Zentrale Schließen-Funktion
  const handleClose = () => {
    console.log('🚪 Modal wird geschlossen, prüfe Entwurf...');
    console.log('📊 Aktuelle Daten:', { name, streetAndNumber, postalAndCity, trackingNumber, regalplatz });
    
    // 🔧 PRÜFE OB IM BEARBEITUNGS-MODUS
    const isEditMode = !!(initialName || initialStreetAndNumber || initialTrackingNumber);
    
    if (isEditMode) {
      // BEARBEITUNGS-MODUS: Nur speichern wenn ÄNDERUNGEN vorhanden
      const hasChanges = 
        name !== (initialName || '') ||
        streetAndNumber !== (initialStreetAndNumber || '') ||
        postalAndCity !== (initialPostalAndCity || '') ||
        trackingNumber !== (initialTrackingNumber || '') ||
        regalplatz !== (initialRegalplatz || '');
      
      if (hasChanges) {
        console.log('📝 Änderungen erkannt beim Schließen, speichere Entwurf');
        saveDraft();
      } else {
        console.log('⏸️ Keine Änderungen, lösche eventuell vorhandenen Entwurf');
        clearDraft(); // Lösche alten Entwurf, da keine Änderungen gemacht wurden
      }
    } else {
      // NEU-MODUS: Speichere Entwurf wenn sinnvolle Daten vorhanden
      const meaningfulData = isDraftMeaningful();
      console.log('📝 Sind Daten sinnvoll für Entwurf?', meaningfulData);
      
      if (meaningfulData) {
        saveDraft();
        console.log('💾 Entwurf beim Schließen gespeichert');
      } else {
        console.log('📝 Keine sinnvollen Daten für Entwurf vorhanden');
      }
    }
    
    // Auto-Save stoppen
    stopAutoSave();
    
    // Modal schließen
    onClose();
  };

  // 📝 DRAFT SYSTEM - Ref um zu tracken ob es der erste Mount ist
  const isInitialMountRef = useRef(true);

  // 📝 DRAFT SYSTEM - useEffects für Initialisierung und Cleanup
  useEffect(() => {
    // ✅ FIX: Nur beim ersten Mount ausführen, nicht bei jedem forceNewPackage-Wechsel
    if (!isInitialMountRef.current) {
      console.log('📝 Überspringe Draft-Check (nicht der erste Mount)');
      return;
    }
    isInitialMountRef.current = false;

    // 🆕 Bei "Neues Paket": Entwurf IMMER ignorieren und löschen
    if (forceNewPackage) {
      console.log('🆕 NEUES PAKET - Entwurf wird ignoriert');
      const saved = localStorage.getItem('paket_entwurf');
      if (saved) {
        console.log('🗑️ Vorhandener Entwurf wird gelöscht (Neues Paket erzwungen)');
        clearDraft();
      }
      startAutoSave();
      return () => stopAutoSave();
    }
    
    // Beim Modal-Öffnen: Prüfe auf Entwurf und FRAGE den Benutzer
    const hasInitialData = initialName || initialStreetAndNumber || initialTrackingNumber;
    
    if (!hasInitialData) {
      try {
        const saved = localStorage.getItem('paket_entwurf');
        if (saved) {
          const draftData = JSON.parse(saved);
          
          // Prüfe ob Entwurf nicht zu alt ist (24 Stunden)
          const draftAge = Date.now() - new Date(draftData.timestamp).getTime();
          const maxAge = 24 * 60 * 60 * 1000; // 24 Stunden
          
          if (draftAge < maxAge) {
            // FRAGE den Benutzer, was er tun möchte
            const timeAgo = Math.round(draftAge / (1000 * 60)); // Minuten
            const timeStr = timeAgo < 60 
              ? `${timeAgo} min` 
              : `${Math.round(timeAgo / 60)} h`;
            
            const shouldLoad = window.confirm(
              `📝 ${t('draft.draft')} (${timeStr}):\n\n` +
              `${t('packages.name')}: ${draftData.name || '(—)'}\n` +
              `${t('packages.street')}: ${draftData.streetAndNumber || '(—)'}\n` +
              `Tracking: ${draftData.trackingNumber || '(—)'}\n` +
              `${t('packages.shelf')}: ${draftData.regalplatz || '(—)'}\n\n` +
              `${t('draft.continueDraft')}?\n\n` +
              `• OK = ${t('common.yes')}\n` +
              `• ${t('common.cancel')} = ${t('common.no')}`
            );
            
            if (shouldLoad) {
              // Lade Entwurf
              const loaded = loadDraft();
              if (loaded) {
                console.log('✅ Entwurf auf Benutzer-Wunsch geladen');
              }
            } else {
              // Verwerfe Entwurf
              console.log('🗑️ Entwurf auf Benutzer-Wunsch verworfen');
              clearDraft();
            }
          } else {
            // Alter Entwurf → automatisch löschen
            console.log('🗑️ Alter Entwurf automatisch gelöscht');
            clearDraft();
          }
        }
      } catch (error) {
        console.error('Fehler beim Prüfen des Entwurfs:', error);
        clearDraft();
      }
    } else {
      // 🔧 BEIM BEARBEITEN: Prüfe ob schon ein Entwurf existiert
      const saved = localStorage.getItem('paket_entwurf');
      if (saved) {
        setHasDraft(true);
        console.log('📝 Entwurf erkannt beim Bearbeiten eines Pakets');
      }
    }
    
    // Starte Auto-Save
    startAutoSave();
    
    // Cleanup beim Unmount
    return () => {
      stopAutoSave();
    };
  }, []); // ✅ FIX: Nur einmal beim Mount ausführen (isInitialMountRef verhindert erneute Ausführung)

  // Escape-Taste zum Schließen des Modals — handleClose per Ref, sonst greift der
  // []-Effekt die STALE Closure des ersten Renders (Entwurf würde nicht gesichert).
  const handleCloseRef = useRef(null);
  handleCloseRef.current = handleClose;
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && handleCloseRef.current) handleCloseRef.current();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Beim ersten Laden gespeicherte Einstellungen laden + bei Konto-Profil-Abgleich aktualisieren
  useEffect(() => {
    const loadFromStorage = () => {
      try {
        const savedVisibility = localStorage.getItem('formSectionVisibility');
        if (savedVisibility) {
          setVisibleSections(prev => ({
            ...prev,
            ...JSON.parse(savedVisibility)
          }));
        }
        setInAppCam(isInAppCameraEnabled());
        setSeriesOnState(isSeriesOn());
      } catch (e) {
        console.error("Fehler beim Laden der Sektionseinstellungen:", e);
      }
    };
    loadFromStorage();
    window.addEventListener('kiosk:profile-applied', loadFromStorage);
    return () => window.removeEventListener('kiosk:profile-applied', loadFromStorage);
  }, []);
  
  // Bei Änderung der Sichtbarkeit speichern
  useEffect(() => {
    try {
      localStorage.setItem('formSectionVisibility', JSON.stringify(visibleSections));
    } catch (e) {
      console.error("Fehler beim Speichern der Sektionseinstellungen:", e);
    }
  }, [visibleSections]);

  // Doppelklick-Handler für Überschriften
  const handleSectionHeaderTap = (section) => {
    const now = Date.now();
    const lastTap = lastTapTimestamp[section] || 0;
    
    if (now - lastTap < 300) {
      // Doppeltipp erkannt
      setVisibleSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    }
    
    // Zeitstempel aktualisieren
    setLastTapTimestamp(prev => ({
      ...prev,
      [section]: now
    }));
  };
  
  // Komponente für einklappbare Überschriften
  const SectionHeader = ({ title, section, isVisible }) => (
    <div 
      onClick={() => handleSectionHeaderTap(section)}
      onDoubleClick={() => {
        setVisibleSections(prev => ({
          ...prev,
          [section]: !prev[section]
        }));
      }}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: isVisible ? '6px' : '2px', // Von 12px auf 6px reduziert
        borderBottom: `1px solid ${colors.border}`,
        paddingBottom: '4px', // Von 8px auf 4px reduziert
        cursor: 'pointer'
      }}
    >
      <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
        {title}
      </h3>
      <span 
        style={{ 
          fontSize: '16px', // Von 18px auf 16px reduziert
          color: colors.darkMode ? '#9e9e9e' : '#666',
          display: 'flex',
          alignItems: 'center',
          padding: '3px' // Von 5px auf 3px reduziert
        }}
        onClick={(e) => {
          e.stopPropagation();
          setVisibleSections(prev => ({
            ...prev,
            [section]: !prev[section]
          }));
        }}
      >
        {isVisible ? <FaEye /> : <FaEyeSlash />}
      </span>
    </div>
  );
  
  // Hilfsfunktion zum Normalisieren des Regalplatzes: Leerzeichen entfernen,
  // evtl. gescanntes "RG:"-Präfix abschneiden (Regal-QR "RG:K28" → "K28"),
  // O→0-Tippfehler („CO5" → „C05") und 2-stellige Box-Notation („C2" → „C02") angleichen.
  const normalizeRegalplatz = (val) => {
    return val ? normalizeRegalplatzHelper(val, pakete) : val;
  };
  
  // Funktionen übernehmen
  const handlePhotoUpload = (e) => {
    serieAngefasstRef.current = false; setAutoSaveIn(null); // ⚡ neues Foto → Auto-Speichern wieder möglich
    // 📋 Barcodes aus dem ORIGINALFOTO lesen (volle Auflösung)
    const foto = e?.target?.files && e.target.files[0];
    setTrackingManuellBestaetigt(false);
    setShowTrackingFallback(false);
    setFallbackNachgefragt(false);
    setShowSideBarcodeScanner(false);
    lokalCodesRef.current = [];
    serverCodesRef.current = [];
    if (foto && !paketId) {
      if (hasCustomFeature('batchBarcodes')) {
        setBatchStatus('lesen');
      }
      setBarcodeTrigger((n) => n + 1);
      const p = barcodesAusFoto(foto).then((codes) => {
        lokalCodesRef.current = Array.isArray(codes) ? codes : [];
        return lokalCodesRef.current;
      });
      batchCodesRef.current = p;
      p.then(() => { if (batchCodesRef.current === p) batchStatusAktualisieren(); }).catch(() => batchStatusAktualisieren());
    }
    photoArrivedRef.current = true;
    setCamPermHint(false);
    // 📱 Orientierungssperre deaktivieren wenn Foto ausgewählt
    handleCameraDeactivation();
    
    if (e.target.files && e.target.files[0]) {
      const rawFile = e.target.files[0];

      // 🔄 Vorherigen Zustand sofort zurücksetzen (ohne auf FileReader zu warten!)
      setOcrComplete(false);
      setStaticImage(null);
      setMlResult(null);
      setAbsenderData(null);
      setPlzCheckBestaetigt(false);
      setShowPlzModal(false);
      setRecognizedBlocks([]);
      setOcrError('');
      setOcrLoading(false);
      setQuickConfirm(false);   // Bestätigungs-Schritt kommt erst nach der neuen Analyse
      setShowKiOverlay(false);

      // Parallel 1: Data-URL für Vorschau & manuellen Cropper-Fallback im Hintergrund einlesen
      const dataUrlPromise = new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const dataUrl = event.target.result;
          setImageUrl(dataUrl);
          resolve(dataUrl);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(rawFile);
      });

      // Parallel 2: KI-Erfassung sofort starten (ohne auf FileReader.onload zu warten)
      (async () => {
        // 🤖 KI ist Standard. Manuell (Cropper + Tesseract) ist nur noch der automatische
        // Fallback — wenn wir offline sind, der ML-Dienst aus ist, oder das kostenlose
        // Tageslimit erreicht wurde.
        const online = (typeof navigator === 'undefined') || navigator.onLine !== false;
        let gate = kiScanAllowed(isPremium);
        // 🔄 Limit erreicht? Erst beim Server nachsehen, ob das Konto inzwischen ein Abo hat
        // (auf einem anderen Gerät gekauft, dieses lief durch) — dann ohne Neustart weiter.
        if (!gate.allowed && recheckPremium && (await recheckPremium())) gate = kiScanAllowed(true);

        if (!gate.allowed) {
          // 🚫 Free-Tageslimit erreicht → Paywall anbieten, manuell weiterarbeiten lassen.
          await dataUrlPromise;
          setOcrError(t('premium.kiLimitReached', { limit: FREE_KI_DAILY_LIMIT }));
          if (onNeedPremium) onNeedPremium();
          // → Cropper erscheint automatisch (imageUrl gesetzt, ocrComplete=false)
        } else if (online && mlAvailable !== false) {
          // 🤖 KI-Erfassung versuchen: Riesen-Fotos der System-Kamera (>2600px / >1.1MB) vorab
          // auf 2600px verkleinern (lokaler Barcode-Scan oben nutzt bereits das volle Original!)
          const uploadBlob = await verkleinereFuerUpload(rawFile, 2600);
          const success = await handleMLAnalysis(null, uploadBlob);
          if (success) {
            if (!isPremium) { noteKiScanUsed(); setKiRemaining(kiScanAllowed(false).remaining); }
            console.log('🤖 ML-Analyse erfolgreich — Crop-Schritt übersprungen');
          } else {
            await dataUrlPromise;
          }
          // sonst: ML-Dienst meldete Fallback → Cropper + OCR-Feld übernehmen
        }
        // sonst (offline / ML aus): direkt manueller Fallback über den Cropper
      })();
    }
    
    // 🔄 Input-Wert zurücksetzen, damit dasselbe Bild erneut gewählt werden kann
    if (e.target) {
      e.target.value = '';
    }
  };
  
  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };
  
  const getCroppedImg = async () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const image = new Image();
      image.src = imageUrl;
      
      return new Promise((resolve) => {
        image.onload = () => {
          canvas.width = croppedAreaPixels.width;
          canvas.height = croppedAreaPixels.height;
          
          ctx.drawImage(
            image,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            croppedAreaPixels.width,
            croppedAreaPixels.height
          );
          
          resolve(canvas.toDataURL('image/jpeg'));
        };
      });
    } catch (e) {
      console.error('Error getting cropped image:', e);
      return null;
    }
  };
  
  // ============================================================================
  // 🤖 ML-ANALYSE — Automatische Paketlabel-Erkennung
  // ============================================================================
  const [mlAnalyzing, setMlAnalyzing] = useState(false);
  const [mlWarte, setMlWarte] = useState(null); // ⏳ Server kurz weg → { seitMs, maxMs, ab } für den Ladebalken
  const [, setMlWarteTick] = useState(0);
  // Ladebalken gleichmäßig weiterschieben (onWarte kommt nur bei jedem neuen Versuch, also alle 1–5 s)
  useEffect(() => {
    if (!mlWarte) return undefined;
    const iv = setInterval(() => setMlWarteTick((n) => n + 1), 500);
    return () => clearInterval(iv);
  }, [mlWarte]);
  const [mlResult, setMlResult] = useState(null); // { imageId, thumbnail, ocrResult, regions, retoure, absender, ... }
  const [absenderData, setAbsenderData] = useState(null); // { name, street, postal, city } — bleibt auch nach Tausch Empfänger↔Absender erhalten
  const [plzCheckBestaetigt, setPlzCheckBestaetigt] = useState(false);
  const [showPlzModal, setShowPlzModal] = useState(false);
  const [ocrComplete, setOcrComplete] = useState(false);
  const [staticImage, setStaticImage] = useState(null);
  const [ocrDiagnosticData, setOcrDiagnosticData] = useState(null);
  const [recognizedBlocks, setRecognizedBlocks] = useState([]);
  const [showRecognitionOverlay, setShowRecognitionOverlay] = useState(false);
  // 📥 Absender laut Etikett — wird IMMER mit gespeichert, damit vertauschte Pakete über die Suche auffindbar bleiben
  const aktiverAbsender = absenderData !== null ? absenderData : (mlResult?.absender || null);
  const formatAbsenderObj = (a) => {
    if (!a) return '';
    if (typeof a === 'string') return a.trim().slice(0, 250);
    const plzOrt = [a.postal, a.city].map((x) => String(x || '').trim()).filter(Boolean).join(' ');
    return [a.name, a.street, plzOrt]
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .join(' · ')
      .slice(0, 250);
  };
  // Nur mit eingeschalteter Option „Absender speichern" (kiosk_capture_sender) — Absenderdaten sind personenbezogen;
  // Gemini hatte das auf „immer" gestellt (Review 21.10.2026, e2e_hilfe „Option aus: kein Absender gespeichert")
  const erkannterAbsender = isCaptureSenderOn() ? formatAbsenderObj(aktiverAbsender) : '';
  const [mlAvailable, setMlAvailable] = useState(null); // null = unbekannt, true/false
  // 🔄 Retoure: KI erkennt zurücklaufende Sendungen → Abholer ist der ABSENDER, nicht der Empfänger
  const [absenderUebernommen, setAbsenderUebernommen] = useState(false);
  const [retoureHintDismissed, setRetoureHintDismissed] = useState(false);
  // 📷 Regal-QR-Scanner: Geräte-Einstellung, direkt am Regalfach umschaltbar
  const [qrOn, setQrOn] = useState(() => isShelfQrScanEnabled());
  // 🔤 Fach automatisch = erster Buchstabe des Nachnamens (pro Gerät, z. B. Green Goat).
  // Füllt nur ein LEERES Feld bzw. überschreibt nur den eigenen vorherigen Vorschlag —
  // eine Eingabe des Nutzers bleibt unangetastet.
  const [autoLetter, setAutoLetter] = useState(() => isAutoRegalLetter());
  const autoRegalRef = useRef('');
  // 📋 Barcode-Stapel: echte Barcodes aus dem Foto (parallel zur KI, blockiert nie)
  const batchCodesRef = useRef(null);
  const lokalCodesRef = useRef([]);
  const serverCodesRef = useRef([]); // vom ML-Server gelesene Barcodes (zuverlässiger als der JS-Leser)
  const [barcodeTrigger, setBarcodeTrigger] = useState(0);
  const [batchStatus, setBatchStatus] = useState(null); // null | 'lesen' | Anzahl (0 = keiner)
  const [showBatchExplain, setShowBatchExplain] = useState(() => listBatch().length === 0);
  const [batchFeatureHidden, setBatchFeatureHidden] = useState(false);
  const [trackingManuellBestaetigt, setTrackingManuellBestaetigt] = useState(false);
  const [showTrackingFallback, setShowTrackingFallback] = useState(false);
  const [fallbackNachgefragt, setFallbackNachgefragt] = useState(false);
  const [showSideBarcodeScanner, setShowSideBarcodeScanner] = useState(false);
  const fallbackTrackingInputRef = useRef(null);

  // 📒 Adressbuch-Autovervollständigung, 🪪 Ausweis-Scan, #001 Nummerierung & 🛡️ Datenschutz-Bestätigung
  const [privacyConfirmed, setPrivacyConfirmed] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [pendingSaveOverrides, setPendingSaveOverrides] = useState(null);
  const [idScanning, setIdScanning] = useState(false);
  const idPhotoInputRef = useRef(null);

  const addressBookSuggestions = useMemo(() => {
    if (paketId) return [];
    const q = String(name || '').trim();
    if (!q || q.startsWith('#')) return [];
    return searchAddressBook(q, 5);
  }, [name, paketId]);

  const matchedAddressBookContact = useMemo(() => {
    const q = String(name || '').trim();
    if (!q || q.startsWith('#')) return null;
    return findBestAddressBookMatch(q);
  }, [name]);

  const applyAddressBookEntry = (entry) => {
    if (!entry) return;
    serieStoppen();
    setName(entry.name);
    if (invalidFields.name) setInvalidFields((f) => ({ ...f, name: false }));
    if (entry.department && !streetAndNumber) {
      setStreetAndNumber(entry.department);
    }
    if (entry.shelf && !regalplatz) {
      setRegalplatz(String(entry.shelf).toUpperCase().replace(/\s+/g, ''));
      if (invalidFields.regalplatz) setInvalidFields((f) => ({ ...f, regalplatz: false }));
    }
  };

  const renderAddressBookChips = () => {
    const showChips = addressBookSuggestions.length > 0 && !(
      addressBookSuggestions.length === 1 &&
      matchedAddressBookContact &&
      matchedAddressBookContact.id === addressBookSuggestions[0].id
    );

    if (!showChips && !matchedAddressBookContact) return null;

    const listMode = getListNotifyMode();

    return (
      <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {matchedAddressBookContact && (
          <div
            data-testid="addressbook-staff-alert"
            style={{
              padding: '9px 12px',
              borderRadius: '9px',
              border: `2px solid ${ matchedAddressBookContact.note ? '#f59e0b' : '#3b82f6' }`,
              background: matchedAddressBookContact.note
                ? (colors.darkMode ? '#3b2a10' : '#fffbeb')
                : (colors.darkMode ? '#172554' : '#eff6ff'),
              color: colors.text,
              fontSize: '13px',
              lineHeight: 1.4,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
              <strong style={{ color: matchedAddressBookContact.note ? (colors.darkMode ? '#fcd34d' : '#b45309') : (colors.darkMode ? '#93c5fd' : '#1d4ed8') }}>
                ⭐ {t('ui.modal.onRecipientList', { name: matchedAddressBookContact.name })}
              </strong>
              {matchedAddressBookContact.shelf && (
                <button
                  type="button"
                  onClick={() => {
                    const targetShelf = String(matchedAddressBookContact.shelf).toUpperCase().replace(/\s+/g, '');
                    setRegalplatz(targetShelf);
                    if (invalidFields.regalplatz) setInvalidFields((f) => ({ ...f, regalplatz: false }));
                  }}
                  style={{
                    padding: '2px 8px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#2563eb',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  🗄️ {t('ui.modal.regularShelf', { shelf: matchedAddressBookContact.shelf })}
                </button>
              )}
            </div>
            {matchedAddressBookContact.note && (
              <div
                data-testid="addressbook-staff-note"
                style={{
                  marginTop: '5px',
                  padding: '6px 9px',
                  borderRadius: '6px',
                  background: colors.darkMode ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7',
                  color: colors.darkMode ? '#fde68a' : '#92400e',
                  fontWeight: 700,
                  fontSize: '13px',
                }}
              >
                📌 {t('ui.modal.staffNote')} {matchedAddressBookContact.note}
              </div>
            )}
            <div style={{ marginTop: '4px', fontSize: '11.5px', opacity: 0.85, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {matchedAddressBookContact.department && <span>🏢 {matchedAddressBookContact.department}</span>}
              {matchedAddressBookContact.email ? (
                <span>
                  {listMode === 'auto'
                    ? `📧 ${t('ui.modal.autoEmailTo', { email: matchedAddressBookContact.email })}`
                    : listMode === 'ask'
                    ? `💬 ${t('ui.modal.askEmail', { email: matchedAddressBookContact.email })}`
                    : `👷 ${t('ui.modal.staffAlertOnly')}`}
                </span>
              ) : (
                <span>👷 {t('ui.modal.staffAlertNoEmail')}</span>
              )}
            </div>
          </div>
        )}

        {showChips && (
          <div
            data-testid="address-book-suggestions"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
            }}
          >
            {addressBookSuggestions.map((entry) => (
              <button
                key={entry.id || entry.name}
                type="button"
                onClick={() => applyAddressBookEntry(entry)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '999px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: `1px solid ${colors.darkMode ? '#3b82f6' : '#93c5fd'}`,
                  background: colors.darkMode ? '#1e3a8a' : '#eff6ff',
                  color: colors.darkMode ? '#bfdbfe' : '#1d4ed8',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <span>📒 {entry.name}</span>
                {entry.department && <span style={{ opacity: 0.85, fontWeight: 500 }}>· {entry.department}</span>}
                {entry.shelf && <span style={{ opacity: 0.9, background: 'rgba(59,130,246,0.2)', padding: '1px 5px', borderRadius: '6px' }}>🗄️ {entry.shelf}</span>}
                {entry.note && <span style={{ opacity: 0.95, color: colors.darkMode ? '#fcd34d' : '#b45309' }}>📌 {entry.note}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // #001 Automatische Fortlaufende Nummer beim Öffnen eines neuen Eintrags (wenn aktiviert und kein Ausweis-zuerst)
  useEffect(() => {
    if (paketId || initialName || initialTrackingNumber) return;
    if (isAutoNumberEnabled() && !isIdFirstEnabled()) {
      const savedDraft = localStorage.getItem('paket_entwurf');
      if (!savedDraft) {
        setName((cur) => cur || getNextAutoNumber(pakete));
      }
    }
    // eslint-disable-next-line
  }, []);

  const handleIdPhotoUpload = async (e) => {
    const file = e?.target?.files && e.target.files[0];
    if (e?.target) e.target.value = '';
    if (!file) return;
    serieStoppen();
    setIdScanning(true);
    try {
      const hit = await identifyForPickup(file);
      if (hit && hit.name) {
        setName(hit.name);
        if (invalidFields.name) setInvalidFields((f) => ({ ...f, name: false }));
        if (hit.street && !streetAndNumber) setStreetAndNumber(hit.street);
        if (hit.postal && !postalAndCity) setPostalAndCity(hit.postal);
        // Adressbuch-Abgleich
        const addrMatches = searchAddressBook(hit.name, 1);
        if (addrMatches.length === 1 && addrMatches[0].name.toLowerCase() === hit.name.toLowerCase()) {
          if (addrMatches[0].department && !hit.street && !streetAndNumber) setStreetAndNumber(addrMatches[0].department);
          if (addrMatches[0].shelf && !regalplatz) setRegalplatz(addrMatches[0].shelf);
        }
        if (window.showToast) {
          window.showToast(`🪪 ${t('ui.modal.idRecognized', { name: hit.name })}`, 'success', 3000);
        }
      } else {
        if (window.showToast) {
          window.showToast(`🪪 ${t('ui.modal.idNoName')}`, 'warning', 4000);
        }
      }
    } catch (err) {
      console.warn('🪪 Ausweis-Scan fehlgeschlagen:', err);
    } finally {
      setIdScanning(false);
    }
  };

  // 📍 PLZ-Kreis des Paketshops aus den vorhandenen Paketen ermitteln
  // WICHTIG: Erst aktiv, wenn mindestens 30 Pakete in der Datenbank liegen (damit neue Konten nicht genervt werden)
  const extractPlzFromText = (str) => {
    const s = String(str || '').trim();
    if (!s) return '';
    const m5 = s.match(/(?:^|[^0-9])(\d{5})(?!\d)/);
    if (m5) return m5[1];
    const m4 = s.match(/(?:^|[^0-9])(\d{4})(?:\s*[A-Za-z]{2}\b|\b)/);
    if (m4) return m4[1];
    return '';
  };

  const shopPlzKreis = useMemo(() => {
    const aktivePakete = Array.isArray(pakete) ? pakete.filter((p) => p && !p.deleted) : [];
    if (aktivePakete.length < 30) return null; // Schutz für neue Accounts (< 30 Pakete)
    const prefix2Counts = {};
    const prefix3Counts = {};
    let mitPlzCount = 0;
    let fiveDigitCount = 0;
    for (const p of aktivePakete) {
      const plz = extractPlzFromText(p.postalAndCity);
      if (!plz || plz.length < 4) continue;
      mitPlzCount++;
      if (plz.length === 5) fiveDigitCount++;
      const p2 = plz.slice(0, 2);
      const p3 = plz.slice(0, 3);
      prefix2Counts[p2] = (prefix2Counts[p2] || 0) + 1;
      prefix3Counts[p3] = (prefix3Counts[p3] || 0) + 1;
    }
    if (mitPlzCount < 15) return null;
    const erlaubtePrefix2 = new Set();
    let maxP2Count = 0;
    for (const [p2, cnt] of Object.entries(prefix2Counts)) {
      if (cnt > maxP2Count) maxP2Count = cnt;
      if (cnt >= 3 && (cnt / mitPlzCount >= 0.04 || cnt >= 5)) {
        erlaubtePrefix2.add(p2);
      }
    }
    if (erlaubtePrefix2.size === 0 || maxP2Count / mitPlzCount < 0.3) return null;
    let topP3 = '';
    let topP3Count = 0;
    for (const [p3, cnt] of Object.entries(prefix3Counts)) {
      if (cnt > topP3Count) {
        topP3Count = cnt;
        topP3 = p3;
      }
    }
    const ist5Stellig = fiveDigitCount >= mitPlzCount * 0.5;
    const beispiel = topP3 ? `${topP3}${ist5Stellig ? 'xx' : 'x'}` : `${Array.from(erlaubtePrefix2)[0]}xxx`;
    return { erlaubtePrefix2, beispiel, ist5Stellig, totalPakete: aktivePakete.length };
  }, [pakete]);

  const plzAbweichung = useMemo(() => {
    if (paketId || !shopPlzKreis) return null;
    try {
      const b = localStorage.getItem('kiosk_branche');
      if (b === 'gepaeck' || b === 'click_collect') return null;
    } catch { /* */ }
    const empfPlz = extractPlzFromText(postalAndCity);
    if (!empfPlz) return null;
    // 🇳🇱 Niederländische Shops (4-stellige PLZ): Kunden kommen oft aus Nachbargemeinden mit anderem
    // 2-Ziffern-Präfix (z. B. 2651 Berkel en Rodenrijs neben 3045 Rotterdam). Dort NICHT bei normalen
    // 4-stelligen NL-Postleitzahlen unterbrechen, sondern nur wenn eine 5-stellige ausländische
    // Hub-/Absender-PLZ (z. B. "05500 Bielefeld" von Vinted BeNeLux) als Empfänger gelesen wurde!
    if (!shopPlzKreis.ist5Stellig && empfPlz.length === 4) return null;
    if (shopPlzKreis.erlaubtePrefix2.has(empfPlz.slice(0, 2))) return null;
    const absPlz = extractPlzFromText([aktiverAbsender?.postal || aktiverAbsender?.plz, aktiverAbsender?.city || aktiverAbsender?.ort].filter(Boolean).join(' '));
    const absPasstZumKreis = Boolean(absPlz && (shopPlzKreis.erlaubtePrefix2.has(absPlz.slice(0, 2)) || (!shopPlzKreis.ist5Stellig && absPlz.length === 4)));
    const hatAbsender = Boolean(aktiverAbsender && (aktiverAbsender.name || aktiverAbsender.street || aktiverAbsender.strasse));
    return {
      empfPlz,
      aktuellePlz: empfPlz,
      empfOrt: String(postalAndCity || '').trim(),
      absPlz,
      absenderPlz: absPlz,
      absPasstZumKreis,
      absenderPasstZumShop: absPasstZumKreis,
      hatAbsender,
      beispiel: shopPlzKreis.beispiel,
      shopKreisLabel: shopPlzKreis.beispiel,
    };
  }, [paketId, shopPlzKreis, postalAndCity, aktiverAbsender]);

  const handleSideBarcodeDetected = ({ tracking, code, allCodes }) => {
    serieStoppen();
    if (code && code.text) {
      lokalCodesRef.current = [code, ...(Array.isArray(allCodes) ? allCodes : []), ...lokalCodesRef.current];
    } else if (Array.isArray(allCodes) && allCodes.length) {
      lokalCodesRef.current = [...allCodes, ...lokalCodesRef.current];
    }
    if (tracking) {
      setTrackingNumber(tracking);
      setTrackingManuellBestaetigt(true);
    }
    setShowSideBarcodeScanner(false);
    setShowTrackingFallback(false);
    setBarcodeTrigger((n) => n + 1);
  };

  /**
   * 📷 Verarbeitet sowohl QR-Codes als auch 1D-/2D-Barcodes beim Paket-Einspeichern:
   * - Regalfach-Codes (z. B. "A1", "B4", "P6-C", ";P6 - C", "K1-C", "RG:A1", "LAGER 2", "14") → füllt `regalplatz`
   * - Paket-/Sendungs-Barcodes (z. B. "0034…", "1Z…", "JVGL…", "JJD…", 10–34-stellige Sendungsnummern) → füllt/ergänzt `trackingNumber`
   */
  const handleShelfOrPackageCodeScan = (text, alle, alleObjekte) => {
    const rawList = Array.isArray(alle) && alle.length ? alle : [text];
    const formatList = Array.isArray(alleObjekte) && alleObjekte.length
      ? alleObjekte
      : rawList.filter(Boolean).map((r) => ({ text: String(r || '').trim(), format: 'code_128' }));

    let erkannterRegalCode = '';
    for (const item of rawList) {
      const clean = String(item || '')
        .replace(/^(\]C[01]|\]d2|\]e0|\]Q3)+/i, '')
        .replace(/[\x00-\x1f]/g, '')
        .trim();
      if (!clean || /^https?:/i.test(clean)) continue;
      const hatRegalPrefix = /^(?:RG:?|;+)/i.test(clean);
      const ohnePrefix = clean
        .replace(/^(?:RG:?|;+)\s*/i, '')
        .replace(/\s*-\s*/g, '-')
        .replace(/\s+/g, '')
        .toUpperCase();
      if (!ohnePrefix || ohnePrefix.length > 12) continue;
      if (!hatRegalPrefix) {
        // Reine Zifferncodes ab 5 Stellen (EAN-8, UPC, Leitcodes, Paketnummern) sind keine Regalfächer
        if (/^\d{5,}$/.test(ohnePrefix)) continue;
        // Typische Paketdienst-Sendungscodes nicht als Regalfach übernehmen
        if (/^(1Z|JVGL|VGL|JJGL|CWGL|3S|JUN|IUN|JJN|JJD|JD\d|0034|0038|VGS|[A-Z]{2}\d{9}[A-Z]{2})/i.test(ohnePrefix)) continue;
        if (ohnePrefix.length >= 9 && !ohnePrefix.includes('-') && !/^(VORNE|HINTEN|SPERRGUT|SCHRANK|POSTSCHRANK|THEKE|LAGER|BODEN|KELLER)/i.test(ohnePrefix)) {
          continue;
        }
      }
      erkannterRegalCode = normalizeRegalplatz(ohnePrefix);
      if (erkannterRegalCode) break;
    }

    if (erkannterRegalCode) {
      setRegalplatz(erkannterRegalCode);
      setInvalidFields((f) => (f.regalplatz ? { ...f, regalplatz: false } : f));
    }

    // Falls im Kamerabild (auch) ein Sendungs-Barcode / Paket-QR erkannt wurde → Sendungsnummer übernehmen, sofern noch leer oder unsicher
    const plz = postalAndCity || mlResult?.ocrResult?.postal || '';
    const dienst = paketdienst || mlResult?.metadata?.paketdienst || '';
    const scanRes = klassifiziereScanCodes(formatList, plz, dienst);
    if (scanRes.status === 'sendung' && scanRes.tracking) {
      if (scanRes.code && scanRes.code.text) {
        lokalCodesRef.current = [scanRes.code, ...lokalCodesRef.current];
      }
      if (!trackingNumber || (!trackingManuellBestaetigt && doppelabgleich?.status === 'unsicher')) {
        setTrackingNumber(scanRes.tracking);
      }
      setBarcodeTrigger((n) => n + 1);
    } else if (!erkannterRegalCode && !trackingNumber) {
      // Fallback für sonstige Paket-Barcodes / QR-Codes (z. B. internationale/Grenzpaket-Sendungscodes >= 8 Zeichen)
      for (const item of rawList) {
        const suchText = scanZuSuchtext(item);
        if (suchText && suchText.length >= 8 && suchText.length <= 35 && !/\s/.test(suchText)) {
          setTrackingNumber(suchText);
          break;
        }
      }
    }
  };

  const renderBatchStatusBox = () => {
    const normDienst = normalisiereStapelDienst(paketdienst || '');
    if (batchFeatureHidden || !hasCustomFeature('batchBarcodes') || batchStatus === null) return null;
    if (normDienst && !isBatchCarrierEnabled(normDienst)) return null;
    const isFirstPackage = listBatch().length === 0;
    return (
      <div style={{ margin: '6px 0 8px' }}>
        <div
          data-testid="batch-status"
          onClick={() => setShowBatchExplain((v) => !v)}
          style={{
            fontSize: '12px',
            color: batchStatus === 0 ? '#b26a00' : (colors.textSecondary || '#666'),
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '6px',
            padding: '5px 8px',
            borderRadius: '7px',
            background: colors.darkMode ? 'rgba(123, 97, 255, 0.12)' : '#f5f3ff',
            border: `1px solid ${colors.darkMode ? '#4c3a8a' : '#ddd6fe'}`,
          }}
        >
          <span>
            📋 {batchStatus === 'lesen' ? t('batch.reading') : batchStatus > 0 ? t('batch.codesRead', { count: batchStatus }) : t('batch.noCode')}
          </span>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#7b61ff' }}>
            {showBatchExplain ? `▲ ${t('ui.modal.infoClose')}` : (isFirstPackage ? `💡 ${t('ui.modal.whyField')} ▼` : 'ℹ️ Info ▼')}
          </span>
        </div>
        {showBatchExplain && (
          <div
            style={{
              marginTop: '4px',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              lineHeight: 1.4,
              background: colors.darkMode ? '#1e2430' : '#faf5ff',
              border: `1px solid ${colors.darkMode ? '#7b61ff' : '#c4b5fd'}`,
              color: colors.text,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '4px' }}>
              💡 {t('ui.modal.batchWhyTitle')}
            </div>
            <div style={{ opacity: 0.9 }}>
              {t('ui.modal.batchWhyText')}
            </div>
            {normDienst && (
              <button
                type="button"
                data-testid="batch-disable-carrier-btn"
                onClick={(ev) => {
                  ev.stopPropagation();
                  toggleBatchCarrierEnabled(normDienst, false);
                  setShowBatchExplain(false);
                  if (window.showToast) {
                    window.showToast(
                      `📋 ${normDienst} ${tr(
                        'wird nicht mehr im Barcode-Stapel gesammelt (in der Stapel-Ansicht jederzeit wieder aktivierbar)',
                        'will no longer be collected in the barcode batch (can be re-enabled anytime in the batch view)',
                        'wordt niet meer in de barcodestapel verzameld (in de stapelweergave op elk moment weer in te schakelen)'
                      )}`,
                      'info',
                      4000
                    );
                  }
                }}
                style={{
                  marginTop: '8px',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  fontSize: '11.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: `1px solid ${colors.darkMode ? '#64748b' : '#94a3b8'}`,
                  background: colors.darkMode ? '#1e293b' : '#f8fafc',
                  color: colors.text,
                  width: '100%',
                }}
              >
                🙈 {tr(`Nur ${normDienst} nicht im Barcode-Stapel sammeln`, `Do not collect ${normDienst} in barcode batch`, `Alleen ${normDienst} niet in barcodestapel verzamelen`)}
              </button>
            )}
            <button
              type="button"
              onClick={async (ev) => {
                ev.stopPropagation();
                await disableBatchBarcodesFeature();
                setBatchFeatureHidden(true);
                if (window.showToast) {
                  window.showToast(`📋 ${t('ui.modal.batchHiddenToast')}`, 'info', 4000);
                }
              }}
              style={{
                marginTop: '8px',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '11.5px',
                fontWeight: 700,
                cursor: 'pointer',
                border: `1px solid ${colors.darkMode ? '#64748b' : '#94a3b8'}`,
                background: colors.darkMode ? '#0f172a' : '#ffffff',
                color: colors.text,
                width: '100%',
              }}
            >
              🙈 {t('ui.modal.batchHideBtn')}
            </button>
          </div>
        )}
      </div>
    );
  };
  const [paketdienst, setPaketdienst] = useState(''); // 🚚 vom Label-Logo gelesen (KI) — nur fürs Etikett
  const [nameUnsicher, setNameUnsicher] = useState(false); // ⚠️ Etikett im Foto sehr klein → KI-Name bitte prüfen (13.10.2026)
  const batchStatusAktualisieren = async () => {
    try {
      const lokal = batchCodesRef.current ? await batchCodesRef.current : lokalCodesRef.current;
      lokalCodesRef.current = Array.isArray(lokal) ? lokal : [];
    } catch { /* ignore */ }
    setBarcodeTrigger((n) => n + 1);
  };
  // 🔍 Doppelabgleich & Fallback („Was ist die Sendungsnummer?") ist für deutsche DHL-/Post-Shops gedacht
  // (nicht für niederländische Shops wie Green Goat oder Fremddienste wie DPD/GLS/Vinted/PostNL/Mondial Relay).
  const istDhlDoppelabgleichAktiv = useMemo(() => {
    if (lang === 'nl') return false;
    const plz = String(postalAndCity || mlResult?.ocrResult?.postal || '').trim();
    if (/\b\d{4}\s?[A-Z]{2}\b/i.test(plz)) return false; // NL-Postleitzahl (z. B. 3045RD)
    const dienst = String(paketdienst || mlResult?.metadata?.paketdienst || '').trim();
    if (dienst && !/^(dhl|deutsche\s*post)/i.test(dienst)) return false;
    return true;
  }, [lang, postalAndCity, mlResult, paketdienst]);

  // 🔍 Doppelabgleich zwischen im Foto gelesenen Barcodes und OCR-Sendungsnummer (inkl. Leitcode-Filter & DHL-Prüfziffer)
  const doppelabgleich = useMemo(() => {
    if (paketId || !ocrComplete || !istDhlDoppelabgleichAktiv) return null;
    const alleCodes = [...lokalCodesRef.current, ...serverCodesRef.current];
    const ocrTrk = String(mlResult?.ocrResult?.tracking || mlResult?.metadata?.trackingNummer || trackingNumber || '').trim();
    return doppelabgleichSendung({
      codes: alleCodes,
      tracking: trackingNumber || ocrTrk,
      plz: postalAndCity || mlResult?.ocrResult?.postal || '',
      manuellBestaetigt: trackingManuellBestaetigt,
      dienst: paketdienst || mlResult?.metadata?.paketdienst || '',
    });
    // eslint-disable-next-line
  }, [paketId, ocrComplete, istDhlDoppelabgleichAktiv, barcodeTrigger, trackingNumber, postalAndCity, mlResult, trackingManuellBestaetigt, paketdienst]);

  // Wenn ein verifizierter Barcode im Foto eine OCR-Fehllesung korrigiert (z. B. „6" statt „8" oder fehlendes „00")
  // oder bei DPD/GLS/Hermes/UPS die Sendungsnummer im Barcode steckt, während OCR keine gelesen hat,
  // Sendungsnummer automatisch berichtigen und Stapel-Status aktualisieren.
  useEffect(() => {
    if (paketId || !ocrComplete) return;
    const alleCodes = [...lokalCodesRef.current, ...serverCodesRef.current];
    const plz = postalAndCity || mlResult?.ocrResult?.postal || '';
    const ocrTrk = String(mlResult?.ocrResult?.tracking || mlResult?.metadata?.trackingNummer || '').trim();
    const effDienst = paketdienst || mlResult?.metadata?.paketdienst || '';
    const abgleich = doppelabgleichSendung({
      codes: alleCodes,
      tracking: trackingNumber || ocrTrk,
      plz,
      manuellBestaetigt: trackingManuellBestaetigt,
      dienst: effDienst,
    });
    if (!trackingManuellBestaetigt) {
      if (istDhlDoppelabgleichAktiv) {
        if (abgleich.tracking && (abgleich.status === 'korrigiert' || abgleich.status === 'verifiziert' || abgleich.status === 'pruefziffer_ok')) {
          // Nur automatisch korrigieren, wenn die aktuelle Nummer leer oder identisch mit dem ungeprüften OCR-Wert ist
          if (!trackingNumber || trackingNumber === ocrTrk) {
            if (trackingNumber !== abgleich.tracking) {
              setTrackingNumber(abgleich.tracking);
            }
          }
        } else if (abgleich.status === 'unsicher' && !abgleich.tracking && trackingNumber && trackingNumber === ocrTrk) {
          // Ungültiges OCR-Fragment (z. B. 19-stellige 0034…-Fehllesung oder 8-stelliger Steuercode) verwerfen
          setTrackingNumber('');
        }
      } else if (lang !== 'nl' && !/\b\d{4}\s*[A-Za-z]{2}\b/.test(postalAndCity || '') && abgleich.barcodeGefunden && abgleich.tracking && (abgleich.status === 'verifiziert' || abgleich.status === 'korrigiert')) {
        // 📦 Auch bei DPD / GLS / Hermes / UPS: wenn ein echter Barcode im Bild gefunden wurde
        // (z. B. 28-stelliger DPD-Barcode -> 14-stellige Paketnummer oder 1Z-UPS-Code) und das Feld noch leer
        // ist (oder zum erkannten Paketdienst passt), die verifizierte Barcode-Nummer übernehmen!
        const dienstLow = String(effDienst || '').trim().toLowerCase();
        const istDpdOderUpsBarcode = /^1Z[A-Z0-9]{16}$/i.test(abgleich.tracking) || /^\d{14}$/.test(abgleich.tracking);
        const dienstPasst = !dienstLow || (dienstLow === 'dpd' && /^\d{14}$/.test(abgleich.tracking)) || (dienstLow === 'ups' && /^1Z[A-Z0-9]{16}$/i.test(abgleich.tracking));
        if (!trackingNumber || (trackingNumber === ocrTrk && (!istDpdOderUpsBarcode || dienstPasst))) {
          if (trackingNumber !== abgleich.tracking) {
            setTrackingNumber(abgleich.tracking);
          }
        }
      }
    }
    if (hasCustomFeature('batchBarcodes')) {
      const stapelCodes = ergaenzeStrichcode(dedupeCodes(alleCodes, plz), (istDhlDoppelabgleichAktiv ? abgleich.tracking : (abgleich.barcodeGefunden && abgleich.tracking ? abgleich.tracking : trackingNumber)) || '', plz, trackingManuellBestaetigt);
      setBatchStatus(stapelCodes.length);
    }
    // eslint-disable-next-line
  }, [paketId, ocrComplete, istDhlDoppelabgleichAktiv, barcodeTrigger, mlResult, trackingManuellBestaetigt, trackingNumber, paketdienst]);
  // 📦 Vinted-Import fertig → Platzhalter „Vinted 300-352" im offenen Formular durch den Namen ersetzen
  useEffect(() => {
    const onNames = () => {
      setName((cur) => {
        const c = platzhalterCode(cur);
        const n = c ? lookupName(c) : '';
        return n || cur;
      });
    };
    window.addEventListener('kiosk:vinted-names', onNames);
    if (dpdAktiv()) {
      dpdVorladenStart(false).catch(() => {});
    }
    return () => window.removeEventListener('kiosk:vinted-names', onNames);
  }, []);
  // 📦 Gleicher Empfänger schon im Regal? → dazulegen (hat Vorrang vor dem Buchstaben)
  const gleicherEmpfaenger = useMemo(() => {
    if (paketId || typeof findSameRecipient !== 'function' || !name) return null;
    try { return findSameRecipient({ name, streetAndNumber, id: paketId }); } catch { return null; }
  }, [findSameRecipient, name, streetAndNumber, paketId]);
  const gleicheSendung = useMemo(() => {
    if (paketId || typeof findSameTracking !== 'function' || !trackingNumber) return null;
    try { return findSameTracking({ trackingNumber, id: paketId, paketdienst }); } catch { return null; }
  }, [findSameTracking, trackingNumber, paketId]);
  useEffect(() => {
    if (paketId || !name) return;
    const effDienst = paketdienst || mlResult?.metadata?.paketdienst || '';
    const carrierSort = getCarrierSortMode(effDienst, trackingNumber, autoLetter);
    const explicitSort = getExplicitCarrierSort(effDienst, trackingNumber);
    const gruppe = gleicherEmpfaenger && gleicherEmpfaenger.sicher ? String(gleicherEmpfaenger.regalplatz || '') : '';

    let vorschlag = '';
    if (carrierSort === 'kurznummer') {
      // 📦 Nach Kurznummer einsortieren: Fach = V + letzte Ziffer der Kurznummer (V0–V9)
      const vFach = vintedFach(vintedCodeAusTracking(trackingNumber) || platzhalterCode(name) || glsInVintedCode(trackingNumber, effDienst)) || '';
      const brief = vFach.charAt(0);
      const gruppePasst = gruppe && (!brief || (vFach.length > 1 ? gruppe.toUpperCase() === vFach.toUpperCase() : gruppe.toUpperCase().startsWith(brief)));
      vorschlag = (gruppePasst ? gruppe : '') || vFach;
    } else if (carrierSort === 'nachname') {
      // 🔤 Nach Nachname (A–Z) einsortieren: Fach = Buchstabe (ggf. mit Unterfach z. B. „B4")
      const fach = (autoLetter || explicitSort === 'nachname') ? autoRegalFach(name) : '';
      const brief = fach.charAt(0);
      const gruppePasst = gruppe && (!brief || (fach.length > 1 ? gruppe.toUpperCase() === fach.toUpperCase() : gruppe.toUpperCase().startsWith(brief)));
      vorschlag = (gruppePasst ? gruppe : '') || fach;
    } else {
      // 🏠 Nach Hausnummer einsortieren (Standard oder Dienst-Ausnahme wie PostNL):
      // Keinen Nachname-Buchstaben vorschlagen! Wenn der Dienst explizit auf „hausnummer" steht
      // (oder Serienmodus/autoLetter aktiv ist), Kunden-Hausnummer als Fach vorschlagen (sofern nicht Shop-Adresse).
      let hnFach = '';
      if ((explicitSort === 'hausnummer' || autoLetter) && streetAndNumber && !isForwardingAddress(streetAndNumber)) {
        const normStr = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const eigeneStr = normStr(streetAndNumber);
        const aktive = (pakete || []).filter((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt);
        const empfMitGleicherStr = new Set(
          aktive
            .filter((p) => normStr(p.streetAndNumber) === eigeneStr)
            .map((p) => String(p.name || '').trim().toLowerCase())
            .filter(Boolean)
        );
        if (empfMitGleicherStr.size < 3) {
          hnFach = extractHausnummer(streetAndNumber).toUpperCase();
        }
      }
      vorschlag = gruppe || hnFach;
    }

    if (!vorschlag && !autoRegalRef.current) return;
    setRegalplatz((cur) => {
      if (cur && cur !== autoRegalRef.current) return cur; // Eingabe des Nutzers bleibt
      autoRegalRef.current = vorschlag;
      return vorschlag;
    });
  }, [name, streetAndNumber, paketId, autoLetter, gleicherEmpfaenger, trackingNumber, paketdienst, mlResult, pakete]);

  // 💾 Solange dieses Modal offen ist, die untere App-Navigation ausblenden — sie schwebt
  // sonst über der Tastatur und nimmt beim Regalfach-Eintippen zu viel Platz weg.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('kiosk:save-modal', { detail: true }));
    return () => window.dispatchEvent(new CustomEvent('kiosk:save-modal', { detail: false }));
  }, []);

  // ⚠️ Verwechslungs-Schutz: Liegt im Nachbarfach schon dieselbe Hausnummer (anderer Empfänger)?
  // Wird beim Tippen/Scannen des Regalfachs live geprüft — rein lokal, blockiert nichts.
  // Wenn derselbe Empfänger schon im gewählten Fach liegt (gleicherEmpfaenger), kein Konflikt!
  const regalKonflikt = useMemo(() => {
    if (typeof checkRegalConflict !== 'function' || !regalplatz || !streetAndNumber) return null;
    const normZiel = normalizeRegalplatz(regalplatz);
    if (gleicherEmpfaenger && gleicherEmpfaenger.sicher && regalGleich(gleicherEmpfaenger.regalplatz, normZiel)) {
      return null;
    }
    try {
      return checkRegalConflict({
        regalplatz: normZiel,
        streetAndNumber,
        name,
        id: paketId,
        paketdienst: paketdienst || mlResult?.metadata?.paketdienst || '',
        trackingNumber,
      });
    } catch { return null; }
  }, [checkRegalConflict, regalplatz, streetAndNumber, name, paketId, gleicherEmpfaenger, paketdienst, mlResult, trackingNumber]);
  const [showRegionCorrector, setShowRegionCorrector] = useState(false);
  const [regionCorrecting, setRegionCorrecting] = useState(false);

  // 🗄️ 1-Tipp-Schnellwahl für Box-Systeme (z. B. A01–C12, PL1–PL4):
  // Zeigt häufige Präfixe (A, B, C, PL1–PL4) und die am wenigsten belegten / freien Boxen.
  const regalSchnellwahl = useMemo(() => {
    if (autoLetter) return { hatBoxSystem: false, prefixes: [], sonderPlaetze: [], vorschlaege: [], aktiverPrefix: '' };
    return berechneRegalSchnellwahl(pakete, regalplatz);
  }, [pakete, regalplatz, autoLetter]);

  const renderRegalSchnellwahl = (focusInputRef = null) => {
    if (!regalSchnellwahl.hatBoxSystem) return null;
    const curNorm = String(regalplatz || '').trim().toUpperCase();
    const prefixes = regalSchnellwahl.prefixes || [];
    const sonderPlaetze = regalSchnellwahl.sonderPlaetze || [];
    const vorschlaege = regalSchnellwahl.vorschlaege || [];
    return (
      <div data-testid="shelf-quick-select" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {(prefixes.length > 0 || sonderPlaetze.length > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: colors.textSecondary || '#888', fontWeight: 600 }}>🗄️ Regal:</span>
            {prefixes.map((pfx) => {
              const isActive = curNorm === pfx || (curNorm.startsWith(pfx) && /^\d+$/.test(curNorm.slice(pfx.length)));
              return (
                <button
                  key={pfx}
                  type="button"
                  data-testid={`shelf-prefix-${pfx}`}
                  onClick={() => {
                    serieStoppen();
                    setRegalplatz(pfx);
                    if (invalidFields.regalplatz) setInvalidFields((f) => ({ ...f, regalplatz: false }));
                    if (focusInputRef?.current) {
                      try { focusInputRef.current.focus(); } catch { /* ignore */ }
                    }
                  }}
                  style={{
                    padding: '5px 11px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    border: `1.5px solid ${isActive ? '#3b82f6' : (colors.border || '#ccc')}`,
                    background: isActive ? (colors.darkMode ? '#1e3a8a' : '#dbeafe') : (colors.darkMode ? '#22272e' : '#f3f4f6'),
                    color: isActive ? (colors.darkMode ? '#93c5fd' : '#1d4ed8') : colors.text,
                  }}
                >
                  {pfx}
                </button>
              );
            })}
            {sonderPlaetze.map((sp) => {
              const isActive = normalizeRegalplatz(curNorm) === sp.code;
              return (
                <button
                  key={sp.code}
                  type="button"
                  data-testid={`shelf-prefix-${sp.code}`}
                  onClick={() => {
                    serieStoppen();
                    setRegalplatz(sp.code);
                    if (invalidFields.regalplatz) setInvalidFields((f) => ({ ...f, regalplatz: false }));
                  }}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '8px',
                    fontSize: '12.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    border: `1.5px solid ${isActive ? '#3b82f6' : (colors.border || '#ccc')}`,
                    background: isActive ? (colors.darkMode ? '#1e3a8a' : '#dbeafe') : (colors.darkMode ? '#22272e' : '#f3f4f6'),
                    color: isActive ? (colors.darkMode ? '#93c5fd' : '#1d4ed8') : colors.text,
                  }}
                  title={sp.count > 0 ? `${sp.code} (${sp.count} belegt)` : `${sp.code} (frei)`}
                >
                  {sp.code}{sp.count > 0 ? ` (${sp.count})` : ''}
                </button>
              );
            })}
          </div>
        )}
        {vorschlaege.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: colors.textSecondary || '#888', fontWeight: 600 }}>💡 Frei:</span>
            {vorschlaege.map((b) => {
              const selected = normalizeRegalplatz(curNorm) === b.code;
              return (
                <button
                  key={b.code}
                  type="button"
                  data-testid={`shelf-free-${b.code}`}
                  onClick={() => {
                    serieStoppen();
                    setRegalplatz(b.code);
                    if (invalidFields.regalplatz) setInvalidFields((f) => ({ ...f, regalplatz: false }));
                  }}
                  style={{
                    padding: '4px 9px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: `1px solid ${selected ? '#16a34a' : (b.count === 0 ? '#4caf50' : (colors.border || '#ccc'))}`,
                    background: selected
                      ? '#16a34a'
                      : b.count === 0
                        ? (colors.darkMode ? '#13301a' : '#e8f5e9')
                        : (colors.darkMode ? '#22272e' : '#f8fafc'),
                    color: selected ? '#fff' : (b.count === 0 ? (colors.darkMode ? '#86efac' : '#166534') : colors.text),
                  }}
                  title={b.count === 0 ? `${b.code} ist aktuell frei` : `${b.code} (${b.count} Paket)`}
                >
                  {b.code}{b.count > 0 ? ` (${b.count})` : ''}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const istGrenzpaket = grenzpaketAktiv();

  // 🇨🇭 Grenzpaket: Einzeilige Schnelltipp-Buttons statt Regalfach-Textfeld
  // (Später (Lager-Scan), Vorne, Hinten, Sperrgut in einer Zeile)
  const renderGrenzpaketRegalSchnelltipp = () => {
    const curReg = String(regalplatz || '').trim();
    const isCustomScanned = !!curReg && !['vorne', 'hinten', 'sperrgut'].includes(curReg.toLowerCase());
    const options = [
      { val: '', label: '⏳ Später (Lager-Scan)' },
      { val: 'Vorne', label: '⬆️ Vorne' },
      { val: 'Hinten', label: '⬇️ Hinten' },
      { val: 'Sperrgut', label: '📐 Sperrgut' },
    ];
    return (
      <div data-testid="gp-shelf-quickrow" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '6px',
            width: '100%',
          }}
        >
          {options.map((opt) => {
            const isSel = curReg.toLowerCase() === opt.val.toLowerCase();
            return (
              <button
                key={opt.val || 'later'}
                type="button"
                data-testid={`gp-presort-${opt.val || 'later'}`}
                onClick={() => {
                  serieStoppen();
                  setRegalplatz(opt.val);
                  try { localStorage.setItem('kiosk_gp_last_presort', opt.val); } catch { /* ignore */ }
                  if (invalidFields.regalplatz) setInvalidFields((f) => ({ ...f, regalplatz: false }));
                }}
                style={{
                  padding: '10px 4px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  lineHeight: 1.2,
                  fontWeight: isSel ? 800 : 600,
                  cursor: 'pointer',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  border: `2px solid ${isSel ? '#2563eb' : (colors.border || '#cbd5e1')}`,
                  background: isSel ? (colors.darkMode ? '#1e3a8a' : '#dbeafe') : (colors.darkMode ? '#1c2128' : '#ffffff'),
                  color: isSel ? (colors.darkMode ? '#93c5fd' : '#1d4ed8') : colors.text,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {isCustomScanned && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 10px',
              borderRadius: '8px',
              fontSize: '12.5px',
              fontWeight: 700,
              background: colors.darkMode ? '#14331f' : '#dcfce7',
              border: '1px solid #22c55e',
              color: colors.darkMode ? '#86efac' : '#166534',
            }}
          >
            <span>📍 Regalfach gescannt: {curReg}</span>
            <button
              type="button"
              onClick={() => {
                serieStoppen();
                setRegalplatz('');
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                fontWeight: 800,
                cursor: 'pointer',
                fontSize: '13px',
                padding: '0 4px',
              }}
            >
              ✕
            </button>
          </div>
        )}
      </div>
    );
  };

  // 🪵 Paletten-/Gebinde-Schnellwahl (EU-Palette, Einwegpalette, Halbpalette, Gitterbox + freie Eingabe)
  const PALETTEN_PRESETS = ['EU-Palette', 'Einwegpalette', 'Halbpalette', 'Gitterbox', 'Sperrgut'];
  const renderPalettenAuswahl = () => {
    if (!showPaletteBar && !palettenTyp) return null;
    const curTyp = String(palettenTyp || '').trim();
    const isCustomTyp = Boolean(curTyp) && !PALETTEN_PRESETS.some((p) => p.toLowerCase() === curTyp.toLowerCase());
    return (
      <div
        data-testid="palette-type-selector"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '6px',
          width: '100%',
          background: colors.darkMode ? '#2d2417' : '#fffbeb',
          border: `1px solid ${colors.darkMode ? '#78350f' : '#fde68a'}`,
          borderRadius: '8px',
          padding: '6px 10px',
          marginTop: '6px',
          boxSizing: 'border-box',
        }}
      >
        <span
          style={{
            fontSize: '12px',
            fontWeight: 700,
            color: colors.darkMode ? '#fcd34d' : '#92400e',
            marginRight: '2px',
            whiteSpace: 'nowrap',
          }}
        >
          🪵 Palette / Gebinde:
        </span>
        {PALETTEN_PRESETS.map((preset) => {
          const active = curTyp.toLowerCase() === preset.toLowerCase();
          return (
            <button
              key={preset}
              type="button"
              data-testid={`palette-preset-${preset}`}
              onClick={() => {
                serieStoppen();
                setPalettenTyp(active ? '' : preset);
              }}
              style={{
                padding: '5px 10px',
                borderRadius: '999px',
                fontSize: '12.5px',
                fontWeight: active ? 800 : 600,
                cursor: 'pointer',
                border: active
                  ? '2px solid #d97706'
                  : `1px solid ${colors.darkMode ? '#574327' : '#fcd34d'}`,
                background: active
                  ? '#d97706'
                  : (colors.darkMode ? '#1c1917' : '#ffffff'),
                color: active ? '#ffffff' : (colors.darkMode ? '#fde68a' : '#78350f'),
                transition: 'all 0.15s',
              }}
            >
              {preset}
            </button>
          );
        })}
        <input
          type="text"
          data-testid="palette-custom-input"
          maxLength={40}
          value={isCustomTyp ? curTyp : ''}
          placeholder="+ Eigene Art…"
          onChange={(e) => {
            serieStoppen();
            setPalettenTyp(e.target.value);
          }}
          style={{
            width: '115px',
            padding: '5px 8px',
            borderRadius: '8px',
            fontSize: '12.5px',
            fontWeight: isCustomTyp ? 700 : 500,
            border: isCustomTyp
              ? '2px solid #d97706'
              : `1px solid ${colors.darkMode ? '#574327' : '#fcd34d'}`,
            background: colors.darkMode ? '#1c1917' : '#ffffff',
            color: colors.darkMode ? '#fde68a' : '#78350f',
          }}
        />
        {curTyp && (
          <button
            type="button"
            title="Paletten-Auswahl zurücksetzen"
            onClick={() => {
              serieStoppen();
              setPalettenTyp('');
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.darkMode ? '#fcd34d' : '#92400e',
              fontWeight: 800,
              cursor: 'pointer',
              fontSize: '13px',
              padding: '2px 4px',
            }}
          >
            ✕
          </button>
        )}
      </div>
    );
  };

  // 📦 Paketanzahl-Leiste: Schnellwahl 1–5 + freies Zahlenfeld (xx Pakete bis 999) + optionaler Paletten-Schalter
  const renderPaketAnzahlLeiste = () => {
    const isCustomCount = paketAnzahl > 5 || paketAnzahlInput !== '';
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '4px',
          flex: '1 0 auto',
          background: colors.darkMode ? '#1e3a5f' : '#e0f2fe',
          borderRadius: '6px',
          padding: '4px 8px',
          minWidth: '200px',
        }}
      >
        <span
          style={{
            fontSize: '12px',
            color: colors.darkMode ? '#93c5fd' : '#1e40af',
            marginRight: '4px',
            whiteSpace: 'nowrap',
            fontWeight: 600,
          }}
        >
          📦 {t('packages.amount')}:
        </span>
        {[1, 2, 3, 4, 5].map((num) => (
          <button
            key={num}
            type="button"
            data-testid={`pkg-count-btn-${num}`}
            onClick={() => {
              serieStoppen();
              setPaketAnzahl(num);
              setPaketAnzahlInput('');
            }}
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border:
                paketAnzahl === num && paketAnzahlInput === ''
                  ? '3px solid #3b82f6'
                  : `1px solid ${colors.darkMode ? '#4a5568' : '#cbd5e0'}`,
              background:
                paketAnzahl === num && paketAnzahlInput === ''
                  ? '#3b82f6'
                  : (colors.darkMode ? '#2d3748' : '#fff'),
              color:
                paketAnzahl === num && paketAnzahlInput === ''
                  ? '#fff'
                  : (colors.darkMode ? '#e2e8f0' : '#2d3748'),
              fontWeight: 'bold',
              fontSize: '15px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {num}
          </button>
        ))}
        {/* 🔢 Freies Zahlenfeld für xx Pakete (1–999) */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            marginLeft: '4px',
            background: colors.darkMode ? '#2d3748' : '#ffffff',
            border: isCustomCount
              ? '2px solid #3b82f6'
              : `1px solid ${colors.darkMode ? '#4a5568' : '#cbd5e0'}`,
            borderRadius: '8px',
            padding: '2px 6px',
            height: '34px',
            boxSizing: 'border-box',
          }}
        >
          <input
            type="number"
            min="1"
            max="999"
            inputMode="numeric"
            data-testid="pkg-count-custom-input"
            value={paketAnzahlInput !== '' ? paketAnzahlInput : (paketAnzahl > 5 ? String(paketAnzahl) : '')}
            placeholder="xx"
            onChange={(e) => {
              serieStoppen();
              const raw = e.target.value.replace(/\D+/g, '').slice(0, 3);
              setPaketAnzahlInput(raw);
              if (raw) {
                const n = Math.min(999, Math.max(1, parseInt(raw, 10) || 1));
                setPaketAnzahl(n);
              } else {
                setPaketAnzahl(1);
              }
            }}
            onBlur={() => {
              if (paketAnzahlInput !== '') {
                const n = Math.min(999, Math.max(1, parseInt(paketAnzahlInput, 10) || 1));
                setPaketAnzahl(n);
                setPaketAnzahlInput(n > 5 ? String(n) : '');
              }
            }}
            style={{
              width: '42px',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: colors.darkMode ? '#e2e8f0' : '#1e293b',
              fontWeight: 800,
              fontSize: '14px',
              textAlign: 'center',
              padding: 0,
            }}
          />
          <span
            style={{
              fontSize: '11.5px',
              fontWeight: 600,
              color: colors.darkMode ? '#93c5fd' : '#1e40af',
              whiteSpace: 'nowrap',
            }}
          >
            Pakete
          </span>
        </div>
        {/* 🪵 Paletten-Leiste ein-/ausblenden */}
        <button
          type="button"
          data-testid="toggle-palette-bar-btn"
          onClick={() => {
            serieStoppen();
            setShowPaletteBar((v) => !v);
          }}
          style={{
            marginLeft: 'auto',
            padding: '5px 9px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
            border: (showPaletteBar || palettenTyp)
              ? '1.5px solid #d97706'
              : `1px solid ${colors.darkMode ? '#4a5568' : '#cbd5e0'}`,
            background: palettenTyp
              ? '#d97706'
              : (showPaletteBar ? (colors.darkMode ? '#3b2f1e' : '#fef3c7') : (colors.darkMode ? '#2d3748' : '#fff')),
            color: palettenTyp
              ? '#fff'
              : (showPaletteBar ? (colors.darkMode ? '#fcd34d' : '#92400e') : (colors.darkMode ? '#e2e8f0' : '#2d3748')),
            whiteSpace: 'nowrap',
          }}
          title="Paletten- / Gebinde-Art auswählen (EU-Palette, Einwegpalette …)"
        >
          🪵 {palettenTyp || 'Palette'}
        </button>
      </div>
    );
  };

  // ✅ Bestätigungs-Schritt nach erfolgreicher KI-Erfassung: zeigt nur die erkannten
  // Daten + ein großes Regalfach-Feld. Der Normalfall (KI stimmt, nur Fach fehlt) ist
  // damit ohne Scrollen erledigt. „Alles bearbeiten" führt ins volle Formular.
  const [quickConfirm, setQuickConfirm] = useState(false);
  // 🤖 Annotiertes KI-Bild: liegt jetzt unten und ist standardmäßig zugeklappt
  const [showKiOverlay, setShowKiOverlay] = useState(false);
  const quickShelfRef = useRef(null);

  // Fach-Feld im Bestätigungs-Schritt scharfstellen: der Regal-QR-Scanner tippt
  // „RG:K28" + Enter direkt hinein → Foto, Scan, gespeichert.
  // Im Serienmodus mit bereits automatisch vorbelegtem Fach (z. B. A–Z „B3") die Tastatur
  // NICHT aufklappen — der 2-Sekunden-Auto-Save läuft sofort los und die Tastatur verdeckt sonst das Bild.
  useEffect(() => {
    if (!quickConfirm) return;
    if (seriesOn && regalplatz && String(regalplatz).trim()) return;
    const id = setTimeout(() => {
      try { quickShelfRef.current?.focus(); } catch { /* egal */ }
    }, 120);
    return () => clearTimeout(id);
  }, [quickConfirm, seriesOn, regalplatz]);

  /**
   * Versucht ML-Analyse: Foto direkt an Server senden, ohne Zuschneiden.
   * Bei Erfolg: Vorschlag anzeigen. Bei Fehler/Fallback: Normaler Crop-Flow.
   */
  const handleMLAnalysis = async (imageDataUrl, rawFile = null) => {
    setMlAnalyzing(true);
    setMlResult(null);
    setAbsenderData(null);
    setPlzCheckBestaetigt(false);
    setShowPlzModal(false);
    setOcrComplete(false);
    setStaticImage(null);
    setRecognizedBlocks([]);
    
    try {
      const { analyzeImage } = await import('./services/mlService');
      
      // Direkt File nutzen wenn vorhanden, sonst Data-URL zu Blob konvertieren
      const blob = rawFile || await (await fetch(imageDataUrl)).blob();
      
      console.log('🤖 Sende Bild an ML-Service...');
      setMlWarte(null);
      const result = await analyzeImage(blob, { onWarte: (w) => {
        setMlWarte((alt) => ({ ...w, ab: alt ? alt.ab : Date.now() - w.seitMs }));
        // Ausfall sofort ins Fehlerprotokoll (nicht erst nach Ablauf der Wartezeit) — einmal je Foto
        if (w.versuch === 1) reportError('erkennung_server_weg', 'Erkennung kurz nicht erreichbar — App wartet und versucht erneut');
      } });
      setMlWarte(null);
      // 📋 Server-Barcodes (zxing-cpp) immer übernehmen (für Sendungsnummer-Abgleich + Barcode-Stapel)
      if (Array.isArray(result?.barcodes)) {
        serverCodesRef.current = result.barcodes;
        batchStatusAktualisieren();
      }
      
      if (result.fallback) {
        // ML-Service nicht verfügbar → normaler Flow
        console.log('🤖 ML-Service nicht verfügbar, Fallback auf manuelles Cropping');
        reportError('erkennung_nicht_verfuegbar', result.error || result.reason || '');
        setMlAvailable(false);
        setMlAnalyzing(false);
        return false;
      }
      
      setMlAvailable(true);
      console.log(`🤖 ML-Ergebnis: ${result.regions?.length} Regionen, ` +
                   `Name="${result.ocrResult?.name}", Straße="${result.ocrResult?.street}", ` +
                   `Tracking="${result.ocrResult?.tracking || '-'}", Gewicht="${result.ocrResult?.weight || '-'}", ` +
                   `Farbe="${result.ocrResult?.paketFarbe || '-'}", ` +
                   `${result.processingTime}ms`);
      
      // Ergebnis speichern für Anzeige
      setMlResult(result);
      setAbsenderData(result.absender || null);
      setPlzCheckBestaetigt(false);
      setShowPlzModal(false);
      // 🔄 Retoure-Hinweis-Zustand für dieses (neue) Bild zurücksetzen
      setAbsenderUebernommen(false);
      setRetoureHintDismissed(false);

      // Felder vorausfüllen wenn OCR-Ergebnis vorhanden
      if (result.ocrResult) {
        const { rawName: rawOcrName } = integrationIntake.preprocessOcr(result.ocrResult);
        const cleanedOcrName = bereinigeEmpfaengerName(rawOcrName);
        if (cleanedOcrName) setName(cleanedOcrName);
        if (result.ocrResult.street) setStreetAndNumber(result.ocrResult.street);
        if (result.ocrResult.postal && result.ocrResult.city) {
          setPostalAndCity(`${result.ocrResult.postal} ${result.ocrResult.city}`);
        } else if (result.ocrResult.postal) {
          setPostalAndCity(result.ocrResult.postal);
        } else if (result.ocrResult.city) {
          setPostalAndCity(result.ocrResult.city);
        }
        // 📦 Sendungsnummer aus OCR (Sektions-Sichtbarkeit bleibt wie vom Nutzer gewählt)
        if (result.ocrResult.tracking) {
          setTrackingNumber(result.ocrResult.tracking);
        }
      }

      // 📦 Label OHNE Empfänger (z. B. Vinted Go): ocrResult ist dann null, die Sendungsnummer
      // steht nur in den Metadaten → trotzdem übernehmen (sonst findet die Abholcode-Suche nichts).
      const metaTracking = String(result.metadata?.trackingNummer || '').trim();
      if (!result.ocrResult?.tracking && metaTracking) {
        setTrackingNumber(metaTracking);
      }

      // 🚚 Paketdienst laut Logo/Text (nicht aus 3S-Sendungsnummer geraten: 3S ist in NL mehrdeutig).
      // ✈️ 10-stellige Waybill (z. B. "55 9854 2434", Modulo-7) oder (2L)-Routingbarcode → "DHL Express",
      // 📦 12-stellige Airwaybill (z. B. "878509317530", FedEx NEW HAL ADDRESS) → "FedEx".
      let erkannterDienst = erkennePaketdienst({
        metadata: result.metadata,
        ocrResult: result.ocrResult,
        barcodes: [...lokalCodesRef.current, ...(result.barcodes || [])],
        tracking: result.ocrResult?.tracking || metaTracking || '',
      });
      setPaketdienst(erkannterDienst);
      setNameUnsicher(!!(result.nameUnsicher || result.etikettKlein));

      // ⚖️ Gewicht + 🎨 Farbe kommen aus den Gemini-Metadaten (result.metadata.gewicht
      // / .paketFarbe), NICHT aus ocrResult. Fallback auf ocrResult, falls der
      // Server sie später dort einsortiert. (extrasSection wird NICHT zwangsweise aufgeklappt!)
      const detectedWeight = result.metadata?.gewicht || result.ocrResult?.weight;
      if (detectedWeight) {
        setGewicht(detectedWeight);
        console.log(`⚖️ Gewicht erkannt: ${detectedWeight}`);
      }
      // Kleinschreibung, damit der Wert die Farbkugeln (braun/weiß/grau/…) trifft;
      // unbekannte Farben (z.B. "blau-weiß") werden trotzdem gespeichert + als Text gezeigt.
      const detectedColor = (result.metadata?.paketFarbe || result.ocrResult?.paketFarbe || '').toLowerCase().trim();
      if (detectedColor) {
        setFarbe(detectedColor);
        console.log(`🎨 Paketfarbe erkannt: ${detectedColor}`);
      }
      
      // Thumbnail als statisches Bild setzen
      if (result.thumbnail) {
        setStaticImage(`data:image/jpeg;base64,${result.thumbnail}`);
      }
      
      // 📱 Foto vom Bildschirm der Vinted-App statt vom Etikett (nur Sonderfunktion „vintedImport"):
      // die Etikett-Erkennung liest dort Unsinn oder nichts → den Listen-Leser fragen. Liefert er
      // genau EIN Paar Kurznummer + Name, gilt das; sonst bleibt alles wie erkannt.
      let vintedSchirm = null;
      // … oder Vinted wurde als Paketdienst erkannt, aber keine Nummer gelesen (dann fehlte sie auf dem Etikett).
      if (!paketId && hasCustomFeature('vintedImport') && brauchtVintedNachfrage({
        name: result.ocrResult?.name, street: result.ocrResult?.street,
        tracking: result.ocrResult?.tracking || result.metadata?.trackingNummer,
        paketdienst: result.metadata?.paketdienst,
      })) {
        try {
          const { extractVintedList } = await import('./services/mlService');
          vintedSchirm = ausVintedBildschirm(await extractVintedList(blob), result.ocrResult?.tracking || result.metadata?.trackingNummer);
        } catch (e) {
          console.warn('📱 Vinted-Bildschirm nicht lesbar:', e?.message || e);
        }
        if (vintedSchirm) {
          setName(vintedSchirm.name);
          setStreetAndNumber(''); setPostalAndCity(''); // was die Etikett-Erkennung dort „gelesen" hat, ist Bildschirmtext
          setTrackingNumber(vintedSchirm.tracking);
          setPaketdienst('Vinted Go');
          rememberPairs([{ code: vintedSchirm.code, name: vintedSchirm.name }]);
        }
      }

      setMlAnalyzing(false);
      setOcrComplete(true);

      // 📦 Vinted-Go-Label: KEIN Name drauf, nur Sendungsnummer (16 Ziffern, beginnt mit 17,
      // oder „VGS…"). Platzhalter „Vinted 308-794" = letzte 6 Ziffern — genau diese Kurznummer
      // zeigt die Vinted-Partner-App bei der Abholung → so findet man das Paket per Suche.
      let erkannterName = vintedSchirm ? vintedSchirm.name : bereinigeEmpfaengerName(result.ocrResult?.name || '');
      const trk = String(result.ocrResult?.tracking || result.metadata?.trackingNummer || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!erkannterName && (/^17\d{14}$/.test(trk) || /^VGS\d{6,}$/.test(trk))) {
        const d = trk.replace(/\D/g, '').slice(-6);
        // Sonderfunktion: vorher importierte Vinted-Liste kennt den echten Namen schon
        const bekannt = hasCustomFeature('vintedImport') ? lookupName(d) : '';
        erkannterName = bekannt || platzhalterName(d);
        setName(erkannterName);
      }

      // 📦 DPD Pickup: Vorab-Namen (SHIPPED / DELIVERED) im Formular ergänzen (nur lesend, keine Buchung vor dem Speichern!)
      if (!paketId && dpdAktiv()) {
        const dpdCode = dpdCodeAusTracking(trk)
          || (Array.isArray(result.barcodes) ? result.barcodes.map((b) => dpdCodeAusTracking(b?.text || b?.value || b)).find(Boolean) : '')
          || '';
        if (dpdCode && !/^(vinted|dhl|mondial|inpost|gls|ups|postnl|bpost|hermes)/i.test(result.metadata?.paketdienst || '')) {
          let dpdName = bekannterDpdName(dpdCode);
          if (!dpdName && !erkannterName && (/^dpd/i.test(result.metadata?.paketdienst || '') || trk.length >= 27)) {
            try {
              const lDpd = await dpdLookup(dpdCode);
              if (lDpd && lDpd.found && lDpd.name) dpdName = lDpd.name;
            } catch { /* ignore */ }
          }
          if (dpdName && (!erkannterName || result.nameUnsicher || result.etikettKlein)) {
            erkannterName = bereinigeEmpfaengerName(dpdName) || dpdName;
            setName(erkannterName);
          }
          if (dpdName || /^dpd/i.test(result.metadata?.paketdienst || '')) {
            setPaketdienst('DPD');
            if (!trk || trk.length >= 27) setTrackingNumber(dpdCode);
          }
        }
      }

      // 🪪 „Ausweis zuerst"-Modus: Wenn auf dem Foto kein Paket-Empfänger stand (weil Ausweis fotografiert wurde),
      // direkt über die Ausweis-KI (identifyForPickup) Namen & Adresse lesen.
      if (!paketId && !erkannterName && isIdFirstEnabled()) {
        try {
          const idHit = await identifyForPickup(blob);
          if (idHit && idHit.name) {
            erkannterName = idHit.name;
            setName(idHit.name);
            if (idHit.street && !result.ocrResult?.street) setStreetAndNumber(idHit.street);
            if (idHit.postal && !result.ocrResult?.postal) setPostalAndCity(idHit.postal);
          }
        } catch (idErr) {
          console.warn('🪪 Ausweis-Fallback nicht lesbar:', idErr);
        }
      }

      // #001 Gepäck / Gegenstand ohne Namen: Wenn aktiviert und kein Name auf dem Foto stand → nächste Nummer vergeben
      if (!paketId && !erkannterName && isAutoNumberEnabled()) {
        erkannterName = getNextAutoNumber(pakete);
        setName(erkannterName);
      }

      // 📒 Adressbuch-Abgleich: Wenn der erkannte Name im Adressbuch steht, Abteilung/Zimmer, Stamm-Fach & Mitarbeiter-Hinweis zeigen
      if (!paketId && erkannterName && !erkannterName.startsWith('#')) {
        const exactHit = findBestAddressBookMatch(erkannterName) || (() => {
          const addrHits = searchAddressBook(erkannterName, 3);
          return addrHits.find((e) => e.name.toLowerCase() === erkannterName.toLowerCase()) || (addrHits.length === 1 ? addrHits[0] : null);
        })();
        if (exactHit) {
          if (exactHit.department && !result.ocrResult?.street) {
            setStreetAndNumber(exactHit.department);
          }
          if (exactHit.shelf) {
            setRegalplatz((cur) => cur || String(exactHit.shelf).toUpperCase().replace(/\s+/g, ''));
          }
          if (window.showToast) {
            const parts = [`⭐ ${t('ui.modal.onListToast', { name: exactHit.name })}`];
            if (exactHit.note) parts.push(`📌 ${exactHit.note}`);
            if (exactHit.shelf) parts.push(`🗄️ ${t('ui.modal.shelfToast', { shelf: exactHit.shelf })}`);
            window.showToast(parts.join(' · '), 'info', 5500);
          }
        }
      }

      if (!paketId && erkannterName) setQuickConfirm(true);
      // 🧯 Foto ausgewertet, aber weder Name noch Sendungsnummer gefunden → fürs Admin-Board zählen
      if (!paketId && !erkannterName && !trk) reportError('erkennung_leer', '', { ms: result.processingTime || null });

      return true;

    } catch (err) {
      console.warn('🤖 ML-Analyse fehlgeschlagen:', err);
      if (!/\(401\)|401/.test(String((err && err.message) || err))) reportError('erkennung_fehler', err);
      setMlAvailable(false);
      setMlAnalyzing(false);
      return false;
    }
  };

  // 🔄 Empfänger ↔ Absender tauschen (für Retoure, PLZ-Kreis-Warnung oder manuellen 1-Tipp-Tausch).
  // Die bisherige Empfänger-Adresse geht NICHT verloren, sondern wird als Absender am Paket gespeichert!
  const tauscheEmpfaengerUndAbsender = () => {
    serieStoppen();
    const a = aktiverAbsender;
    if (!a || (!a.name && !a.street && !a.postal && !a.city)) return null;

    const curPlz = extractPlzFromText(postalAndCity);
    const curCity = curPlz
      ? String(postalAndCity || '').replace(curPlz, '').trim()
      : String(postalAndCity || '').trim();
    const prevEmpfaenger = {
      name: String(name || '').trim(),
      street: String(streetAndNumber || '').trim(),
      postal: curPlz,
      city: curCity,
    };

    const nextName = String(a.name || '').trim();
    const nextStreet = String(a.street || '').trim();
    const nextPostalCity = [a.postal, a.city].map((x) => String(x || '').trim()).filter(Boolean).join(' ');
    const hatPrev = Boolean(prevEmpfaenger.name || prevEmpfaenger.street || prevEmpfaenger.postal || prevEmpfaenger.city);
    const nextAbsenderObj = hatPrev ? prevEmpfaenger : null;

    if (nextName) {
      setName(nextName);
      if (invalidFields.name) setInvalidFields((f) => ({ ...f, name: false }));
    }
    setStreetAndNumber(nextStreet);
    setPostalAndCity(nextPostalCity);
    setAbsenderData(nextAbsenderObj);
    setAbsenderUebernommen(true);
    setPlzCheckBestaetigt(true);
    setShowPlzModal(false);

    let nextRegalplatz = regalplatz;
    if (!paketId && autoLetter && nextName && !isVintedSortOn()) {
      const neuesFach = autoRegalFach(nextName);
      if (neuesFach && (!regalplatz || regalplatz === autoRegalRef.current)) {
        autoRegalRef.current = neuesFach;
        setRegalplatz(neuesFach);
        nextRegalplatz = neuesFach;
      }
    }

    return {
      name: nextName || name,
      streetAndNumber: nextStreet,
      postalAndCity: nextPostalCity,
      regalplatz: nextRegalplatz,
      absender: isCaptureSenderOn() ? formatAbsenderObj(nextAbsenderObj) : '',
    };
  };

  // 🔄 Bei Retoure: Absenderdaten (vom Label gelesen) als Abholer/Empfänger übernehmen.
  const uebernehmeAbsenderAlsEmpfaenger = () => {
    tauscheEmpfaengerUndAbsender();
  };

  // 📍 Falls nur 1 Adresse gelesen wurde (und diese im falschen PLZ-Kreis liegt):
  // Aktuelle Adresse als Absender sichern und Empfänger-Felder zur Neueingabe leeren.
  const verschiebeAktuellenInAbsenderUndLeereEmpfaenger = () => {
    serieStoppen();
    const curPlz = extractPlzFromText(postalAndCity);
    const curCity = curPlz
      ? String(postalAndCity || '').replace(curPlz, '').trim()
      : String(postalAndCity || '').trim();
    const prevEmpfaenger = {
      name: String(name || '').trim(),
      street: String(streetAndNumber || '').trim(),
      postal: curPlz,
      city: curCity,
    };
    if (prevEmpfaenger.name || prevEmpfaenger.street || prevEmpfaenger.postal) {
      setAbsenderData(prevEmpfaenger);
    }
    setName('');
    setStreetAndNumber('');
    setPostalAndCity('');
    setPlzCheckBestaetigt(true);
    setShowPlzModal(false);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="quick-name-input"]') || document.getElementById('nameField');
      if (el) { try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); } catch { /* */ } }
    }, 80);
  };

  // 📍 Wiederverwendbares PLZ-Kreis-Warnbanner (erscheint sofort nach dem Scan, wenn die PLZ vom Shop-PLZ-Kreis abweicht)
  const renderPlzKreisHinweis = () => {
    if (!plzAbweichung || plzCheckBestaetigt) return null;
    const a = aktiverAbsender;
    const absZeile = formatAbsenderObj(a);
    return (
      <div
        data-testid="plz-kreis-warning"
        style={{
          marginTop: '10px',
          marginBottom: '12px',
          padding: '12px 14px',
          borderRadius: '10px',
          background: colors.darkMode ? '#3b1d1d' : '#fff1f2',
          border: '2px solid #f43f5e',
          color: colors.text,
          boxShadow: '0 2px 10px rgba(244, 63, 94, 0.18)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800, fontSize: '14px', color: colors.darkMode ? '#fda4af' : '#be123c' }}>
          <span>⚠️ {t('ui.modal.plzMismatchTitle', { plz: plzAbweichung.empfPlz, usual: plzAbweichung.beispiel })}</span>
        </div>
        <div style={{ fontSize: '13px', marginTop: '4px', lineHeight: 1.4 }}>
          {plzAbweichung.absPasstZumKreis
            ? t('ui.modal.plzSenderFits', { senderPlz: plzAbweichung.absPlz })
            : t('ui.modal.plzExplain')}
        </div>
        {plzAbweichung.hatAbsender ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
            <button
              type="button"
              data-testid="plz-swap-btn"
              onClick={() => tauscheEmpfaengerUndAbsender()}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                fontWeight: 800,
                background: plzAbweichung.absPasstZumKreis ? '#16a34a' : '#f59e0b',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <span>🔄 {t('ui.modal.plzUseSender', { sender: absZeile })}</span>
              {plzAbweichung.absPasstZumKreis && (
                <span style={{ fontSize: '11px', background: 'rgba(255,255,255,0.22)', padding: '2px 6px', borderRadius: '999px', flexShrink: 0 }}>
                  🎯 PLZ {plzAbweichung.absPlz}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => { serieStoppen(); setPlzCheckBestaetigt(true); }}
              style={{
                width: '100%',
                padding: '7px 10px',
                fontSize: '12px',
                fontWeight: 600,
                background: 'transparent',
                color: colors.textSecondary || '#888',
                border: `1px solid ${colors.border || '#ccc'}`,
                borderRadius: '7px',
                cursor: 'pointer',
              }}
            >
              ✓ {t('ui.modal.plzKeepCurrent', { name: name || t('ui.modal.currentRecipient'), plz: plzAbweichung.empfPlz })}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={verschiebeAktuellenInAbsenderUndLeereEmpfaenger}
              style={{
                flex: 1,
                minWidth: '170px',
                padding: '9px 12px',
                fontSize: '13px',
                fontWeight: 700,
                background: '#f59e0b',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              ✏️ {t('ui.modal.plzIsSender')}
            </button>
            <button
              type="button"
              onClick={() => { serieStoppen(); setPlzCheckBestaetigt(true); }}
              style={{
                padding: '9px 12px',
                fontSize: '13px',
                fontWeight: 600,
                background: 'transparent',
                color: colors.text,
                border: `1px solid ${colors.border || '#ccc'}`,
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              ✓ {t('ui.modal.plzConfirmShort')}
            </button>
          </div>
        )}
      </div>
    );
  };

  // 🔄 Wiederverwendbares Retoure-Banner (Schnellansicht + volles Formular)
  const renderRetoureHinweis = () => {
    if (!mlResult?.retoure || retoureHintDismissed) return null;
    const a = aktiverAbsender;
    return (
      <div style={{
        marginTop: '12px', marginBottom: '12px', padding: '12px 14px', borderRadius: '10px',
        background: colors.darkMode ? '#3a2c10' : '#fff4e0', border: '1px solid #f5a623',
        color: colors.text,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '14px' }}>
          {t('quick.retoureTitle')}
          <button type="button" onClick={() => setRetoureHintDismissed(true)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: colors.textSecondary || '#999',
              fontSize: '18px', cursor: 'pointer', lineHeight: 1 }} aria-label={t('common.close')}>✕</button>
        </div>
        <div style={{ fontSize: '13px', marginTop: '4px' }}>
          {t('quick.retoureExplain')}
        </div>
        {absenderUebernommen ? (
          <div style={{ marginTop: '8px', fontSize: '13px', fontWeight: 600, color: '#2e7d32' }}>
            {t('quick.retoureApplied')}
          </div>
        ) : a && (a.name || a.street) ? (
          <button type="button" onClick={uebernehmeAbsenderAlsEmpfaenger}
            style={{ marginTop: '10px', width: '100%', padding: '10px', fontSize: '14px', fontWeight: 700,
              background: '#f5a623', color: '#3a2c10', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
            {t('quick.retoureApplyBtn')}{a.name ? ` (${a.name})` : ''}
          </button>
        ) : (
          <div style={{ marginTop: '8px', fontSize: '12px', color: colors.textSecondary || '#999' }}>
            {t('quick.retoureNoSender')}
          </div>
        )}
      </div>
    );
  };

  /**
   * 📐 Bereich korrigieren: User hat den Empfänger-Bereich manuell angepasst.
   * Sendet die korrigierten Koordinaten an /reanalyze, bekommt neues OCR-Ergebnis.
   */
  const handleRegionCorrection = async (correctedBox) => {
    if (!mlResult?.imageId) {
      console.warn('📐 Kein imageId für Re-Analyse vorhanden');
      setShowRegionCorrector(false);
      return;
    }
    
    setRegionCorrecting(true);
    
    try {
      const { reanalyzeRegion } = await import('./services/mlService');
      console.log(`📐 Re-Analyse mit korrigiertem Bereich: (${correctedBox.x},${correctedBox.y}) ${correctedBox.w}x${correctedBox.h}`);
      
      const result = await reanalyzeRegion(mlResult.imageId, correctedBox);
      
      console.log(`📐 Re-Analyse Ergebnis: Name="${result.ocrResult?.name}", Straße="${result.ocrResult?.street}", ${result.processingTime}ms`);
      
      // Felder mit neuem OCR-Ergebnis aktualisieren
      if (result.ocrResult) {
        const cleanedReName = bereinigeEmpfaengerName(result.ocrResult.name);
        if (cleanedReName) setName(cleanedReName);
        if (result.ocrResult.street) setStreetAndNumber(result.ocrResult.street);
        if (result.ocrResult.postal && result.ocrResult.city) {
          setPostalAndCity(`${result.ocrResult.postal} ${result.ocrResult.city}`);
        }
        if (result.ocrResult.tracking) {
          setTrackingNumber(result.ocrResult.tracking);
        }
        if (result.ocrResult.weight) {
          setGewicht(result.ocrResult.weight);
        }
      }
      
      // Thumbnail + mlResult aktualisieren
      if (result.thumbnail) {
        setStaticImage(`data:image/jpeg;base64,${result.thumbnail}`);
      }
      
      setMlResult(prev => {
        // Empfänger-Region mit korrigierter Box aktualisieren,
        // damit beim erneuten Öffnen des RegionSelectors die Position erhalten bleibt
        const regions = [...(prev?.regions || [])];
        const empIdx = regions.findIndex(r => r.type === 'empfaenger');
        if (empIdx >= 0 && result.correctedBox) {
          regions[empIdx] = { ...regions[empIdx], box: result.correctedBox };
        }
        
        return {
          ...prev,
          ...result,
          regions,
          feedbackSent: false,
          feedbackType: null,
          regionCorrected: true,
        };
      });
      
      setShowRegionCorrector(false);
      
    } catch (err) {
      console.error('📐 Re-Analyse fehlgeschlagen:', err);
      alert(`Re-Analyse fehlgeschlagen: ${err.message}`);
    } finally {
      setRegionCorrecting(false);
    }
  };

  // Aktualisiere die handleExtractText-Funktion
  const handleExtractText = async () => {
    console.log("OCR-Funktion aufgerufen");
    setOcrDiagnosticData(null);
    setRecognizedBlocks([]);
    
    if (!imageUrl) {
      alert(t('ocr.takePhotoFirst'));
      return;
    }
    
    try {
      setOcrLoading(true);
      setOcrError('');
      
      // Bild zuschneiden und fixieren
      const croppedImg = await getCroppedImg();
      if (!croppedImg) {
        setOcrError(t('ocr.cropError'));
        setOcrLoading(false);
        return;
      }
      
      // Speichere das Bild statisch für die Anzeige
      setStaticImage(croppedImg);
      
      // OCR-Erkennung durchführen
      const { data } = await Tesseract.recognize(
        croppedImg,
        'deu',
        { 
          logger: m => console.log('OCR Progress:', m),
          tessedit_pageseg_mode: '6',
          preserve_interword_spaces: '1',
          tessjs_create_hocr: '1',
          tessjs_create_tsv: '1'
        }
      );
      
      console.log("OCR abgeschlossen mit Konfidenz:", data.confidence);
      console.log("Erkannter Text:", data.text);
      
      // Textblöcke extrahieren mit allen drei Methoden
      const blocks = [];
      let blockId = 0;
      
      // 1. Methode: Paragraphen
      if (data.paragraphs && data.paragraphs.length > 0) {
        console.log("Paragraphen gefunden:", data.paragraphs.length);
        data.paragraphs.forEach(para => {
          const text = para.text.trim();
          if (text.length > 0) {
            blockId++;
            let type = detectTextType(text);
            
            blocks.push({
              id: `block-${blockId}`,
              text: text,
              bbox: {
                x0: Math.min(...para.words.map(w => w.bbox.x0)),
                y0: Math.min(...para.words.map(w => w.bbox.y0)),
                x1: Math.max(...para.words.map(w => w.bbox.x1)), 
                y1: Math.max(...para.words.map(w => w.bbox.y1))
              },
              type,
              confidence: para.confidence || data.confidence
            });
          }
        });
      }
      
      // 2. Methode: Zeilen
      if (blocks.length === 0 && data.lines && data.lines.length > 0) {
        console.log("Zeilen gefunden:", data.lines.length);
        data.lines.forEach(line => {
          const text = line.text.trim();
          if (text.length > 0) {
            blockId++;
            let type = detectTextType(text);
            
            blocks.push({
              id: `block-${blockId}`,
              text: text,
              bbox: line.bbox,
              type,
              confidence: line.confidence || data.confidence
            });
          }
        });
      }
      
      // 3. Methode: Fallback mit Rohtext
      if (blocks.length === 0 && data.text) {
        console.log("Keine strukturierten Daten gefunden, verwende Rohtext");
        const lines = data.text.split('\n').filter(line => line.trim().length > 0);
        
        lines.forEach((line, idx) => {
          const text = line.trim();
          if (text.length > 0) {
            blockId++;
            let type = detectTextType(text);
            
            blocks.push({
              id: `block-${blockId}`,
              text: text,
              bbox: {
                x0: 10,
                y0: 20 + idx * 30,
                x1: 100,
                y1: 50 + idx * 30
              },
              type,
              confidence: data.confidence
            });
          }
        });
      }
      
      console.log("Erkannte Textblöcke:", blocks);
      
      if (blocks.length === 0) {
        console.warn("Keine Textblöcke erkannt!");
        setOcrError(t('ocr.noBlocksFound'));
        setOcrLoading(false);
        return;
      }
      
      // Erkannte Blöcke speichern
      setRecognizedBlocks(blocks);
      
      // Automatisch die Formularfelder ausfüllen
      updateFormFromBlocks(blocks);
      
      // OCR-Status auf abgeschlossen setzen
      setOcrLoading(false);
      setOcrComplete(true);
      
    } catch (error) {
      console.error('OCR Fehler:', error);
      setOcrError(t('ui.modal.ocrError', { message: error.message }));
      setOcrLoading(false);
    }
  };
  
  // Hilfsfunktion zum Erkennen des Texttyps
  const detectTextType = (text) => {
    if (text.match(/^[A-Z][a-zäöüÄÖÜß]+\s+[A-Z][a-zäöüÄÖÜß]+/)) {
      return 'name'; // Name-Format
    } else if (text.match(/str|straße|strasse|gasse|weg|allee|platz|ring/i) && text.match(/\d+/)) {
      return 'street'; // Straßen-Format
    } else if (text.match(/\b\d{5}\b/) && text.length < 40) {
      return 'postal'; // PLZ Format
    } else if (text.match(/^[A-Z0-9]{9,20}$/)) {
      return 'tracking'; // Tracking-Nummer Format
    }
    return 'unassigned';
  };

  // Funktion zum Ändern des Block-Typs - verbesserte Version mit Mehrfach-Auswahl
  const updateBlockType = (blockId, newType) => {
    setRecognizedBlocks(blocks => {
      const block = blocks.find(b => b.id === blockId);
      
      // Wenn der Block bereits diesen Typ hat, setze zurück auf 'unassigned'
      // (abwählen durch erneutes Klicken)
      const updatedType = block && block.type === newType ? 'unassigned' : newType;
      
      const updatedBlocks = blocks.map(b => 
        b.id === blockId ? { ...b, type: updatedType } : b
      );
      
      // Formular mit den aktualisierten Blöcken aktualisieren
      updateFormFromBlocks(updatedBlocks);
      return updatedBlocks;
    });
  };

  // Hilfsfunktion zum Aktualisieren des Formulars aus den Blöcken
  const updateFormFromBlocks = (blocks) => {
    // Sortiere Blöcke nach Y-Koordinate für korrekte Reihenfolge
    const sortedBlocks = [...blocks].sort((a, b) => a.bbox.y0 - b.bbox.y0);
    
    // Sammle alle Blöcke nach Typ
    const nameBlocks = sortedBlocks.filter(block => block.type === 'name');
    const streetBlocks = sortedBlocks.filter(block => block.type === 'street');
    const postalBlocks = sortedBlocks.filter(block => block.type === 'postal');
    const trackingBlocks = sortedBlocks.filter(block => block.type === 'tracking');
    
    // Für Namen: Kombiniere mehrere Blöcke mit Leerzeichen dazwischen
    if (nameBlocks.length > 0) {
      const combinedName = bereinigeEmpfaengerName(nameBlocks.map(block => block.text).join(' '));
      if (combinedName) setName(combinedName);
    }
    
    // Andere Felder behalten wir bei einfacher Zuweisung
    if (streetBlocks.length > 0) setStreetAndNumber(streetBlocks[0].text);
    if (postalBlocks.length > 0) setPostalAndCity(postalBlocks[0].text);
    if (trackingBlocks.length > 0) setTrackingNumber(trackingBlocks[0].text);
  };

  // Füge diesen neuen State am Anfang der Komponente hinzu:
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  // 🔌 Wiesn2022@ DHL-Stick: Direkt beim Foto / Sendungsnummer-Erkennen an Schnelleinlagerung senden,
  // solange der Mitarbeiter noch vorne an der Theke steht und das Scanner-Piep hört!
  const [photoSchnellStatus, setPhotoSchnellStatus] = useState(null); // { state: 'sending'|'sent'|'review'|'error', code, msg }
  const [liveScannerSnap, setLiveScannerSnap] = useState(null);
  const photoSchnellSentRef = useRef('');

  useEffect(() => {
    if (!isWiesnAccount()) return undefined;
    const onSnap = (ev) => {
      if (ev?.detail?.preview) setLiveScannerSnap(ev.detail);
    };
    window.addEventListener('kiosk:dhl-screen-snap', onSnap);
    return () => window.removeEventListener('kiosk:dhl-screen-snap', onSnap);
  }, []);

  const triggerPhotoSchnelleinlagerung = async (codeOverride) => {
    const code = String(codeOverride || trackingNumber || '').trim();
    if (!code || code.length < 6) return;
    photoSchnellSentRef.current = code;
    setPhotoSchnellStatus({ state: 'sending', code });
    const res = await enqueueDhlStickSchnelleinlagerung({
      barcodes: [code],
      label: name ? `${name} (${code.slice(-6)})` : code,
    });
    if (res?.ok) {
      setPhotoSchnellStatus({
        state: res.pendingReview ? 'review' : 'sent',
        code,
        msg: res.pendingReview
          ? 'In Stick-Warteschlange (Prüf-Modus) angelegt'
          : (res.busy || (res.position && res.position > 1)
              ? `In Stick-Queue (#${res.position || 2})`
              : 'An DHL-Schnelleinlagerung gesendet — Piep gehört?'),
      });
    } else {
      setPhotoSchnellStatus({
        state: 'error',
        code,
        msg: res?.error || 'DHL-Stick nicht erreichbar',
      });
    }
  };

  useEffect(() => {
    if (paketId) return;
    if (!isWiesnAccount() || !isDhlStickEnabled() || !isPhotoSchnelleinlagerungEnabled()) return;
    // WICHTIG: Automatisch nur 1x pro Paket auslösen (sobald KI/Barcode die Nummer erkannt hat),
    // damit manuelles Editieren nicht halbfertige Nummern doppelt an den Scanner schickt!
    if (photoSchnellSentRef.current) return;
    const code = String(trackingNumber || '').trim();
    if (code.length < 10) return;
    if (!istDhlPaket({ trackingNumber: code, paketdienst: paketdienst || mlResult?.metadata?.paketdienst || '' })) return;
    triggerPhotoSchnelleinlagerung(code);
  }, [trackingNumber, paketdienst, paketId]);

  // Ändere den handleFormSubmit so ab:
  const padTo2Digits = (num) => String(num).padStart(2, '0');

  // ⚡ Serienmodus: beim Öffnen eines NEUEN Pakets die eingebaute Kamera selbst starten
  useEffect(() => {
    if (paketId || !isSeriesOn() || seriesKameraFehlgeschlagen()) return;
    seriesAutoCamRef.current = true;
    setShowInAppCam(true);
    // eslint-disable-next-line
  }, []);

  // ⚡ Serienmodus: eindeutiges Paket nach kurzer Frist automatisch speichern
  const autoSaveSubmitRef = useRef(null);
  const autoSaveOk = darfAutoSpeichern({
    seriesOn, paketId, quickConfirm, name, regalplatz, isSaving,
    retoure: !!mlResult?.retoure, istPlatzhalter: !!platzhalterCode(name),
    angefasst: serieAngefasstRef.current
      || (hasCustomFeature('batchBarcodes') && doppelabgleich?.status === 'unsicher')
      || integrationIntake.isBlockingAutoSave
      || Boolean(plzAbweichung && !plzCheckBestaetigt)
      || isPrivacyConsentEnabled(),
  });
  useEffect(() => {
    if (!autoSaveOk) { setAutoSaveIn(null); return undefined; }
    setAutoSaveIn(AUTO_SAVE_SEKUNDEN);
    const timer = [];
    for (let i = 1; i < AUTO_SAVE_SEKUNDEN; i++) timer.push(setTimeout(() => setAutoSaveIn(AUTO_SAVE_SEKUNDEN - i), i * 1000));
    timer.push(setTimeout(() => {
      if (serieAngefasstRef.current) return;
      setAutoSaveIn(null);
      if (autoSaveSubmitRef.current) autoSaveSubmitRef.current({ preventDefault() {} });
    }, AUTO_SAVE_SEKUNDEN * 1000));
    return () => timer.forEach(clearTimeout);
  }, [autoSaveOk, name, regalplatz]);

  const fuehreSpeichernAus = (overrides = {}) => {
    if (isSaving) return;
    // 🛡️ Eigene Datenschutz-/AGB-Bestätigung durch den Kunden vor dem Speichern
    if (!paketId && isPrivacyConsentEnabled() && !privacyConfirmed && !overrides._privacyBypass) {
      serieStoppen();
      setPendingSaveOverrides(overrides);
      setShowPrivacyModal(true);
      return;
    }

    let finalName = overrides.name !== undefined ? overrides.name : name;
    let finalStreet = overrides.streetAndNumber !== undefined ? overrides.streetAndNumber : streetAndNumber;
    const finalPostal = overrides.postalAndCity !== undefined ? overrides.postalAndCity : postalAndCity;
    const finalAbsender = overrides.absender !== undefined ? overrides.absender : erkannterAbsender;
    const finalRegalplatz = overrides.regalplatz !== undefined ? overrides.regalplatz : regalplatz;
    let finalNotizen = overrides.notizen !== undefined ? overrides.notizen : notizen;

    // 🧩 Modul-Hook vor dem Speichern (z.B. Grenzpaket-Kundenabgleich in Name, Straße & Notizen übernehmen)
    ({ finalName = finalName, finalStreet, finalNotizen } = integrationIntake.applyBeforeSave({ finalName, finalStreet, finalNotizen }));

    if (!paketId) {
      commitAutoNumberIfUsed(finalName);
    }

    setIsSaving(true);
    // 📋 Neues Paket → seine Barcodes in den Stapel:
    // - Wenn für wiesn2022@ bereits per Foto-Schnelleinlagerung gesendet wurde ("Piep gehört"), NICHT doppelt in den Barcode-Stapel legen!
    // - Wenn der Nutzer explizit "📋 Speichern + in Barcode-Stapel (kein Piep)" geklickt hat (overrides.forceAddToBatch), IMMER in den Barcode-Stapel legen!
    const skipBatchBecauseStickBeep = !overrides.forceAddToBatch && photoSchnellStatus?.state === 'sent';
    if (!paketId && !skipBatchBecauseStickBeep && (overrides.forceAddToBatch || ((batchCodesRef.current || serverCodesRef.current.length > 0 || trackingNumber) && hasCustomFeature('batchBarcodes')))) {
      const plz = finalPostal || mlResult?.ocrResult?.postal || '';
      const istManuell = Boolean(overrides.forceAddToBatch) || trackingManuellBestaetigt || (fallbackNachgefragt && String(trackingNumber || '').trim().length >= 4);
      const eintrag = { name: finalName, regalplatz: normalizeRegalplatz(finalRegalplatz), tracking: trackingNumber, plz, manuellBestaetigt: istManuell, force: Boolean(overrides.forceAddToBatch), imageId: mlResult?.imageId || null, dienst: paketdienst || '' };
      const p = Promise.resolve(batchCodesRef.current || lokalCodesRef.current || []);
      const vomServer = serverCodesRef.current;
      batchCodesRef.current = null;
      p.then((codes) => addToBatch({ ...eintrag, codes: [...(Array.isArray(codes) ? codes : []), ...vomServer, ...(trackingNumber ? [trackingNumber] : [])] }))
        .catch(() => addToBatch({ ...eintrag, codes: [...vomServer, ...(trackingNumber ? [trackingNumber] : [])] }));
    }
    console.log('💾 Speichervorgang gestartet');

    const now = new Date();
    const timeStr = `${padTo2Digits(now.getHours())}:${padTo2Digits(now.getMinutes())}:${padTo2Digits(now.getSeconds())}`;

    const paket = {
      id: paketId,
      name: finalName,
      streetAndNumber: finalStreet,
      postalAndCity: finalPostal,
      trackingNumber,
      regalplatz: normalizeRegalplatz(finalRegalplatz),
      paketAnzahl: Math.min(999, Math.max(1, parseInt(paketAnzahl, 10) || 1)), // 📦 Anzahl der Pakete (1-999)
      palettenTyp: String(palettenTyp || '').trim(), // 🪵 Paletten-/Gebinde-Typ (leer wenn keiner)
      // 💰🎨⚖️ EXTRAS (wie bei Quest VR)
      betrag: betrag ? parseFloat(betrag) : null, // Nachnahme/Geldbetrag
      paketFarbe: farbe || '', // Paketfarbe
      gewicht: gewicht || '', // Paketgewicht
      international: international || false, // 🌍 Internationales Paket
      // Korrekte Zeitformatierung
      datum: datum ? new Date(`${datum}T${timeStr}`).toISOString() : now.toISOString(),
      ausgegebenAm: ausgegebenAm ? new Date(`${ausgegebenAm}T${timeStr}`).toISOString() : '',
      zurueckgeschickt,
      imageUrl: mlResult?.imageId || '', // 📷 Foto-Verknüpfung (ML-imageId → image_url)
      paketdienst: paketdienst || '', // 🚚 fürs Etikett; der Server merkt ihn für den Nachdruck
      ...(finalNotizen ? { notizen: finalNotizen } : {}),
      ...(paketId && schadenAngefasstRef.current ? { schaden } : {}), // ⚠️ nur wenn bearbeitet
      // 📥 Absender IMMER mit abspeichern, sobald einer erkannt wurde (Sicherheitsnetz bei KI-Dreher)
      ...(finalAbsender ? { absender: finalAbsender } : {}),
      updatedAt: now.toISOString()  // Wichtig: Speichere immer den aktuellen Zeitstempel
    };

    // ⚠️ gespeicherte Schadensfotos merken (die übrigen hochgeladenen werden beim Schließen weggeräumt)
    if (paketId && schadenAngefasstRef.current) schadenGespeichertRef.current = (schaden && schaden.fotos) || [];
    const serieWeiter = !paketId && seriesOn && seriesAllowed();
    setShowSuccessOverlay(true);
    clearDraft();
    stopAutoSave();
    Promise.resolve(onSave(paket, false)).catch((e) => console.error('❌ Speichern fehlgeschlagen:', e));
    setTimeout(() => {
      setShowSuccessOverlay(false);
      onClose();
      setIsSaving(false); // 🛡️ Speicher-Lock freigeben
      // ⚡ Serienmodus: direkt das nächste Paket (Formular + Kamera öffnen sich wieder)
      if (serieWeiter) { try { window.dispatchEvent(new CustomEvent('kiosk:series-next')); } catch { /* */ } }
      console.log('✅ Speichervorgang abgeschlossen');
    }, 700);
  };

  const handleFormSubmitWithOptions = (overrides = {}) => {
    // 🛡️ VERHINDERE DOPPEL-SPEICHERN
    if (isSaving) {
      console.log('⚠️ Speichervorgang läuft bereits, ignoriere doppelten Submit');
      return;
    }

    // ⚠️ Pflichtfelder selbst prüfen (Form hat noValidate → keine Browser-Sprechblase).
    const missing = {};
    if (!name || !name.trim()) missing.name = true;
    if ((!regalplatz || !regalplatz.trim()) && !integrationIntake.allowEmptyShelf) missing.regalplatz = true;
    if (Object.keys(missing).length > 0) {
      setInvalidFields(missing);
      if (missing.regalplatz) setVisibleSections((prev) => ({ ...prev, shelfSection: true }));
      const firstId = missing.name ? 'nameField' : 'regalplatz';
      setTimeout(() => {
        const el = document.getElementById(firstId);
        if (el) { try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); } catch { /* */ } }
      }, 60);
      return; // NICHT speichern
    }
    setInvalidFields({});

    // 🔍 Fallback bei unklarer/internationaler Sendungsnummer: einmal gezielt nachfragen („Was ist die Sendungsnummer?"),
    // bevor ohne gültigen Stapel-Barcode gespeichert wird.
    if (!paketId && hasCustomFeature('batchBarcodes') && doppelabgleich?.status === 'unsicher' && !trackingManuellBestaetigt && !fallbackNachgefragt) {
      setFallbackNachgefragt(true);
      setShowTrackingFallback(true);
      setTimeout(() => {
        const el = fallbackTrackingInputRef.current;
        if (el) { try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); el.select?.(); } catch { /* */ } }
      }, 60);
      return;
    }

    // 📮 PLZ-Kreis-Check beim Speichern (aktiv ab 30 Paketen in der DB):
    // Wenn die erkannte Empfänger-PLZ vom üblichen PLZ-Kreis abweicht, gezielt nachfragen:
    // „Was ist Empfänger, was ist Absender?"
    if (!paketId && plzAbweichung && !plzCheckBestaetigt) {
      setShowPlzModal(true);
      return;
    }

    fuehreSpeichernAus(overrides);
  };

  const handleFormSubmit = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    handleFormSubmitWithOptions({});
  };

  const renderWiesnSchnellSaveBar = (isQuick = false) => {
    if (paketId || !isWiesnAccount() || !isDhlStickEnabled()) return null;
    const isDhl = istDhlPaket({ trackingNumber, paketdienst: paketdienst || mlResult?.metadata?.paketdienst || '' });
    if (!isDhl && !photoSchnellStatus) return null;
    const curCode = String(trackingNumber || '').trim();
    const codeWasEdited = Boolean(photoSchnellSentRef.current && curCode && photoSchnellSentRef.current !== curCode);
    return (
      <div
        data-testid="wiesn-schnelleinlagerung-bar"
        style={{
          marginTop: isQuick ? 12 : 8,
          padding: '10px 12px',
          borderRadius: 10,
          border: `1.5px solid ${
            liveScannerSnap?.popupSuspected
              ? '#ef4444'
              : photoSchnellStatus?.state === 'sent'
              ? '#10b981'
              : photoSchnellStatus?.state === 'error'
              ? '#ef4444'
              : '#eab308'
          }`,
          background: colors.darkMode ? '#18202c' : '#fffbeb',
          fontSize: 12.5,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: colors.text }}>
            {photoSchnellStatus?.state === 'sending' && '⏳ Sende Barcode an DHL-Schnelleinlagerung…'}
            {photoSchnellStatus?.state === 'sent' && `🔊 ${photoSchnellStatus.msg || 'An DHL-Stick gesendet — Piep gehört?'}`}
            {photoSchnellStatus?.state === 'review' && `🛡️ ${photoSchnellStatus.msg}`}
            {photoSchnellStatus?.state === 'error' && `⚠️ ${photoSchnellStatus.msg}`}
            {!photoSchnellStatus && '🔌 DHL-Paket erkannt — Schnelleinlagerung bereit'}
          </span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {codeWasEdited && (
              <button
                type="button"
                onClick={async () => {
                  await removeLastDhlStickItem();
                  await triggerPhotoSchnelleinlagerung(curCode);
                }}
                style={{
                  padding: '5px 8px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#eab308',
                  color: '#000',
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                🔄 Alte Nr. ersetzen ({curCode.slice(-6)})
              </button>
            )}
            {curCode && (
              <button
                type="button"
                onClick={() => triggerPhotoSchnelleinlagerung(curCode)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: `1px solid ${colors.border}`,
                  background: colors.darkMode ? '#2d333b' : '#fff',
                  color: colors.text,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                🔄 Nochmals an Stick
              </button>
            )}
            <button
              type="button"
              onClick={() => removeLastDhlStickItem()}
              title="Oberste Sendungsnummer in der offenen DHL-Liste wieder löschen (35/22 -> 32/80)"
              style={{
                padding: '4px 8px',
                borderRadius: 6,
                border: `1px solid ${colors.border}`,
                background: colors.darkMode ? '#2d333b' : '#fff',
                color: '#ef4444',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              🗑️ Nr. am Scanner löschen
            </button>
          </div>
        </div>

        {/* 👁️ Live-Kontrollbild vom Scanner-1-Display direkt in der Eingabemaske */}
        {liveScannerSnap?.preview && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              padding: '6px 8px',
              marginBottom: 8,
              borderRadius: 8,
              background: colors.darkMode ? '#0f172a' : '#f8fafc',
              border: `1px solid ${liveScannerSnap.color || colors.border}`,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 11.5, color: liveScannerSnap.popupSuspected ? '#ef4444' : colors.text }}>
                👁️ Scanner-1-Display: {liveScannerSnap.label || 'Live-Bild'}
              </div>
              {liveScannerSnap.popupSuspected && (
                <div style={{ marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={() => safeHomeDhlStick()}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 6,
                      border: 'none',
                      background: '#ef4444',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    🏠 Scanner auf Start zurücksetzen
                  </button>
                </div>
              )}
            </div>
            <img
              src={liveScannerSnap.preview}
              alt="DHL Scanner"
              style={{ width: 64, borderRadius: 6, border: '1.5px solid #ffcc00', flexShrink: 0 }}
            />
          </div>
        )}

        <button
          type="button"
          data-testid="save-and-batch-btn"
          disabled={isSaving}
          onClick={() => {
            serieStoppen();
            handleFormSubmitWithOptions({ forceAddToBatch: true });
          }}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1.5px solid #d97706',
            background: colors.darkMode ? 'rgba(217,119,6,0.2)' : '#fef3c7',
            color: colors.darkMode ? '#fde68a' : '#92400e',
            fontWeight: 800,
            fontSize: 13,
            cursor: isSaving ? 'default' : 'pointer',
          }}
        >
          📋 Speichern + in Barcode-Stapel legen (kein Piep gehört)
        </button>
      </div>
    );
  };

  autoSaveSubmitRef.current = handleFormSubmit; // ⚡ immer die aktuelle Version (frische States)

  // #41: handleSaveAndNew / handleSaveAndCopyPerson entfernt — waren toter Code (nie im JSX
  // verwendet) und hätten die Fach-Pflicht des Speicher-Guards umgangen.

  // Füge diese Funktion zum Hinzufügen von Favoriten hinzu
  const addFavorit = () => {
    if (!streetAndNumber) {
      alert(t('favorites.enterStreet'));
      return;
    }

    // Entferne die Hausnummer aus der Straße
    const street = streetAndNumber.replace(/\s*\d+[a-zA-Z]?\s*.*$/, '').trim();
    
    // postalAndCity ist optional
    const postalCityToSave = postalAndCity || '';

    // Verwende ein leeres Array falls favoriten null/undefined ist
    const currentFavoriten = Array.isArray(favoriten) ? favoriten : [];

    // Prüfe auf Duplikate im sicheren Array
    const isDuplicate = currentFavoriten.some(fav => {
      if (!postalCityToSave) {
        // Wenn keine PLZ angegeben, prüfe nur die Straße
        return fav.street === street;
      } else {
        // Wenn PLZ angegeben, prüfe beide Felder
        return fav.street === street && fav.postalAndCity === postalCityToSave;
      }
    });

    if (!isDuplicate) {
      const neueFavoriten = [...currentFavoriten, { street, postalAndCity: postalCityToSave }];
      
      // State aktualisieren
      setFavoriten(neueFavoriten);
      
      // In IndexedDB speichern
      set('favoriten', neueFavoriten)
        .then(() => console.log('Favorit wurde zur IndexedDB hinzugefügt'))
        .catch(err => console.error('Fehler beim Speichern des Favoriten:', err));
      
      alert(t('favorites.favoriteAdded'));
    } else {
      alert(t('favorites.favoriteExists'));
    }
  };

  const favInitRef = useRef(true); // ersten (Initial-Load-)Lauf überspringen, sonst leerer Anfangszustand → Server geleert
  // Favoriten mit Server synchronisieren
  useEffect(() => {
    const syncFavorites = async () => {
      try {
        const token = localStorage.getItem('kiosk_jwt_access');
        if (!token) return;
        
        const { api } = await import('./services/apiService');
        await api.put('/favoriten/sync', {
          favoriten: Array.isArray(favoriten) ? favoriten : []
        });
        console.log('Favoriten wurden mit Server synchronisiert');
      } catch (error) {
        console.error('Fehler beim Synchronisieren der Favoriten mit Server:', error);
      }
    };
    
    // Auch bei LEERER Liste synchronisieren — sonst propagiert das Löschen des letzten
    // Favoriten nie zum Server (er taucht beim nächsten Sync wieder auf). Nur den ersten
    // Lauf (Initial-Load) überspringen, damit der leere Anfangszustand nichts überschreibt.
    if (favInitRef.current) { favInitRef.current = false; }
    else if (Array.isArray(favoriten)) { syncFavorites(); }
  }, [favoriten]); // Wird ausgelöst, wenn sich die favoriten-Liste ändert
  
  // Add a new state for filtered favorites
  const [favoritenVorschlaege, setFavoritenVorschlaege] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Update the street input change handler to filter favorites
  const handleStreetInputChange = (e) => {
    const value = e.target.value;
    setStreetAndNumber(value);
    
    // Filter favorites based on input
    if (value.trim().length > 0) {
      const filtered = favoriten.filter(fav => 
        fav.street.toLowerCase().startsWith(value.toLowerCase())
      );
      setFavoritenVorschlaege(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFavoritenVorschlaege([]);
      setShowSuggestions(false);
    }
  };
  
  // Function to delete a favorite
  const deleteFavorit = (street, postalAndCity) => {
    const gefilterteFavoriten = favoriten.filter(
      (fav) => fav.street !== street || fav.postalAndCity !== postalAndCity
    );
    setFavoriten(gefilterteFavoriten);

    // Update local storage
    set('favoriten', gefilterteFavoriten)
      .catch(err => console.error('Fehler beim Löschen des Favoriten:', err));
  };
  
  // Function to select a favorite from suggestions
  const selectFavorit = (fav) => {
    // Füge ein Leerzeichen am Ende der Straße hinzu
    setStreetAndNumber(fav.street + ' ');
    setPostalAndCity(fav.postalAndCity || '');
    setShowSuggestions(false); // Hide suggestions after selection
    
    // Setze den Fokus zurück auf das Straßen-Eingabefeld
    setTimeout(() => {
      if (streetInputRef.current) {
        streetInputRef.current.focus();
        
        // Setze den Cursor nach dem Leerzeichen
        const length = fav.street.length + 1;
        streetInputRef.current.setSelectionRange(length, length);
      }
    }, 50);
  };

  // 🧩 Modul-Slot für Sonderkunden (z.B. Grenzpaket Kundenabgleich)
  const renderGrenzpaketCard = () => integrationIntake.renderIntakeSlot({ colors, lang });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }} onClick={(e) => {
      // Nur schließen wenn auf das Overlay geklickt wird, nicht auf den Modal-Inhalt
      if (e.target === e.currentTarget) {
        handleClose();
      }
    }}>
      <div style={{
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        backgroundColor: colors.darkMode ? '#2d333b' : '#ffffff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        overflow: 'auto'
      }}>
        {/* Puls-Highlight für leere Pflichtfelder (Name/Regalfach) */}
        <style>{`@keyframes ppFieldPulse{0%{box-shadow:0 0 0 0 rgba(229,72,77,.55)}70%{box-shadow:0 0 0 6px rgba(229,72,77,0)}100%{box-shadow:0 0 0 0 rgba(229,72,77,0)}}
        .field-invalid{animation:ppFieldPulse 1.1s ease-out 2}`}</style>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ margin: 0, whiteSpace: 'nowrap' }}>{paketId ? t('packages.editPackage') : t('packages.newPackage')}</h2>

            {/* 🤖 KI ist Standard — nur für Free-Nutzer der dezente Rest-Zähler des Tageslimits */}
            {!isPremium && !paketId && (
              <span
                title={t('quick.aiQuota', { remaining: kiRemaining, limit: FREE_KI_DAILY_LIMIT })}
                style={{
                  fontSize: 11,
                  color: kiRemaining <= 3 ? '#e53e3e' : (colors.textSecondary || '#888'),
                  border: `1px solid ${kiRemaining <= 3 ? '#e53e3e' : (colors.border || '#ddd')}`,
                  borderRadius: 10,
                  padding: '2px 8px',
                  whiteSpace: 'nowrap',
                }}
              >
                🤖 {kiRemaining}/{FREE_KI_DAILY_LIMIT}
              </span>
            )}

            {/* 📝 ENTWURF-BADGE - Zeigt an, dass ein Entwurf geladen ist */}
            {hasDraft && (
              <div style={{
                background: '#f6ad55',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span>📝</span>
                <span>{t('draft.draft')}</span>
              </div>
            )}
            
            {/* 📝 DRAFT SYSTEM - Auto-Save Indikator */}
            {isAutoSaving && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: '#f6ad55',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                <span>💾</span>
                <span>{t('quick.saving')}</span>
              </div>
            )}
          </div>
          
          <button 
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: colors.text
            }}
          >
            ×
          </button>
        </div>
        
        {/* 🖼️ Bearbeiten: das gespeicherte Paketfoto ganz oben — erst Bild, dann die Daten.
            Tippen = Vollbild mit Zoom. Nur wenn das Paket ein Foto hat und kein neues aufgenommen wurde. */}
        {paketId && !imageUrl && (() => {
          const p = (pakete || []).find((x) => x.id === paketId);
          if (!p || !(p.imageUrl || p.image_url)) return null;
          return (
            <div data-testid="edit-photo" style={{ marginBottom: '12px' }}>
              <PackageThumbnail packageId={paketId} />
            </div>
          );
        })()}

        {/* Hinweis für Doppelklick — nur im vollen Formular, nicht im kompakten Bestätigungs-Schritt */}
        {!quickConfirm && (
        <div style={{
          margin: '4px 0 8px', // Von 8px 0 16px auf 4px 0 8px reduziert
          padding: '6px 12px', // Von 8px 12px auf 6px 12px reduziert
          background: colors.darkMode ? '#2a3040' : '#f5f5f5',
          borderRadius: '6px',
          fontSize: '12px' // Von 14px auf 12px reduziert
        }}>
          <strong>{t('modal.tip')}</strong> {t('modal.doubleTapToHide')}
        </div>
        )}
        
        <form onSubmit={handleFormSubmit} noValidate onPointerDownCapture={serieStoppen} onKeyDownCapture={serieStoppen}
          style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {/* ============================================================
              ✅ BESTÄTIGUNGS-SCHRITT nach der KI-Erfassung
              Der Normalfall: die KI hat alles erkannt, es fehlt nur das Regalfach.
              Statt durch das ganze Formular zu scrollen, gibt es hier die erkannten
              Daten kompakt, ein großes Fach-Feld (fokussiert) und Speichern.
              Enter im Feld speichert — der Regal-QR-Scanner tippt „RG:K28" + Enter.
              ============================================================ */}
          {quickConfirm && (
            <div>
              {/* Erkannte Daten kompakt */}
              <div style={{
                background: colors.darkMode ? '#22272e' : '#f6f9fc',
                border: `1px solid ${colors.darkMode ? '#2a3a4a' : '#dbe6f1'}`,
                borderRadius: '10px',
                padding: '14px 16px',
                marginBottom: '14px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '6px',
                }}>
                  <span style={{
                    fontSize: '11px',
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    color: colors.textSecondary || '#888',
                  }}>
                    🤖 {t('quick.recognized')}
                  </span>
                  {/* ✏️ Bearbeiten: dezent im erkannten Container statt als Button unten,
                      den die geöffnete Tastatur überlagert. Führt ins volle Formular. */}
                  <button
                    type="button"
                    onClick={() => setQuickConfirm(false)}
                    aria-label={t('quick.editAll')}
                    title={t('quick.editAll')}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      lineHeight: 1,
                      padding: '2px 6px',
                      margin: '-2px -4px -2px 0',
                      color: colors.textSecondary || '#888',
                    }}
                  >
                    ✏️
                  </button>
                </div>
                <input
                  type="text"
                  data-testid="quick-name-input"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (invalidFields.name) setInvalidFields((f) => ({ ...f, name: false }));
                  }}
                  placeholder={texts[lang]?.name || 'Name'}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: colors.text,
                    lineHeight: 1.25,
                    background: colors.darkMode ? '#1b1f24' : '#ffffff',
                    border: `1px solid ${colors.darkMode ? '#374151' : '#cbd5e1'}`,
                    borderRadius: '8px',
                    padding: '6px 10px',
                    marginTop: '2px',
                  }}
                />
                {renderAddressBookChips()}
                {renderGrenzpaketCard()}
                {streetAndNumber && (
                  <div style={{ fontSize: '14px', color: colors.text, marginTop: '2px' }}>{streetAndNumber}</div>
                )}
                {postalAndCity && (
                  <div style={{ fontSize: '14px', color: colors.text }}>{postalAndCity}</div>
                )}
                {erkannterAbsender && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginTop: '4px' }}>
                    <div data-testid="quick-sender" style={{ fontSize: '13px', color: colors.textSecondary || '#888' }}>
                      {t('details.sender')}: {erkannterAbsender}
                    </div>
                    {aktiverAbsender && String(aktiverAbsender.name || '').trim() && (
                      <button
                        type="button"
                        data-testid="quick-swap-sender"
                        onClick={tauscheEmpfaengerUndAbsender}
                        title={t('plzCheck.swapTooltip', 'Empfänger und Absender tauschen')}
                        style={{
                          padding: '2px 8px',
                          borderRadius: '6px',
                          border: `1px solid ${colors.border || '#cbd5e1'}`,
                          background: colors.darkMode ? '#2d333b' : '#ffffff',
                          color: colors.text,
                          fontSize: '11px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        🔄 {t('plzCheck.swapShort', 'Tauschen')}
                      </button>
                    )}
                  </div>
                )}
                {trackingNumber && (
                  <div style={{ fontSize: '12px', color: colors.textSecondary || '#888', marginTop: '4px' }}>
                    {t('quick.trackingNo')}: {trackingNumber}
                  </div>
                )}
                {(paketdienst || Object.keys(getCarrierSortMap()).length > 0) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap', fontSize: 12, color: colors.textSecondary || '#888' }}>
                    <span>🚚</span>
                    {Object.keys(getCarrierSortMap()).length > 0 ? (
                      <select
                        data-testid="quick-carrier-select"
                        value={paketdienst || ''}
                        onChange={(e) => { serieStoppen(); setPaketdienst(e.target.value); }}
                        style={{
                          fontSize: 12, fontWeight: 600, padding: '2px 6px', borderRadius: 6,
                          border: `1px solid ${colors.border || '#cbd5e1'}`,
                          background: colors.darkMode ? '#1b1f24' : '#fff',
                          color: colors.text, cursor: 'pointer',
                        }}
                      >
                        <option value="">{paketdienst || t('ui.modal.carrierDefault')}</option>
                        {['Grenzpaket', 'PostNL', 'DHL', 'DHL Express', 'Hermes', 'DPD', 'GLS', 'UPS', 'Amazon', 'FedEx', 'Vinted', 'Mondial Relay', 'InPost'].map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ fontWeight: 600, color: colors.text }}>{paketdienst}</span>
                    )}
                    {Object.keys(getCarrierSortMap()).length > 0 && (
                      <span style={{ fontSize: 11, opacity: 0.85 }}>
                        ({getCarrierSortMode(paketdienst, trackingNumber, autoLetter) === 'hausnummer'
                          ? `🏠 ${t('ui.modal.sortHouseNo')}`
                          : getCarrierSortMode(paketdienst, trackingNumber, autoLetter) === 'kurznummer'
                            ? `🔢 ${t('ui.modal.sortShortNo')}`
                            : '🔤 A–Z'})
                      </span>
                    )}
                  </div>
                )}
                {(betrag || farbe || gewicht || international) && (
                  <div style={{ fontSize: '12px', color: colors.textSecondary || '#888', marginTop: '4px' }}>
                    {betrag ? `💰 ${betrag} € ` : ''}
                    {farbe ? `🎨 ${translatePackageColor(farbe, lang)} ` : ''}
                    {gewicht ? `⚖️ ${gewicht} ` : ''}
                    {international ? `🌍 ${t('packages.international')}` : ''}
                  </div>
                )}
              </div>

              {/* 🔄 Retoure-Hinweis (vor dem Speichern sichtbar — Abholer ist der Absender) */}
              {renderRetoureHinweis()}

              {/* 📮 PLZ-Kreis-Plausibilitätscheck (ab 30 Paketen in der DB) */}
              {renderPlzKreisHinweis()}

              {/* 📋 Barcode-Stapel & Doppelabgleich (Barcode ↔ Sendungsnummer) */}
              {renderBatchStatusBox()}
              {!batchFeatureHidden && hasCustomFeature('batchBarcodes') && doppelabgleich && (
                <div
                  data-testid="doppelabgleich-badge"
                  style={{
                    margin: '0 0 12px',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: 1.35,
                    border: `1.5px solid ${doppelabgleich.status === 'unsicher' ? '#e5484d' : doppelabgleich.status === 'pruefziffer_ok' ? '#f59e0b' : '#4caf50'}`,
                    background: doppelabgleich.status === 'unsicher'
                      ? (colors.darkMode ? '#3a1a1e' : '#ffebee')
                      : doppelabgleich.status === 'pruefziffer_ok'
                        ? (colors.darkMode ? '#3a2c10' : '#fff8e1')
                        : (colors.darkMode ? '#13301a' : '#e8f5e9'),
                    color: doppelabgleich.status === 'unsicher'
                      ? (colors.darkMode ? '#ff8a80' : '#c62828')
                      : doppelabgleich.status === 'pruefziffer_ok'
                        ? (colors.darkMode ? '#fcd34d' : '#92400e')
                        : (colors.darkMode ? '#81c784' : '#1b5e20'),
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                    <span>
                      {doppelabgleich.status === 'verifiziert' && (
                        <>{t('quick.dhlVerified')}</>
                      )}
                      {doppelabgleich.status === 'korrigiert' && (
                        <>{doppelabgleich.ocrTracking
                          ? t('quick.dhlCorrected', { old: doppelabgleich.ocrTracking, new: doppelabgleich.tracking })
                          : t('quick.dhlBarcodeOnly')}</>
                      )}
                      {doppelabgleich.status === 'pruefziffer_ok' && (
                        <>{t('quick.dhlNoBarcode', { ocr: doppelabgleich.tracking || trackingNumber })}</>
                      )}
                      {doppelabgleich.status === 'manuell_bestaetigt' && (
                        <>✓ {t('quick.trackingNo')}: <b>{doppelabgleich.tracking}</b></>
                      )}
                      {doppelabgleich.status === 'unsicher' && (
                        <>{t('quick.trackingPromptQuestion')}</>
                      )}
                    </span>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {doppelabgleich.status === 'pruefziffer_ok' && !showSideBarcodeScanner && (
                        <button
                          type="button"
                          onClick={() => { serieStoppen(); setShowSideBarcodeScanner(true); }}
                          style={{
                            flexShrink: 0, padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700,
                            border: '1px solid #d97706', background: colors.darkMode ? '#78350f' : '#fef3c7',
                            color: colors.darkMode ? '#fde68a' : '#92400e', cursor: 'pointer',
                          }}
                        >
                          📷 Barcode scannen
                        </button>
                      )}
                      {doppelabgleich.status !== 'unsicher' && !showTrackingFallback && (
                        <button
                          type="button"
                          onClick={() => setShowTrackingFallback(true)}
                          style={{
                            flexShrink: 0, padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700,
                            border: `1px solid ${colors.border || '#4caf50'}`, background: 'transparent', color: 'inherit', cursor: 'pointer',
                          }}
                        >
                          {t('quick.editTrackingBtn')}
                        </button>
                      )}
                    </div>
                  </div>

                  {(doppelabgleich.status === 'unsicher' || showTrackingFallback || showSideBarcodeScanner) && (
                    <div data-testid="tracking-fallback-box" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(128,128,128,0.25)' }}>
                      {!showSideBarcodeScanner ? (
                        <button
                          type="button"
                          data-testid="open-side-barcode-scanner"
                          onClick={() => { serieStoppen(); setShowSideBarcodeScanner(true); }}
                          style={{
                            width: '100%',
                            marginBottom: '8px',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 800,
                            border: 'none',
                            background: '#7b61ff',
                            color: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            boxShadow: '0 2px 8px rgba(123, 97, 255, 0.35)',
                          }}
                        >
                          <span>📷 {t('ui.modal.sideScanBtn')}</span>
                          <span style={{ fontSize: '11px', opacity: 0.92, fontWeight: 600 }}>
                            (🟥 {t('ui.modal.routingCode')} / 🟩 {t('ui.modal.trackingShort')})
                          </span>
                        </button>
                      ) : (
                        <SideBarcodeScanner
                          active={showSideBarcodeScanner}
                          plz={postalAndCity || mlResult?.ocrResult?.postal || ''}
                          paketdienst={paketdienst || mlResult?.metadata?.paketdienst || ''}
                          recipientName={name}
                          lang={lang}
                          onDetected={handleSideBarcodeDetected}
                          onClose={() => setShowSideBarcodeScanner(false)}
                        />
                      )}

                      <div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '6px', opacity: 0.92 }}>
                        {doppelabgleich.status === 'unsicher' && !trackingNumber
                          ? t('quick.trackingPromptSubNone')
                          : t('quick.trackingPromptSubCheck')}
                      </div>
                      {Array.isArray(doppelabgleich.kandidaten) && doppelabgleich.kandidaten.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                          {doppelabgleich.kandidaten.map((k) => (
                            <button
                              key={k.value}
                              type="button"
                              onClick={() => {
                                setTrackingNumber(k.value);
                                setTrackingManuellBestaetigt(true);
                                setShowTrackingFallback(false);
                              }}
                              style={{
                                padding: '5px 9px', borderRadius: '7px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                                border: '1px solid #4caf50', background: colors.darkMode ? '#1b3320' : '#fff',
                                color: colors.darkMode ? '#a5d6a7' : '#1b5e20', fontFamily: 'monospace',
                              }}
                            >
                              {k.quelle === 'barcode'
                                ? t('quick.trackingBarcodeLabel', { val: k.value })
                                : t('quick.trackingTextLabel', { val: k.value })} ✓
                            </button>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          ref={fallbackTrackingInputRef}
                          type="text"
                          data-testid="fallback-tracking-input"
                          value={trackingNumber}
                          onChange={(e) => {
                            setTrackingNumber(e.target.value.trim());
                            setTrackingManuellBestaetigt(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && String(trackingNumber || '').trim().length >= 4) {
                              e.preventDefault();
                              setTrackingManuellBestaetigt(true);
                              setShowTrackingFallback(false);
                            }
                          }}
                          placeholder={t('quick.trackingPromptPh')}
                          style={{
                            flex: 1, padding: '8px 10px', borderRadius: '7px', fontSize: '14px', fontWeight: 700,
                            fontFamily: 'monospace', border: `1.5px solid ${colors.border || '#ccc'}`,
                            background: colors.darkMode ? '#1c2128' : '#fff', color: colors.text,
                          }}
                        />
                        <button
                          type="button"
                          data-testid="fallback-tracking-confirm"
                          disabled={String(trackingNumber || '').trim().length < 4}
                          onClick={() => {
                            if (String(trackingNumber || '').trim().length >= 4) {
                              setTrackingManuellBestaetigt(true);
                              setShowTrackingFallback(false);
                            }
                          }}
                          style={{
                            padding: '8px 12px', borderRadius: '7px', fontSize: '13px', fontWeight: 700,
                            border: 'none', background: '#2e7d32', color: '#fff',
                            cursor: String(trackingNumber || '').trim().length >= 4 ? 'pointer' : 'default',
                            opacity: String(trackingNumber || '').trim().length >= 4 ? 1 : 0.5,
                          }}
                        >
                          {t('quick.trackingApplyBtn')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 📦 Vinted ohne Namen → Liste importieren (nur Sonderfunktion) */}
              {hasCustomFeature('vintedImport') && platzhalterCode(name) && (
                <button type="button" data-testid="vinted-import-link"
                  onClick={() => window.dispatchEvent(new CustomEvent('kiosk:open-vinted-import'))}
                  style={{ width: '100%', margin: '0 0 12px', padding: '9px 12px', borderRadius: '8px', cursor: 'pointer',
                    border: '1px dashed #3b9fd1', background: 'transparent', color: colors.text, fontSize: '13px', textAlign: 'left' }}>
                  📦 {t('vinted.importLink')}
                </button>
              )}

              {/* Regalfach — das einzige, was noch fehlt. Rechts der QR-Scanner-Schalter
                  (gilt nur für DIESES Gerät, direkt hier an-/abschaltbar). */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                <label htmlFor="regalplatz" style={{ fontWeight: 'bold', color: colors.text }}>
                  🗄️ {texts[lang]?.shelf || t('packages.shelfLocation')}
                </label>
                <span style={{ flex: 1 }} />
                {seriesAllowed() && !paketId && (
                <button
                  type="button"
                  data-testid="series-toggle-quick"
                  onClick={() => { const n = !seriesOn; setSeriesOn(n); setSeriesOnState(n); setAutoSaveIn(null); }}
                  title={seriesOn ? t('series.toggleOn') : t('series.toggleOff')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '7px',
                    padding: '6px 10px', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '12px', fontWeight: 700,
                    border: `1.5px solid ${seriesOn ? '#4caf50' : colors.border}`,
                    background: seriesOn ? (colors.darkMode ? '#13301a' : '#e8f5e9') : (colors.darkMode ? '#22272e' : '#f3f4f6'),
                    color: seriesOn ? (colors.darkMode ? '#66bb6a' : '#2e7d32') : colors.text,
                  }}
                >
                  <span>⚡ {t('series.short')}</span>
                  <span style={{
                    position: 'relative', display: 'inline-block', width: 28, height: 16, borderRadius: 999,
                    background: seriesOn ? '#4caf50' : (colors.darkMode ? '#555' : '#bbb'),
                    transition: 'background 0.15s',
                  }}>
                    <span style={{
                      position: 'absolute', top: 2, left: seriesOn ? 14 : 2, width: 12, height: 12,
                      borderRadius: '50%', background: '#fff', transition: 'left 0.15s',
                    }} />
                  </span>
                </button>
                )}
                {hasCustomFeature('nachnameSortierung') && (
                <button
                  type="button"
                  onClick={() => {
                    const n = !autoLetter; setAutoLetter(n); setAutoRegalLetter(n);
                    if (!n && regalplatz === autoRegalRef.current) { setRegalplatz(''); }
                    autoRegalRef.current = '';
                  }}
                  title={t('quick.autoLetterTitle')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 10px', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '12px', fontWeight: 600,
                    border: `1px solid ${autoLetter ? '#4caf50' : colors.border}`,
                    background: autoLetter ? (colors.darkMode ? '#13301a' : '#e8f5e9') : 'transparent',
                    color: autoLetter ? '#2e7d32' : (colors.textSecondary || '#8a8a8a'),
                  }}
                >
                  🔤 A–Z {autoLetter ? t('quick.on') : t('quick.off')}
                </button>
                )}
                <button
                  type="button"
                  onClick={() => { const n = !qrOn; setQrOn(n); setShelfQrScanEnabled(n); }}
                  title={qrOn ? t('quick.qrTitleOn') : t('quick.qrTitleOff')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 10px', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '12px', fontWeight: 600,
                    border: `1px solid ${qrOn ? '#4caf50' : colors.border}`,
                    background: qrOn ? (colors.darkMode ? '#13301a' : '#e8f5e9') : 'transparent',
                    color: qrOn ? '#2e7d32' : (colors.textSecondary || '#8a8a8a'),
                  }}
                >
                  📷 QR / Barcode {qrOn ? t('quick.on') : t('quick.off')}
                </button>
              </div>
              {istGrenzpaket ? (
                renderGrenzpaketRegalSchnelltipp()
              ) : (
                <>
                  <input
                    id="regalplatz"
                    ref={quickShelfRef}
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    value={regalplatz}
                    onChange={(e) => {
                      setRegalplatz(e.target.value.toUpperCase().replace(/\s+/g, '').replace(/^RG:?/i, ''));
                      if (invalidFields.regalplatz) setInvalidFields((f) => ({ ...f, regalplatz: false }));
                    }}
                    onBlur={() => {
                      if (regalplatz) setRegalplatz((cur) => normalizeRegalplatz(cur));
                    }}
                    placeholder={t('quick.shelfPlaceholder')}
                    className={invalidFields.regalplatz ? 'field-invalid' : undefined}
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '26px',
                      fontWeight: 700,
                      textAlign: 'center',
                      letterSpacing: '.06em',
                      borderRadius: '10px',
                      border: `2px solid ${invalidFields.regalplatz ? '#e5484d' : colors.border}`,
                      background: colors.inputBg || (colors.darkMode ? '#1c2128' : '#fff'),
                      color: colors.text,
                      boxSizing: 'border-box',
                    }}
                  />
                  {invalidFields.regalplatz && (
                    <div style={{ color: '#e5484d', fontSize: 12, marginTop: 6, textAlign: 'center' }}>
                      {t('quick.shelfMissing')}
                    </div>
                  )}
                  {renderRegalSchnellwahl(quickShelfRef)}
                  <div style={{
                    fontSize: '11px',
                    color: colors.textSecondary || '#888',
                    marginTop: '6px',
                    textAlign: 'center',
                  }}>
                    {t('quick.shelfHint')}
                  </div>
                </>
              )}

              {/* ⚠️ Diese Sendungsnummer ist schon erfasst → nur Hinweis, Speichern bleibt möglich */}
              {gleicheSendung && (
                <div data-testid="hint-duplicate" style={{
                  marginTop: '10px', padding: '10px 12px', borderRadius: '10px',
                  background: colors.darkMode ? '#3a2c10' : '#fff4e0',
                  border: '1px solid #f5a623', color: colors.text, fontSize: '13px',
                }}>
                  ⚠️ {t('quick.duplicateTracking', { slot: gleicheSendung.regalplatz || '–', name: gleicheSendung.name || '' })}
                </div>
              )}

              {/* 📦 Empfänger hat schon Pakete im Regal → dazulegen */}
              {gleicherEmpfaenger && (
                <div
                  onClick={() => {
                    if (gleicherEmpfaenger.regalplatz) {
                      serieStoppen();
                      setRegalplatz(normalizeRegalplatz(gleicherEmpfaenger.regalplatz));
                      if (invalidFields.regalplatz) setInvalidFields((f) => ({ ...f, regalplatz: false }));
                    }
                  }}
                  style={{
                    marginTop: '10px', padding: '10px 12px', borderRadius: '10px',
                    background: colors.darkMode ? '#12303a' : '#e6f4fb',
                    border: `1px ${gleicherEmpfaenger.sicher ? 'solid' : 'dashed'} #3b9fd1`, color: colors.text, fontSize: '13px',
                    cursor: gleicherEmpfaenger.regalplatz ? 'pointer' : 'default',
                  }}
                >
                  📦 {t(gleicherEmpfaenger.sicher ? 'quick.sameRecipient' : 'quick.maybeSameRecipient', {
                    name: gleicherEmpfaenger.name || name,
                    count: gleicherEmpfaenger.anzahl,
                    slot: gleicherEmpfaenger.regalplatz,
                  })}
                </div>
              )}

              {/* ⚠️ Verwechslungs-Warnung: gleiche Hausnummer im Nachbarfach */}
              {regalKonflikt && (
                <div style={{
                  marginTop: '10px', padding: '10px 12px', borderRadius: '10px',
                  background: colors.darkMode ? '#3a2c10' : '#fff4e0',
                  border: '1px solid #f5a623', color: colors.text, fontSize: '13px',
                }}>
                  <div>
                    {t('quick.houseConflict', { hn: regalKonflikt.hausnummer, slot: regalKonflikt.regalplatz, name: regalKonflikt.name })}
                  </div>
                  <div style={{ fontSize: '12px', marginTop: '3px', opacity: 0.85 }}>
                    {t('quick.houseConflictSub')}
                  </div>
                </div>
              )}

              {/* ⚡ Serienmodus: automatisches Speichern mit Abbruch-Möglichkeit */}
              {autoSaveIn !== null && (
                <div data-testid="series-countdown" style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                  background: colors.darkMode ? '#1e3a24' : '#e8f5e9', border: '1px solid #4caf50', color: colors.text }}>
                  <span>⚡ {t('series.savingIn', { seconds: autoSaveIn })}</span>
                  <button type="button" data-testid="series-cancel" onClick={serieStoppen}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.text, fontSize: '13px', cursor: 'pointer' }}>
                    {t('series.cancel')}
                  </button>
                </div>
              )}

              {/* 📦🪵 Bei aktiviertem Palettenvertrieb (oder mehr als 1 Paket) auch direkt im Schnell-Dialog wählbar */}
              {(showPaletteBar || palettenTyp || paketAnzahl > 1) && (
                <div style={{ marginTop: '12px' }}>
                  {renderPaketAnzahlLeiste()}
                  {renderPalettenAuswahl()}
                </div>
              )}

              {/* Speichern */}
              <button
                type="submit"
                disabled={isSaving}
                style={{
                  width: '100%',
                  marginTop: '14px',
                  padding: '16px',
                  fontSize: '17px',
                  fontWeight: 700,
                  background: colors.button,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: isSaving ? 'default' : 'pointer',
                  opacity: isSaving ? 0.6 : 1,
                }}
              >
                {isSaving
                  ? `💾 ${t('quick.saving')}`
                  : (photoSchnellStatus?.state === 'sent'
                      ? '✅ Speichern (Piep gehört — weiter)'
                      : `✓ ${t('common.save')}`)}
              </button>
              {renderWiesnSchnellSaveBar(true)}

            </div>
          )}

          {/* 📷 Regal-QR- & Barcode-Dauerscanner — Opt-in pro Gerät, nur beim NEUANLEGEN.
              Läuft ab Start der KI-Analyse, liest QR-Codes UND 1D-/2D-Barcodes (Regalfach + Sendungsnummer).
              Gespeichert wird weiterhin per Knopf. */}
          <ShelfQrScanner
            active={qrOn && !paketId && (mlAnalyzing || ocrComplete || !quickConfirm) && !showInAppCam && !showSideBarcodeScanner}
            formats={SUCH_SCAN_FORMATE}
            onCode={(text, alle, _foto, _vollFoto, alleObjekte) => {
              handleShelfOrPackageCodeScan(text, alle, alleObjekte);
            }}
          />

          {!quickConfirm && (<>
          {/* Foto-Bereich - immer sichtbar */}
          <div style={{ marginBottom: '16px' }}>
            {/* Foto-Button zum Aufnehmen des Bildes */}
            <label>
              <input
                ref={paketPhotoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                id="paket-photo-input" />
              <button
                type="button"
                data-testid="camera-open"
                onClick={handleCameraActivation}
                style={{
                  background: colors.button,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  margin: '8px 0',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24
                }}
                title={t('camera.openCamera')}
              >
                <FaCamera />
              </button>
              <span style={{ marginLeft: 12 }}>{t('ocr.extractText')}</span>
            </label>
            {Capacitor.isNativePlatform() && (
              <button type="button"
                onClick={() => { const n = !inAppCam; setInAppCameraEnabled(n); setInAppCam(n); setCamPermHint(false); }}
                style={{
                  display: 'block', marginTop: 2, padding: '4px 0', background: 'none', border: 'none',
                  color: colors.darkMode ? '#9ab' : '#667', fontSize: 12, textDecoration: 'underline', cursor: 'pointer',
                }}>
                {inAppCam ? t('camera.inAppOn') : t('camera.inAppOff')}
              </button>
            )}
            {seriesAllowed() && !paketId && (
              <button
                type="button"
                data-testid="series-toggle"
                onClick={() => { const n = !seriesOn; setSeriesOn(n); setSeriesOnState(n); setAutoSaveIn(null); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  width: '100%', marginTop: 10, padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                  border: `1.5px solid ${seriesOn ? '#4caf50' : (colors.border || (colors.darkMode ? '#3a414c' : '#d1d5db'))}`,
                  background: seriesOn ? (colors.darkMode ? '#13301a' : '#e8f5e9') : (colors.darkMode ? '#1f242c' : '#f3f4f6'),
                  color: colors.text, fontSize: 13, fontWeight: 600, textAlign: 'left',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>⚡</span>
                  <span>{seriesOn ? t('series.toggleOn') : t('series.toggleOff')}</span>
                </span>
                <span style={{
                  position: 'relative', display: 'inline-block', flexShrink: 0, width: 42, height: 24, borderRadius: 999,
                  background: seriesOn ? '#4caf50' : (colors.darkMode ? '#555' : '#bbb'),
                  transition: 'background 0.15s',
                }}>
                  <span style={{
                    position: 'absolute', top: 3, left: seriesOn ? 21 : 3, width: 18, height: 18,
                    borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.15s',
                  }} />
                </span>
              </button>
            )}
            {showInAppCam && (
              <InAppCamera
                t={t}
                hint={seriesOn && !paketId ? t('series.cameraHint') : undefined}
                cancelLabel={seriesOn && !paketId && seriesAutoCamRef.current ? t('series.stop') : undefined}
                onError={seriesOn && !paketId ? () => {
                  // Eingebaute Kamera geht auf diesem Gerät nicht → für diese Sitzung normaler Weg
                  merkeKameraFehler(); seriesAutoCamRef.current = false;
                  setShowInAppCam(false); setCamPermHint(true);
                } : undefined}
                onClose={() => {
                  setShowInAppCam(false);
                  // Serie: „Beenden" in der automatisch geöffneten Kamera schließt auch das leere Formular
                  if (seriesAutoCamRef.current) { seriesAutoCamRef.current = false; handleClose(); }
                }}
                onCapture={(file) => { seriesAutoCamRef.current = false; setShowInAppCam(false); handlePhotoUpload({ target: { files: [file] } }); }}
              />
            )}
            {camPermHint && (
              <div style={{
                marginTop: 6, padding: '10px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.45,
                background: colors.darkMode ? '#3a2a12' : '#fff4e5', color: colors.darkMode ? '#ffd8a8' : '#8a4b00',
                border: '1px solid #f0a040',
              }}>
                {t('camera.permissionHint')}
              </div>
            )}
            {renderBatchStatusBox()}
            {!batchFeatureHidden && hasCustomFeature('batchBarcodes') && doppelabgleich && (
              <div
                data-testid="doppelabgleich-badge"
                style={{
                  margin: '6px 0 10px',
                  padding: '9px 12px',
                  borderRadius: '9px',
                  fontSize: '13px',
                  fontWeight: 600,
                  lineHeight: 1.35,
                  border: `1.5px solid ${doppelabgleich.status === 'unsicher' ? '#e5484d' : '#4caf50'}`,
                  background: doppelabgleich.status === 'unsicher'
                    ? (colors.darkMode ? '#3a1a1e' : '#ffebee')
                    : (colors.darkMode ? '#13301a' : '#e8f5e9'),
                  color: doppelabgleich.status === 'unsicher'
                    ? (colors.darkMode ? '#ff8a80' : '#c62828')
                    : (colors.darkMode ? '#81c784' : '#1b5e20'),
                }}
              >
                {doppelabgleich.status === 'verifiziert' && (
                  <>{t('quick.dhlVerified')}</>
                )}
                {doppelabgleich.status === 'korrigiert' && (
                  <>{doppelabgleich.ocrTracking
                    ? t('quick.dhlCorrected', { old: doppelabgleich.ocrTracking, new: doppelabgleich.tracking })
                    : t('quick.dhlBarcodeOnly')}</>
                )}
                {doppelabgleich.status === 'pruefziffer_ok' && (
                  <>{t('quick.dhlNoBarcode', { ocr: doppelabgleich.tracking || trackingNumber })}</>
                )}
                {doppelabgleich.status === 'manuell_bestaetigt' && (
                  <>✓ {t('quick.trackingNo')}: <b>{doppelabgleich.tracking}</b></>
                )}
                {doppelabgleich.status === 'unsicher' && (
                  <div>
                    <div style={{ marginBottom: '8px' }}>{t('quick.trackingPromptQuestion')}</div>
                    {!showSideBarcodeScanner ? (
                      <button
                        type="button"
                        onClick={() => { serieStoppen(); setShowSideBarcodeScanner(true); }}
                        style={{
                          width: '100%',
                          marginBottom: '8px',
                          padding: '9px 12px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: 800,
                          border: 'none',
                          background: '#7b61ff',
                          color: '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <span>📷 {t('ui.modal.sideScanBtn')}</span>
                        <span style={{ fontSize: '11px', opacity: 0.92, fontWeight: 600 }}>
                          (🟥 {t('ui.modal.routingCode')} / 🟩 {t('ui.modal.trackingShort')})
                        </span>
                      </button>
                    ) : (
                      <SideBarcodeScanner
                        active={showSideBarcodeScanner}
                        plz={postalAndCity || mlResult?.ocrResult?.postal || ''}
                        paketdienst={paketdienst || mlResult?.metadata?.paketdienst || ''}
                        recipientName={name}
                        lang={lang}
                        onDetected={handleSideBarcodeDetected}
                        onClose={() => setShowSideBarcodeScanner(false)}
                      />
                    )}
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                      <input
                        type="text"
                        value={trackingNumber}
                        onChange={(e) => { setTrackingNumber(e.target.value.trim()); setTrackingManuellBestaetigt(false); }}
                        placeholder={t('quick.trackingPromptPh')}
                        style={{
                          flex: 1, padding: '7px 10px', borderRadius: '7px', fontSize: '14px', fontWeight: 700,
                          fontFamily: 'monospace', border: `1.5px solid ${colors.border || '#ccc'}`,
                          background: colors.darkMode ? '#1c2128' : '#fff', color: colors.text,
                        }}
                      />
                      <button
                        type="button"
                        disabled={String(trackingNumber || '').trim().length < 4}
                        onClick={() => { if (String(trackingNumber || '').trim().length >= 4) setTrackingManuellBestaetigt(true); }}
                        style={{
                          padding: '7px 12px', borderRadius: '7px', fontSize: '13px', fontWeight: 700,
                          border: 'none', background: '#2e7d32', color: '#fff',
                          cursor: String(trackingNumber || '').trim().length >= 4 ? 'pointer' : 'default',
                          opacity: String(trackingNumber || '').trim().length >= 4 ? 1 : 0.5,
                        }}
                      >
                        {t('quick.trackingApplyBtn')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 🤖 ML-Analyse Ladeanimation */}
            {mlAnalyzing && (
              <div style={{
                margin: '16px 0',
                padding: '32px 16px',
                background: colors.darkMode ? '#1a1d23' : '#f0f4ff',
                border: `2px dashed ${colors.button}`,
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: 48,
                  marginBottom: 12,
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}>🤖</div>
                <div style={{
                  fontSize: 16,
                  fontWeight: 'bold',
                  color: colors.text,
                  marginBottom: 8
                }}>{t('ocr.analyzingImage')}</div>
                <div style={{
                  fontSize: 13,
                  color: colors.textSecondary || '#888'
                }}>{t('ocr.aiRecognizesAuto')}</div>
                {mlWarte && (
                  <div data-testid="ml-server-warte" style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: colors.darkMode ? '#ffcc80' : '#b26a00' }}>⏳ {t('ui.modal.serverWait')}</div>
                    <div style={{ marginTop: 8, height: 6, borderRadius: 3, background: colors.darkMode ? '#333' : '#e0e0e0', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, Math.max(8, Math.round(((Date.now() - mlWarte.ab) / mlWarte.maxMs) * 100)))}%`, height: '100%', background: '#f59e0b', transition: 'width 0.5s linear' }} />
                    </div>
                  </div>
                )}
                <style>{`@keyframes pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.15); opacity: 0.7; } }`}</style>
              </div>
            )}

            {/* Bilderbereich: Entweder Cropper oder OCR-Analyse */}
            {imageUrl && !ocrComplete && !mlAnalyzing && (
              // Bild-Cropper vor der OCR-Analyse (Fallback wenn ML nicht verfügbar)
              <>
                <div style={{ position: 'relative', height: '210px', marginBottom: '16px' }}>
                  <Cropper
                    image={imageUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={4 / 2}
                    rotation={rotation}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete} 
                  />
                  
                  <button
                    type="button"
                    onClick={() => setImageUrl(null)}
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background: 'rgba(255,255,255,0.7)',
                      border: 'none',
                      borderRadius: '50%',
                      width: 30,
                      height: 30,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      cursor: 'pointer',
                      zIndex: 100
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <button
                  type="button"
                  onClick={handleExtractText}
                  style={{
                    margin: '8px 0 16px',
                    padding: '12px 0',
                    width: '100%',
                    fontSize: 16,
                    fontWeight: 'bold',
                    borderRadius: 8,
                    background: colors.button,
                    color: '#fff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  disabled={ocrLoading}
                >
                  {ocrLoading ? t('ocr.ocrRunning') : t('ocr.extractText')}
                </button>
              </>
            )}
            
            {/* OCR-Analyseergebnis direkt im Formular - kompaktere Version */}
            {ocrComplete && staticImage && (
              <div style={{ 
                marginTop: '12px',
                marginBottom: '12px',
                background: colors.darkMode ? '#1e2127' : '#f5f5f5',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                padding: '12px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: window.innerWidth > 500 ? 'row' : 'column',
                  gap: '12px',
                  position: 'relative'
                }}>
                  {/* Fixiertes Bild links */}
                  <div style={{ 
                    width: window.innerWidth > 500 ? '40%' : '100%',
                    position: 'relative'
                  }}>
                    <img 
                      src={staticImage} 
                      alt="OCR Scan"
                      style={{
                        width: '100%',
                        display: 'block',
                        borderRadius: '4px',
                        border: `1px solid ${colors.border}`
                      }}
                    />
                    
                    {/* Textblock-Markierungen im Bild */}
                    {recognizedBlocks.map(block => (
                      <div
                        key={`marker-${block.id}`}
                        style={{
                          position: 'absolute',
                          left: `${(block.bbox.x0 / croppedAreaPixels.width) * 100}%`,
                          top: `${(block.bbox.y0 / croppedAreaPixels.height) * 100}%`,
                          width: `${((block.bbox.x1 - block.bbox.x0) / croppedAreaPixels.width) * 100}%`,
                          height: `${((block.bbox.y1 - block.bbox.y0) / croppedAreaPixels.height) * 100}%`,
                          background: (() => {
                            switch(block.type) {
                              case 'name': return 'rgba(0, 128, 255, 0.3)';
                              case 'street': return 'rgba(76, 175, 80, 0.3)';
                              case 'postal': return 'rgba(255, 193, 7, 0.3)';
                              case 'tracking': return 'rgba(233, 30, 99, 0.3)';
                              default: return 'rgba(158, 158, 158, 0.3)';
                            }
                          })(),
                          border: '1px solid',
                          borderColor: (() => {
                            switch(block.type) {
                              case 'name': return '#0080ff';
                              case 'street': return '#4CAF50';
                              case 'postal': return '#FFC107';
                              case 'tracking': return '#E91E63';
                              default: return '#9e9e9e';
                            }
                          })(),
                          borderRadius: '2px',
                          zIndex: 2,
                          pointerEvents: 'none'
                        }}
                      />
                    ))}
                    
                    {/* Reset-Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setOcrComplete(false);
                        setStaticImage(null);
                        setRecognizedBlocks([]);
                        setMlResult(null);
                      }}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: colors.darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        color: colors.darkMode ? 'white' : 'black',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        zIndex: 3
                      }}
                    >
                      {t('common.reset')}
                    </button>
                    
                    {/* 🤖 ML-Konfidenz-Badge auf dem Bild */}
                    {mlResult && (
                      <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '8px',
                        background: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        borderRadius: '4px',
                        padding: '3px 8px',
                        fontSize: '11px',
                        zIndex: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {t('quick.aiOverlayBtn')}
                        {mlResult.confidence && (
                          <span style={{
                            background: mlResult.confidence > 0.7 ? '#4CAF50' : mlResult.confidence > 0.4 ? '#FFC107' : '#f44336',
                            borderRadius: '3px',
                            padding: '1px 5px',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}>
                            {Math.round(mlResult.confidence * 100)}%
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Kompakte Textblöcke rechts */}
                  <div style={{ 
                    width: window.innerWidth > 500 ? '60%' : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    {recognizedBlocks.map(block => (
                      <div
                        key={block.id}
                        style={{
                          position: 'relative',
                          display: 'inline-block', // Wichtig: inline-block statt flex für angepasste Breite
                          background: colors.darkMode ? '#282c34' : 'white',
                          borderRadius: '4px',
                          border: `1px solid ${colors.border}`,
                          marginBottom: '6px',
                          fontSize: '13px',
                          padding: '8px 12px',
                          maxWidth: '100%',
                          overflow: 'hidden'
                        }}
                        title={block.text}
                      >
                        {/* Der erkannte Text */}
                        <span
                          style={{
                            display: 'block',
                            color: colors.darkMode ? '#eee' : '#333',
                            textAlign: 'left',
                            position: 'relative',
                            zIndex: 1,
                            wordBreak: 'break-word'
                          }}
                        >
                          {block.text}
                        </span>

                        {/* Überlappende Typ-Auswahl als transparentes Overlay */}
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '6px',
                            zIndex: 2,
                            opacity: 0.6,
                            pointerEvents: 'auto'
                          }}
                        >
                          {['name', 'street', 'postal', 'tracking'].map(type => {
                            const color =
                              type === 'name'
                                ? '#0080ff'
                                : type === 'street'
                                ? '#4CAF50'
                                : type === 'postal'
                                ? '#FFC107'
                                : '#E91E63';
                            const bgColor =
                              type === 'name'
                                ? 'rgba(0,128,255,0.18)'
                                : type === 'street'
                                ? 'rgba(76,175,80,0.18)'
                                : type === 'postal'
                                ? 'rgba(255,193,7,0.18)'
                                : 'rgba(233,30,99,0.18)';
                            const typeLabel = type === 'name'
                              ? t('packages.name')
                              : type === 'street'
                              ? t('packages.street')
                              : type === 'postal'
                              ? t('details.city')
                              : t('packages.trackingNumber');
                            return (
                              <div
                                key={type}
                                onClick={() => updateBlockType(block.id, type)}
                                style={{
                                  width: 18,
                                  height: 18,
                                  border: `2px solid ${color}`,
                                  background: block.type === type ? bgColor : 'rgba(255,255,255,0.2)',
                                  borderRadius: 3,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center'
                                }}
                                title={block.type === type
                                  ? t('ocr.unmark', { type: typeLabel })
                                  : t('ocr.markAs', { type: typeLabel })}
                              >
                                {block.type === type && (
                                  <div
                                    style={{
                                      width: 10,
                                      height: 10,
                                      background: color,
                                      borderRadius: 2
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Legende unten */}
                <div style={{
                  marginTop: '10px',
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: '8px',
                  background: colors.darkMode ? '#222730' : '#f0f0f0',
                  padding: '6px',
                  borderRadius: '4px',
                  fontSize: '11px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '12px', height: '12px', background: 'rgba(0, 128, 255, 0.3)', border: '1px solid #0080ff', marginRight: '4px' }}></div>
                    {t('packages.name')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '12px', height: '12px', background: 'rgba(76, 175, 80, 0.3)', border: '1px solid #4CAF50', marginRight: '4px' }}></div>
                    {t('packages.street')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '12px', height: '12px', background: 'rgba(255, 193, 7, 0.3)', border: '1px solid #FFC107', marginRight: '4px' }}></div>
                    {t('details.city')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '12px', height: '12px', background: 'rgba(233, 30, 99, 0.3)', border: '1px solid #E91E63', marginRight: '4px' }}></div>
                    {t('packages.trackingNumber')}
                  </div>
                </div>
                
                {/* Fehlermeldung */}
                {ocrError && (
                  <div style={{
                    marginTop: '8px',
                    padding: '6px 10px',
                    color: 'white',
                    background: '#f44336',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    {ocrError}
                  </div>
                )}
                
              </div>
            )}
            
            {/* Fehlermeldung wenn OCR nicht erfolgreich */}
            {ocrError && !ocrComplete && (
              <div style={{
                marginTop: '12px',
                padding: '8px 12px',
                color: 'white',
                background: '#f44336',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                {ocrError}
              </div>
            )}
          </div>
          
          {/* 🔄 Retoure-Hinweis auch im vollen Formular (oberhalb Name) */}
          {renderRetoureHinweis()}

          {/* 📮 PLZ-Kreis-Plausibilitätscheck auch im vollen Formular */}
          {renderPlzKreisHinweis()}

          {/* Name - immer sichtbar */}
          <div style={{ marginBottom: '8px' }}>
            <input
              ref={idPhotoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleIdPhotoUpload}
              style={{ display: 'none' }}
            />
            {!paketId && isIdFirstEnabled() && !name && (
              <button
                type="button"
                data-testid="id-first-banner-btn"
                disabled={idScanning}
                onClick={() => idPhotoInputRef.current?.click()}
                style={{
                  width: '100%',
                  marginBottom: '8px',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1.5px solid #3b82f6',
                  background: colors.darkMode ? '#1e3a8a' : '#eff6ff',
                  color: colors.darkMode ? '#bfdbfe' : '#1d4ed8',
                  fontWeight: 800,
                  fontSize: '13.5px',
                  cursor: idScanning ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <span>{idScanning ? `⏳ ${t('ui.modal.idReading')}` : `🪪 ${t('ui.modal.idStep1')}`}</span>
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <label htmlFor="nameField" style={{ fontWeight: 'bold' }}>
                {texts[lang]?.name || t('packages.name')}
              </label>
              {!paketId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {(isAutoNumberEnabled() || !name) && (
                    <button
                      type="button"
                      data-testid="auto-number-btn"
                      onClick={() => {
                        const nextNum = getNextAutoNumber(pakete);
                        setName(nextNum);
                        if (invalidFields.name) setInvalidFields((f) => ({ ...f, name: false }));
                      }}
                      title={t('ui.modal.autoNumberTitle')}
                      style={{
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontSize: '11.5px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        border: `1px solid ${colors.border || '#cbd5e1'}`,
                        background: colors.darkMode ? '#22272e' : '#f3f4f6',
                        color: colors.text,
                      }}
                    >
                      🔢 {getNextAutoNumber(pakete)}
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="scan-id-btn"
                    disabled={idScanning}
                    onClick={() => idPhotoInputRef.current?.click()}
                    title={t('ui.modal.idScanTitle')}
                    style={{
                      padding: '3px 8px',
                      borderRadius: '6px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      cursor: idScanning ? 'wait' : 'pointer',
                      border: `1px solid ${colors.border || '#cbd5e1'}`,
                      background: colors.darkMode ? '#22272e' : '#f3f4f6',
                      color: colors.text,
                    }}
                  >
                    {idScanning ? `⏳ ${t('ui.modal.idShort')}…` : `🪪 ${t('ui.modal.idShort')}`}
                  </button>
                </div>
              )}
            </div>
            <input
              id="nameField"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameUnsicher(false); if (invalidFields.name) setInvalidFields((f) => ({ ...f, name: false })); }}
              placeholder={texts[lang]?.name || t('packages.name')}
              className={invalidFields.name ? 'field-invalid' : undefined}
              style={{
                width: '100%',
                padding: '8px 12px', // Von 10px auf 8px reduziert
                borderRadius: '6px',
                border: `1px solid ${invalidFields.name ? '#e5484d' : colors.border}`,
                background: colors.inputBg,
                color: colors.text
              }}
            />
            {renderAddressBookChips()}
            {renderGrenzpaketCard()}
            {invalidFields.name && (
              <div style={{ color: '#e5484d', fontSize: '12px', marginTop: '3px' }}>{t('modal.enterName')}</div>
            )}
            {nameUnsicher && !!name && !paketId && (
              <div data-testid="name-unsicher" style={{ color: '#e65100', fontSize: '12px', marginTop: '3px', lineHeight: 1.4 }}>
                ⚠️ {t('ui.modal.nameUnsure')}
              </div>
            )}
          </div>
          
          {/* SICHTBARE FELDER - NUR wenn sie eingeblendet sind */}
          {/* Straße - nur wenn eingeblendet */}
          {visibleSections.addressSection && (
            <>
              <SectionHeader 
                title={texts[lang]?.street || t('packages.street')} 
                section="addressSection"
                isVisible={true}
              />
              <div style={{ marginBottom: '8px', position: 'relative' }}>
                <input
                  ref={streetInputRef}
                  type="text"
                  value={streetAndNumber}
                  onChange={handleStreetInputChange}
                  onFocus={() => {
                    if (streetAndNumber.trim().length > 0) {
                      const filtered = favoriten.filter(fav => 
                        fav.street.toLowerCase().startsWith(streetAndNumber.toLowerCase())
                      );
                      setFavoritenVorschlaege(filtered);
                      setShowSuggestions(filtered.length > 0);
                    }
                  }}
                  placeholder={texts[lang].street}
                  style={{
                    width: '100%',
                    padding: '8px 10px', // Von 10px 12px auf 8px 10px reduziert
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    background: colors.inputBg,
                    color: colors.text
                  }}
                />
                
                {/* Autosuggest dropdown - unverändert */}
                {showSuggestions && favoritenVorschlaege.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    background: colors.inputBg,
                    border: `1px solid ${colors.border}`,
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    zIndex: 100
                  }}>
                    {favoritenVorschlaege.map((fav, idx) => (
                      <div 
                        key={idx}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: idx < favoritenVorschlaege.length - 1 ? 
                            `1px solid ${colors.border}` : 'none',
                          background: colors.darkMode ? '#2d333b' : '#ffffff'
                        }}
                      >
                        <div onClick={() => selectFavorit(fav)} style={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <div>{fav.street}</div>
                          {fav.postalAndCity && (
                            <div style={{ fontSize: '12px', color: colors.darkMode ? '#aaa' : '#666' }}>
                              {fav.postalAndCity}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteFavorit(fav.street, fav.postalAndCity);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: colors.darkMode ? '#ff7875' : '#f44336',
                            padding: '5px',
                            marginLeft: '8px'
                          }}
                          title={t('favorites.deleteFavorite')}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* HIER NEU: Favoriten-Button direkt nach dem Straßenfeld platzieren */}
              <button
                type="button"
                onClick={addFavorit}
                style={{
                  padding: '6px 12px', // kleiner als vorher
                  background: 'transparent',
                  color: colors.button,
                  border: `1px solid ${colors.button}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  width: 'auto', // nicht mehr 100%
                  marginBottom: '1px',
                  marginLeft: 'auto' // nach rechts ausrichten
                }}
              >
                <span style={{ marginRight: '6px', fontSize: '14px' }}>+</span>
                {t('favorites.addToFavorites')}
              </button>
            </>
          )}
          
          {/* Favoriten - nur wenn eingeblendet */}
          {visibleSections.favoritesList && (
            <>
              <SectionHeader 
                title={t('favorites.favoritesList')} 
                section="favoritesList"
                isVisible={true}
              />
              <div style={{ marginBottom: '16px' }}>
                {/* Favoriten-Liste */}
                <div style={{ 
  maxHeight: '150px', 
  overflowY: 'auto', 
  border: `1px solid ${colors.border}`,
  borderRadius: '6px',
  background: colors.inputBg,
  marginBottom: '8px'
}}>
  {favoriten.length === 0 ? (
    <div style={{ padding: '12px', color: colors.darkMode ? '#aaa' : '#666' }}>
      {t('modal.noFavoritesSaved')}
    </div>
  ) : (
    favoriten.map((fav, idx) => (
      <div 
        key={idx}
        style={{
          padding: '8px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: idx < favoriten.length - 1 ? `1px solid ${colors.border}` : 'none',
          background: streetAndNumber === fav.street ? 
            (colors.darkMode ? '#4a5568' : '#e6f7ff') : 'transparent'
        }}
      >
        <div 
          onClick={() => selectFavorit(fav)}
          style={{ 
            flexGrow: 1, 
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div>{fav.street}</div>
          {fav.postalAndCity && (
            <div style={{ fontSize: '12px', color: colors.darkMode ? '#aaa' : '#666' }}>
              {fav.postalAndCity}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => deleteFavorit(fav.street, fav.postalAndCity)}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: colors.darkMode ? '#ff7875' : '#f44336',
            padding: '5px'
          }}
          title={t('favorites.deleteFavorite')}
        >
          <FaTrash />
        </button>
      </div>
    ))
  )}
</div>
              </div>
            </>
          )}
          
          {/* PLZ und Ort - nur wenn eingeblendet */}
          {visibleSections.postalSection && (
            <>
              <SectionHeader 
                title={texts[lang]?.postalAndCity || t('packages.postalAndCity')} 
                section="postalSection"
                isVisible={true}
              />
              <div style={{ marginBottom: '8px' }}>
                <input
                  type="text"
                  value={postalAndCity}
                  onChange={(e) => setPostalAndCity(e.target.value)}
                  placeholder={texts[lang]?.postalAndCity || t('packages.postalAndCity')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    background: colors.inputBg,
                    color: colors.text
                  }}
                />
              </div>
            </>
          )}
          
          {/* Regalplatz - nur wenn eingeblendet */}
          {visibleSections.shelfSection && (
            <>
              <SectionHeader 
                title={texts[lang]?.shelf || "Regalplatz"} 
                section="shelfSection"
                isVisible={true}
              />
              <div style={{ marginBottom: '8px' }}>
                {istGrenzpaket && !paketId ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {renderGrenzpaketRegalSchnelltipp()}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => { const n = !qrOn; setQrOn(n); setShelfQrScanEnabled(n); }}
                        title={qrOn ? t('quick.qrTitleOn') : t('quick.qrTitleOff')}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '11.5px',
                          fontWeight: 600,
                          border: `1px solid ${qrOn ? '#4caf50' : colors.border}`,
                          background: qrOn ? (colors.darkMode ? '#13301a' : '#e8f5e9') : 'transparent',
                          color: qrOn ? '#2e7d32' : (colors.textSecondary || '#8a8a8a'),
                        }}
                      >
                        📷 QR / Barcode {qrOn ? t('quick.on') : t('quick.off')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input
                        id="regalplatz"
                        type="text"
                        value={regalplatz}
                        onChange={(e) => { setRegalplatz(e.target.value.toUpperCase().replace(/\s+/g, '').replace(/^(?:RG:?|;+)/i, '')); if (invalidFields.regalplatz) setInvalidFields((f) => ({ ...f, regalplatz: false })); }}
                        onBlur={() => { if (regalplatz) setRegalplatz((cur) => normalizeRegalplatz(cur)); }}
                        placeholder={texts[lang].shelf}
                        className={invalidFields.regalplatz ? 'field-invalid' : undefined}
                        style={{
                          flex: 1,
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: `1px solid ${invalidFields.regalplatz ? '#e5484d' : colors.border}`,
                          background: colors.inputBg,
                          color: colors.text
                        }}
                      />
                      {!paketId && (
                        <button
                          type="button"
                          onClick={() => { const n = !qrOn; setQrOn(n); setShelfQrScanEnabled(n); }}
                          title={qrOn ? t('quick.qrTitleOn') : t('quick.qrTitleOff')}
                          style={{
                            flexShrink: 0,
                            padding: '9px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600,
                            border: `1px solid ${qrOn ? '#4caf50' : colors.border}`,
                            background: qrOn ? (colors.darkMode ? '#13301a' : '#e8f5e9') : 'transparent',
                            color: qrOn ? '#2e7d32' : (colors.textSecondary || '#8a8a8a'),
                          }}
                        >
                          📷 QR / Barcode {qrOn ? t('quick.on') : t('quick.off')}
                        </button>
                      )}
                    </div>
                    {invalidFields.regalplatz && (
                      <div style={{ color: '#e5484d', fontSize: '12px', marginTop: '3px' }}>
                        {t('quick.shelfMissing')}
                      </div>
                    )}
                    {renderRegalSchnellwahl()}
                    {istGrenzpaket && (
                      <div style={{ marginTop: '6px' }}>
                        {renderGrenzpaketRegalSchnelltipp()}
                      </div>
                    )}
                  </>
                )}
                {/* ⚠️ Verwechslungs-Warnung: gleiche Hausnummer im Nachbarfach */}
                {regalKonflikt && (
                  <div style={{
                    marginTop: '6px', padding: '8px 10px', borderRadius: '8px',
                    background: colors.darkMode ? '#3a2c10' : '#fff4e0',
                    border: '1px solid #f5a623', color: colors.text, fontSize: '12.5px',
                  }}>
                    {t('quick.houseConflictShort', { hn: regalKonflikt.hausnummer, slot: regalKonflikt.regalplatz, name: regalKonflikt.name })}
                  </div>
                )}
              </div>
            </>
          )}
          
          {/* Tracking-Nummer - nur wenn eingeblendet */}
          {visibleSections.trackingSection && (
            <>
              <SectionHeader 
                title={texts[lang]?.trackingNumber || (lang === 'de' ? 'Tracking-Nummer' : t('packages.trackingNumber'))} 
                section="trackingSection"
                isVisible={true}
              />
              <div style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder={texts[lang]?.trackingNumber || (lang === 'de' ? 'Tracking-Nummer' : t('packages.trackingNumber'))}
                    style={{
                      flex: 1,
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`,
                      background: colors.inputBg,
                      color: colors.text
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => { serieStoppen(); setShowSideBarcodeScanner((s) => !s); }}
                    title="Barcode / QR-Code scannen"
                    style={{
                      flexShrink: 0,
                      padding: '9px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12.5px',
                      fontWeight: 700,
                      border: `1px solid ${showSideBarcodeScanner ? '#7b61ff' : colors.border}`,
                      background: showSideBarcodeScanner ? '#7b61ff' : (colors.darkMode ? '#1f242c' : '#f3f4f6'),
                      color: showSideBarcodeScanner ? '#fff' : colors.text,
                    }}
                  >
                    📷 Scan
                  </button>
                </div>
                {showSideBarcodeScanner && (
                  <div style={{ marginTop: '8px' }}>
                    <SideBarcodeScanner
                      active={showSideBarcodeScanner}
                      plz={postalAndCity || mlResult?.ocrResult?.postal || ''}
                      paketdienst={paketdienst || mlResult?.metadata?.paketdienst || ''}
                      recipientName={name}
                      lang={lang}
                      onDetected={handleSideBarcodeDetected}
                      onClose={() => setShowSideBarcodeScanner(false)}
                    />
                  </div>
                )}
              </div>
              {gleicheSendung && (
                <div data-testid="hint-duplicate-form" style={{
                  margin: '0 0 8px', padding: '8px 10px', borderRadius: '8px',
                  background: colors.darkMode ? '#3a2c10' : '#fff4e0',
                  border: '1px solid #f5a623', color: colors.text, fontSize: '13px',
                }}>
                  ⚠️ {t('quick.duplicateTracking', { slot: gleicheSendung.regalplatz || '–', name: gleicheSendung.name || '' })}
                </div>
              )}
            </>
          )}

          {/* 💰🎨⚖️ EXTRAS: Betrag, Farbe, Gewicht - nur wenn eingeblendet */}
          {visibleSections.extrasSection && (
            <>
              <SectionHeader 
                title={t('packages.extras') || "Extras"} 
                section="extrasSection"
                isVisible={true}
              />
              
              {/* 💰 Geldbetrag bei Abholung */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>
                  💰 {t('packages.paymentOnPickup') || 'Bei Abholung zu zahlen in €'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={betrag}
                  onChange={(e) => setBetrag(e.target.value)}
                  placeholder="12,50"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${betrag ? '#f44336' : colors.border}`,
                    background: betrag ? (colors.darkMode ? '#3d2020' : '#fff0f0') : colors.inputBg,
                    color: colors.text
                  }}
                />
                {betrag && (
                  <div style={{ fontSize: '11px', color: '#f44336', marginTop: '2px' }}>
                    ⚠️ {t('packages.amountWarning') || 'Bei Ausgabe muss dieser Betrag kassiert werden!'}
                  </div>
                )}
              </div>
              
              {/* 🎨 Paketfarbe - als Farbkugeln */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '13px' }}>
                  🎨 {t('packages.color') || 'Paketfarbe'}
                </label>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px',
                  padding: '8px',
                  background: colors.darkMode ? '#1e2127' : '#f5f5f5',
                  borderRadius: '8px'
                }}>
                  {/* Keine Farbe / Löschen */}
                  <button
                    type="button"
                    onClick={() => setFarbe('')}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: !farbe ? '3px solid #3b82f6' : '2px solid #ccc',
                      background: colors.darkMode ? '#2d333b' : '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      boxShadow: !farbe ? '0 0 8px rgba(59, 130, 246, 0.5)' : 'none'
                    }}
                    title={t('common.none') || 'Keine'}
                  >
                    ✕
                  </button>
                  
                  {/* Farbkugeln */}
                  {[
                    { name: 'braun', color: '#8B4513' },
                    { name: 'weiß', color: '#FFFFFF' },
                    { name: 'schwarz', color: '#000000' },
                    { name: 'grau', color: '#808080' },
                    { name: 'blau', color: '#0000FF' },
                    { name: 'gelb', color: '#FFFF00' },
                    { name: 'grün', color: '#00AA00' },
                    { name: 'rot', color: '#FF0000' }
                  ].map(({ name, color }) => {
                    const displayColor = translatePackageColor(name, lang);
                    return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setFarbe(name)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: farbe === name 
                          ? '3px solid #3b82f6' 
                          : color === '#FFFFFF' 
                            ? '2px solid #ccc' 
                            : '2px solid transparent',
                        background: color,
                        cursor: 'pointer',
                        boxShadow: farbe === name 
                          ? '0 0 8px rgba(59, 130, 246, 0.5)' 
                          : '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'transform 0.1s, box-shadow 0.1s'
                      }}
                      title={displayColor.charAt(0).toUpperCase() + displayColor.slice(1)}
                    />
                    );
                  })}
                </div>
                {farbe && (() => {
                  const disp = translatePackageColor(farbe, lang);
                  return (
                    <div style={{ fontSize: '12px', color: colors.text, marginTop: '4px', opacity: 0.7 }}>
                      {t('packages.colorSelected', { color: disp.charAt(0).toUpperCase() + disp.slice(1) })}
                    </div>
                  );
                })()}
              </div>
              
              {/* ⚖️ Paketgewicht */}
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '2px', fontWeight: 'bold', fontSize: '13px' }}>
                  ⚖️ {t('packages.weight') || 'Gewicht'}
                </label>
                <input
                  type="text"
                  value={gewicht}
                  onChange={(e) => setGewicht(e.target.value)}
                  placeholder={t('packages.weightPlaceholder') || 'z.B. 2,5 kg, schwer'}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    background: colors.inputBg,
                    color: colors.text
                  }}
                />
              </div>
              
              {/* 🌍 INTERNATIONAL: Checkbox für internationale Pakete (14 Werktage Lagerfrist) */}
              <div style={{ marginBottom: '8px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${international ? '#1976d2' : colors.border}`,
                  background: international 
                    ? (colors.darkMode ? '#1a2332' : '#e3f2fd') 
                    : colors.inputBg,
                  color: colors.text,
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="checkbox"
                    checked={international}
                    onChange={(e) => setInternational(e.target.checked)}
                    style={{ cursor: 'pointer', width: '16px', height: '16px', flexShrink: 0 }}
                  />
                  <span>🌍 {t('packages.international') || 'Internationales Paket'}</span>
                  <span style={{ 
                    fontSize: '11px', 
                    color: international ? '#1976d2' : (colors.darkMode ? '#999' : '#888'), 
                    fontWeight: 'normal',
                    marginLeft: 'auto'
                  }}>
                    = {t('packages.internationalHint') || 'Lagerdauer 14 Werktage'}
                  </span>
                </label>
              </div>
            </>
          )}

          {/* Datumsinformationen - nur wenn eingeblendet */}
          {visibleSections.dateSection && (
            <>
              <SectionHeader 
                title={texts[lang]?.dateInfo || t('packages.dateAndStatus')} 
                section="dateSection"
                isVisible={true}
              />
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>
                  {texts[lang]?.stored || t('packages.storedOn')}
                </label>
                <input
                  type="date"
                  value={datum}
                  onChange={(e) => setDatum(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    background: colors.inputBg,
                    color: colors.text
                  }}
                />
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '2px', fontWeight: 'bold' }}>
                  {texts[lang]?.issued || t('packages.issuedOn')}
                </label>
                <input
                  type="date"
                  value={ausgegebenAm}
                  onChange={(e) => setAusgegebenAm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    background: colors.inputBg,
                    color: colors.text
                  }}
                />
                {/* ↩️ Ausgabe in einem Tipp zurücknehmen — statt das Datum manuell zu löschen */}
                {ausgegebenAm && (
                  <button
                    type="button"
                    onClick={() => setAusgegebenAm('')}
                    style={{
                      marginTop: '8px',
                      padding: '10px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      background: 'transparent',
                      color: '#b26a00',
                      border: '1px solid #b26a00',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    ↩️ {t('packages.undoIssue')}
                  </button>
                )}
              </div>

              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="zurueckgeschickt"
                  checked={zurueckgeschickt}
                  onChange={(e) => setZurueckgeschickt(e.target.checked)}
                  style={{ 
                    marginRight: '8px', 
                    width: '20px', 
                    height: '20px'
                  }}
                />
                <label htmlFor="zurueckgeschickt" style={{ cursor: 'pointer' }}>
                  {t('packages.returned')}
                </label>
              </div>
            </>
          )}
          
          {/* 🤖 KI-Erkennung — ganz unten, standardmäßig zugeklappt.
              Die Erkennung stimmt fast immer; wer sie prüfen oder den Bereich
              korrigieren will, klappt sie hier auf. */}
          {mlResult && (
            <div style={{ marginTop: '4px' }}>
              <button
                type="button"
                onClick={() => setShowKiOverlay(v => !v)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '12px',
                  textAlign: 'left',
                  borderRadius: '6px',
                  border: `1px solid ${colors.darkMode ? '#2a3a4a' : '#c4d8ef'}`,
                  background: colors.darkMode ? '#161a20' : '#eef4fb',
                  color: colors.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span>{showKiOverlay ? '▾' : '▸'}</span>
                {t('quick.aiOverlayBtn')}
                {mlResult.regions?.length > 0 && (
                  <span style={{ opacity: 0.7 }}>({mlResult.regions.length} {t('quick.aiOverlayAreas')})</span>
                )}
                {mlResult.regionCorrected && (
                  <span style={{ color: '#4CAF50', marginLeft: 'auto' }}>{t('quick.aiOverlayCorrected')}</span>
                )}
              </button>
              {showKiOverlay && (
                <div style={{ marginTop: '4px' }}>
                  {/* 🤖 KI-Erkennungen: Annotiertes Bild mit allen Regionen */}
                  {mlResult?.regions?.length > 0 && imageUrl && mlResult.imageSize && (
                    <div style={{
                      marginTop: '12px',
                      background: colors.darkMode ? '#161a20' : '#eef4fb',
                      border: `1px solid ${colors.darkMode ? '#2a3a4a' : '#c4d8ef'}`,
                      borderRadius: '8px',
                      padding: '10px',
                    }}>
                      <div style={{
                        fontSize: 12,
                        fontWeight: 'bold',
                        color: colors.text,
                        marginBottom: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}>
                        {t('quick.aiOverlayBtn')} ({mlResult.regions.length} {t('quick.aiOverlayAreas')})
                      </div>
                      <div style={{ position: 'relative', width: '100%', lineHeight: 0 }}>
                        <img 
                          src={imageUrl} 
                          alt={t('quick.aiOverlayBtn')}
                          style={{
                            width: '100%',
                            display: 'block',
                            borderRadius: 4,
                          }}
                        />
                        {mlResult.regions.map((region, i) => {
                          const regionColors = {
                            empfaenger: '#00aaff',
                            absender: '#ff9800',
                            barcode: '#9c27b0',
                            tracking: '#e91e63',
                            label: '#607d8b',
                            gewicht: '#4caf50',
                          };
                          const regionLabels = {
                            empfaenger: t('quick.aiOverlayRecipient'),
                            absender: t('quick.aiOverlaySender'),
                            barcode: 'Barcode',
                            tracking: 'Tracking',
                            label: 'Label',
                            gewicht: t('quick.aiOverlayWeight'),
                          };
                          const rc = regionColors[region.type] || '#999';
                          const rl = regionLabels[region.type] || region.type;
                          return (
                            <div key={`ann-${i}`} style={{
                              position: 'absolute',
                              left: `${(region.box.x / mlResult.imageSize.width) * 100}%`,
                              top: `${(region.box.y / mlResult.imageSize.height) * 100}%`,
                              width: `${(region.box.w / mlResult.imageSize.width) * 100}%`,
                              height: `${(region.box.h / mlResult.imageSize.height) * 100}%`,
                              border: `2px solid ${rc}`,
                              background: `${rc}18`,
                              borderRadius: 2,
                              pointerEvents: 'none',
                            }}>
                              <span style={{
                                position: 'absolute',
                                top: -15,
                                left: 0,
                                fontSize: 8,
                                color: '#fff',
                                background: rc,
                                padding: '1px 4px',
                                borderRadius: 2,
                                whiteSpace: 'nowrap',
                                lineHeight: '13px',
                                fontWeight: 'bold',
                              }}>
                                {rl} {Math.round(region.confidence * 100)}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {/* Legende */}
                      <div style={{
                        marginTop: 6,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px 10px',
                        fontSize: 10,
                        color: colors.textSecondary || '#888',
                      }}>
                        {[
                          { type: 'empfaenger', color: '#00aaff', label: t('quick.aiOverlayRecipient') },
                          { type: 'absender', color: '#ff9800', label: t('quick.aiOverlaySender') },
                          { type: 'barcode', color: '#9c27b0', label: 'Barcode' },
                          { type: 'tracking', color: '#e91e63', label: 'Tracking' },
                          { type: 'gewicht', color: '#4caf50', label: t('quick.aiOverlayWeight') },
                          { type: 'label', color: '#607d8b', label: 'Label' },
                        ].filter(l => mlResult.regions.some(r => r.type === l.type))
                         .map(l => (
                          <div key={l.type} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <div style={{ width: 7, height: 7, background: l.color, borderRadius: 1 }} />
                            {l.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 📐 Bereich korrigieren — wenn KI-Erkennung aktiv */}
                  {mlResult && (
                    <div style={{
                      marginTop: '10px',
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                    }}>
                      <button
                        type="button"
                        onClick={() => setShowRegionCorrector(true)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          fontSize: '13px',
                          borderRadius: '6px',
                          border: `1px dashed ${colors.darkMode ? '#4a6fa5' : '#90caf9'}`,
                          background: colors.darkMode ? '#1a2a3d' : '#f0f7ff',
                          color: colors.darkMode ? '#90caf9' : '#1976d2',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                        }}
                      >
                        {t('quick.aiOverlayFixArea')}
                        <span style={{ fontSize: '11px', opacity: 0.7 }}>
                          {t('quick.aiOverlayWrongCrop')}
                        </span>
                      </button>
                    
                      {mlResult.regionCorrected && (
                        <span style={{ fontSize: '11px', color: '#4CAF50', whiteSpace: 'nowrap' }}>
                          {t('quick.aiOverlayCorrected')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ⚠️ Schaden erfassen — nur beim Bearbeiten, zugeklappt (bewusst unauffällig) */}
          {paketId && (
            <SchadenErfassen value={schaden} t={t} colors={colors}
              onChange={(neu) => { schadenAngefasstRef.current = true; setSchaden(neu); }}
              onHochgeladen={(id) => schadenFotosNeuRef.current.add(id)} />
          )}

          {/* Speichern/Abbrechen Buttons - unverändert */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', // Von 12px auf 8px reduziert
            marginTop: '12px', // Von 20px auf 12px reduziert
            marginBottom: '12px', // Von 20px auf 12px reduziert
            flexWrap: 'wrap' 
          }}>
            <button
              type="submit"
              style={{
                flex: '1 0 auto',
                padding: '12px',
                background: colors.button,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 'bold',
                cursor: 'pointer',
                minWidth: '100px'
              }}
            >
              {paketId
                ? t('common.saveChanges')
                : (photoSchnellStatus?.state === 'sent'
                    ? '✅ Speichern (Piep gehört)'
                    : t('common.save'))}
            </button>
            {renderWiesnSchnellSaveBar(false)}
            
            {/* 📦 MEHRFACH-PAKETE (1–5 + xx Pakete bis 999) & 🪵 PALETTEN-AUSWAHL */}
            {renderPaketAnzahlLeiste()}
            {renderPalettenAuswahl()}
            
            {/* 🗑️ PAKET LÖSCHEN - Nur im Bearbeitungs-Modus anzeigen */}
            {paketId && (
              <button
                type="button"
                onClick={handleDeletePackage}
                style={{
                  flex: '1 0 auto',
                  padding: '12px',
                  background: colors.darkMode ? '#991b1b' : '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  minWidth: '100px'
                }}
                title={t('modal.deleteForever')}
              >
                🗑️ {t('common.delete')}
              </button>
            )}
            
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: '1 0 auto',
                padding: '12px',
                background: '#ccc',
                color: '#333',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 'bold',
                cursor: 'pointer',
                minWidth: '100px'
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
          
          {/* AUSGEBLENDETE FELDER - dieser Teil bleibt unverändert */}
          {(!visibleSections.addressSection || !visibleSections.favoritesList || 
            !visibleSections.postalSection || !visibleSections.shelfSection || 
            !visibleSections.trackingSection || !visibleSections.dateSection) && (
            <div style={{ 
              marginTop: '24px',
              padding: '16px',
              background: colors.darkMode ? '#232830' : '#f5f5f5',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`
            }}>
              <h3 style={{ 
                margin: '0 0 16px 0',
                fontSize: '16px',
                color: colors.darkMode ? '#aaa' : '#666'
              }}>
                {t('modal.hiddenSections')}
              </h3>
              
              {/* Straße - wenn ausgeblendet */}
              {!visibleSections.addressSection && (
                <div style={{ marginBottom: '16px' }}>
                  <button 
                    onClick={() => setVisibleSections(prev => ({...prev, addressSection: true}))}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${colors.button}`,
                      color: colors.button,
                      borderRadius: '4px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>{texts[lang]?.street || t('packages.street')}</span>
                    <span style={{ fontSize: '14px' }}>{t('modal.showSection')}</span>
                  </button>
                  <div style={{ 
                    fontSize: '14px', 
                    color: colors.darkMode ? '#aaa' : '#666',
                    padding: '0 4px'
                  }}>
                    {streetAndNumber ? streetAndNumber : t('modal.notSpecified')}
                  </div>
                </div>
              )}
              
              {/* Favoriten - wenn ausgeblendet */}
              {!visibleSections.favoritesList && (
                <div style={{ marginBottom: '16px' }}>
                  <button 
                    onClick={() => setVisibleSections(prev => ({...prev, favoritesList: true}))}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${colors.button}`,
                      color: colors.button,
                      borderRadius: '4px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>{texts[lang]?.favoritesList || t('favorites.favoritesList')}</span>
                    <span style={{ fontSize: '14px' }}>{t('modal.showSection')}</span>
                  </button>
                  <div style={{ 
                    fontSize: '14px', 
                    color: colors.darkMode ? '#aaa' : '#666',
                    padding: '0 4px'
                  }}>
                    {favoriten.length} {t('modal.favoritesSaved')}
                  </div>
                </div>
              )}
              
              {/* PLZ und Ort - wenn ausgeblendet */}
              {!visibleSections.postalSection && (
                <div style={{ marginBottom: '16px' }}>
                  <button 
                    onClick={() => setVisibleSections(prev => ({...prev, postalSection: true}))}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${colors.button}`,
                      color: colors.button,
                      borderRadius: '4px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>{texts[lang]?.postalAndCity || t('packages.postalAndCity')}</span>
                    <span style={{ fontSize: '14px' }}>{t('modal.showSection')}</span>
                  </button>
                  <div style={{ 
                    fontSize: '14px', 
                    color: colors.darkMode ? '#aaa' : '#666',
                    padding: '0 4px'
                  }}>
                    {postalAndCity ? postalAndCity : t('modal.notSpecified')}
                  </div>
                </div>
              )}
              
              {/* Regalplatz - wenn ausgeblendet */}
              {!visibleSections.shelfSection && (
                <div style={{ marginBottom: '16px' }}>
                  <button 
                    onClick={() => setVisibleSections(prev => ({...prev, shelfSection: true}))}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${colors.button}`,
                      color: colors.button,
                      borderRadius: '4px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>{texts[lang]?.shelf || t('packages.shelfLocation')}</span>
                    <span style={{ fontSize: '14px' }}>{t('modal.showSection')}</span>
                  </button>
                  <div style={{ 
                    fontSize: '14px', 
                    color: colors.darkMode ? '#aaa' : '#666',
                    padding: '0 4px'
                  }}>
                    {regalplatz ? regalplatz : t('modal.notSpecified')}
                  </div>
                </div>
              )}
              
              {/* Tracking-Nummer - wenn ausgeblendet */}
              {!visibleSections.trackingSection && (
                <div style={{ marginBottom: '16px' }}>
                  <button 
                    onClick={() => setVisibleSections(prev => ({...prev, trackingSection: true}))}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${colors.button}`,
                      color: colors.button,
                      borderRadius: '4px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>{texts[lang]?.trackingNumber || t('packages.trackingNumber')}</span>
                    <span style={{ fontSize: '14px' }}>{t('modal.showSection')}</span>
                  </button>
                  <div style={{ 
                    fontSize: '14px', 
                    color: colors.darkMode ? '#aaa' : '#666',
                    padding: '0 4px'
                  }}>
                    {trackingNumber ? trackingNumber : t('modal.notSpecified')}
                  </div>
                </div>
              )}
              
              {/* 💰🎨⚖️ EXTRAS (Betrag, Farbe, Gewicht) - wenn ausgeblendet */}
              {!visibleSections.extrasSection && (
                <div style={{ marginBottom: '16px' }}>
                  <button 
                    onClick={() => setVisibleSections(prev => ({...prev, extrasSection: true}))}
                    style={{
                      background: betrag ? '#ffebee' : 'transparent',
                      border: `1px solid ${betrag ? '#f44336' : colors.button}`,
                      color: betrag ? '#f44336' : colors.button,
                      borderRadius: '4px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>💰🎨⚖️ {t('packages.extras') || 'Extras'}</span>
                    <span style={{ fontSize: '14px' }}>{t('modal.showSection')}</span>
                  </button>
                  <div style={{ 
                    fontSize: '14px', 
                    color: colors.darkMode ? '#aaa' : '#666',
                    padding: '0 4px'
                  }}>
                    {betrag ? <span style={{ color: '#f44336', fontWeight: 'bold' }}>💰 {parseFloat(betrag).toFixed(2).replace('.', ',')} €</span> : null}
                    {farbe ? <span style={{ marginLeft: betrag ? '8px' : 0 }}>🎨 {translatePackageColor(farbe, lang)}</span> : null}
                    {gewicht ? <span style={{ marginLeft: (betrag || farbe) ? '8px' : 0 }}>⚖️ {gewicht}</span> : null}
                    {!betrag && !farbe && !gewicht && t('modal.notSpecified')}
                  </div>
                </div>
              )}
              
              {/* Datumsinformationen - wenn ausgeblendet */}
              {!visibleSections.dateSection && (
                <div style={{ marginBottom: '16px' }}>
                  <button 
                    onClick={() => setVisibleSections(prev => ({...prev, dateSection: true}))}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${colors.button}`,
                      color: colors.button,
                      borderRadius: '4px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>{texts[lang]?.dateInfo || t('packages.dateAndStatus')}</span>
                    <span style={{ fontSize: '14px' }}>{t('modal.showSection')}</span>
                  </button>
                  <div style={{ 
                    fontSize: '14px', 
                    color: colors.darkMode ? '#aaa' : '#666',
                    padding: '0 4px'
                  }}>
                    <div>{t('modal.storedAt')}: {datum ? new Date(datum).toLocaleDateString() : t('modal.notSpecified')}</div>
                    {ausgegebenAm && <div>{t('modal.issuedAt')}: {new Date(ausgegebenAm).toLocaleDateString()}</div>}
                    {zurueckgeschickt && <div>{t('modal.statusReturned')}</div>}
                  </div>
                </div>
              )}
            </div>
          )}
          </>)}
        </form>
        
        {/* Erfolgs-Overlay */}
        {showSuccessOverlay && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(76, 175, 80, 0.9)', // Grüner Hintergrund mit Transparenz
            zIndex: 1200,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{
              fontSize: '24px',
              color: 'white',
              fontWeight: 'bold',
              textAlign: 'center',
              padding: '30px'
            }}>
              <div style={{ marginBottom: '16px', fontSize: '48px' }}>✅</div>
              <div style={{ marginBottom: '20px' }}>{t('modal.savedSuccessfully')}</div>
              
              {/* Details zum gespeicherten Paket */}
              <div style={{
                fontSize: '16px',
                fontWeight: 'normal',
                marginTop: '20px',
                padding: '15px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                textAlign: 'left'
              }}>
                {name && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>📦 {t('packages.name')}:</strong> {name}
                  </div>
                )}
                {streetAndNumber && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>📍 {t('packages.street')}:</strong> {streetAndNumber}
                  </div>
                )}
                {regalplatz && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>🗄️ {t('packages.shelfLocation')}:</strong> {regalplatz}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* OCR Diagnose-Overlay - NEU HIER */}
        {ocrDiagnosticData && (
          <div style={{
            margin: '10px 0',
            padding: '10px',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            background: colors.darkMode ? '#2a3040' : '#f5f5f5'
          }}>
            <h4 style={{margin: '0 0 8px 0'}}>OCR Diagnose:</h4>
            
            {ocrDiagnosticData.imagePreview && (
              <div style={{marginBottom: '10px'}}>
                <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>Verarbeitetes Bild:</p>
                <img 
                  src={ocrDiagnosticData.imagePreview}
                  alt="Zugeschnittenes Bild"
                  style={{maxWidth: '100%', maxHeight: '200px', border: '1px solid #ccc'}}
                />
              </div>
            )}
            
            {/* Erkannte Felder */}
            {ocrDiagnosticData.extractedFields && (
              <div style={{marginBottom: '10px', background: colors.darkMode ? '#222730' : '#e9e9e9', padding: '8px', borderRadius: '4px'}}>
                <p style={{margin: '0 0 4px 0', fontSize: '14px', fontWeight: 'bold'}}>Erkannte Daten:</p>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                  <div style={{padding: '4px 8px', background: ocrDiagnosticData.extractedFields.name === "✅" ? '#c8e6c9' : '#ffcdd2', color: '#333', borderRadius: '4px'}}>
                    Name: {ocrDiagnosticData.extractedFields.name}
                  </div>
                  <div style={{padding: '4px 8px', background: ocrDiagnosticData.extractedFields.street === "✅" ? '#c8e6c9' : '#ffcdd2', color: '#333', borderRadius: '4px'}}>
                    Straße: {ocrDiagnosticData.extractedFields.street}
                  </div>
                  <div style={{padding: '4px 8px', background: ocrDiagnosticData.extractedFields.postal === "✅" ? '#c8e6c9' : '#ffcdd2', color: '#333', borderRadius: '4px'}}>
                    PLZ/Ort: {ocrDiagnosticData.extractedFields.postal}
                  </div>
                  <div style={{padding: '4px 8px', background: ocrDiagnosticData.extractedFields.tracking === "✅" ? '#c8e6c9' : '#ffcdd2', color: '#333', borderRadius: '4px'}}>
                    Tracking: {ocrDiagnosticData.extractedFields.tracking}
                  </div>
                </div>
              </div>
            )}
            
            {ocrDiagnosticData.rawText && (
              <div>
                <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>Erkannter Text:</p>
                <pre style={{
                  margin: 0,
                  padding: '8px',
                  background: colors.darkMode ? '#1e222a' : '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflow: 'auto',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {ocrDiagnosticData.rawText || 'Kein Text erkannt'}
                </pre>
              </div>
            )}
            
            <div style={{marginTop: '8px', fontSize: '12px', color: colors.darkMode ? '#aaa' : '#666'}}>
              OCR-Status: {ocrLoading ? 'Verarbeite...' : 'Bereit'}
            </div>
          </div>
        )}

        {/* NEUES OVERLAY FÜR TEXTBLOCKS */}
        {showRecognitionOverlay && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 1300,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{
              background: colors.darkMode ? '#2d333b' : '#ffffff',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              maxWidth: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowRecognitionOverlay(false)}
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: colors.text
                }}
                title={t('common.close')}
              >
                ×
              </button>
              
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 'bold', textAlign: 'center' }}>
                {t('ocr.ocrStatus')}
              </h3>
              
              {/* Liste der erkannten Textblöcke */}
              {recognizedBlocks.length === 0 ? (
  <div style={{ padding: '12px', textAlign: 'center', color: colors.darkMode ? '#aaa' : '#666' }}>
    {t('ocr.noBlocksFound')}
  </div>
) : (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
    {recognizedBlocks.map(block => (
      <div
        key={block.id}
        style={{
          position: 'relative',
          background: colors.darkMode ? '#282c34' : 'white',
          borderRadius: '4px',
          border: `1px solid ${colors.border}`,
          marginBottom: '6px',
          fontSize: '13px',
          padding: '8px 12px',
          overflow: 'hidden'
        }}
        title={block.text}
      >
        {/* Textinhalt */}
        <div style={{ 
          position: 'relative',
          width: '100%',
          zIndex: 1,
        }}>
          <span
            style={{
              display: 'inline-block',
              color: colors.darkMode ? '#eee' : '#333',
              textAlign: 'left',
              wordBreak: 'break-word'
            }}
          >
            {block.text}
          </span>
          
          {/* Auswahlfelder absolut positioniert am rechten Rand */}
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '4px',
              zIndex: 2,
              background: colors.darkMode ? 'rgba(40, 44, 52, 0.7)' : 'rgba(255, 255, 255, 0.7)',
              borderRadius: '4px',
              padding: '2px'
            }}
          >
            {['name', 'street', 'postal', 'tracking'].map(type => {
              const color =
                type === 'name'
                  ? '#0080ff'
                  : type === 'street'
                  ? '#4CAF50'
                  : type === 'postal'
                  ? '#FFC107'
                  : '#E91E63';
              const bgColor =
                type === 'name'
                  ? 'rgba(0,128,255,0.18)'
                  : type === 'street'
                  ? 'rgba(76,175,80,0.18)'
                  : type === 'postal'
                  ? 'rgba(255,193,7,0.18)'
                  : 'rgba(233,30,99,0.18)';
              return (
                <div
                  key={type}
                  onClick={() => updateBlockType(block.id, type)}
                  style={{
                    width: 20,
                    height: 20,
                    border: `2px solid ${color}`,
                    background: block.type === type ? bgColor : 'transparent',
                    borderRadius: 3,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                  title={`Als ${type === 'name'
                    ? 'Name'
                    : type === 'street'
                    ? 'Straße'
                    : type === 'postal'
                    ? 'PLZ/Ort'
                    : 'Tracking'} ${block.type === type ? 'abwählen' : 'markieren'}`}
                >
                  {block.type === type && (
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        background: color,
                        borderRadius: 2
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    ))}
  </div>
)}
              
              {/* Hinweis zur Bearbeitung */}
              <div style={{ 
                marginTop: '16px',
                padding: '12px',
                background: colors.darkMode ? '#2a3040' : '#f5f5f5',
                borderRadius: '6px',
                fontSize: '12px',
                color: colors.darkMode ? '#ccc' : '#333',
                border: `1px solid ${colors.border}`
              }}>
                <strong>Hinweis:</strong> Die erkannte Textblöcke können automatisch ausgefüllt werden. Bitte überprüfen Sie die Angaben vor dem Speichern.
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 📮 PLZ-Kreis Nachfrage-Dialog beim Speichern („Was ist Empfänger, was ist Absender?") */}
      {showPlzModal && plzAbweichung && (
        <div
          data-testid="plz-check-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowPlzModal(false); }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '460px',
              background: colors.darkMode ? '#22272e' : '#ffffff',
              border: '2px solid #f59e0b',
              borderRadius: '14px',
              padding: '20px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
              color: colors.text,
            }}
          >
            <div style={{ fontSize: '18px', fontWeight: 800, color: colors.darkMode ? '#fcd34d' : '#b45309', marginBottom: '8px' }}>
              ⚠️ {t('plzCheck.modalTitle', 'Abweichende Postleitzahl — Was ist Empfänger, was ist Absender?')}
            </div>
            <div style={{ fontSize: '14px', lineHeight: 1.45, marginBottom: '14px', color: colors.text }}>
              {t('plzCheck.modalBody', {
                plz: plzAbweichung.aktuellePlz,
                kreis: plzAbweichung.shopKreisLabel,
                defaultValue: `Die erkannte PLZ ${plzAbweichung.aktuellePlz} liegt außerhalb deines üblichen PLZ-Kreises (${plzAbweichung.shopKreisLabel}). Manchmal vertauscht die KI auf dem Etikett Absender und Empfänger.`,
              })}
            </div>

            {/* Gegenüberstellung Empfänger vs. Absender */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginBottom: '16px',
              padding: '12px',
              borderRadius: '10px',
              background: colors.darkMode ? '#1b1f24' : '#fffbeb',
              border: `1px solid ${colors.darkMode ? '#4b3814' : '#fde68a'}`,
              fontSize: '13px',
            }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: colors.textSecondary || '#888' }}>
                  📦 {t('plzCheck.currentRecipient', 'Aktuell als Empfänger (Abholer) eingetragen')}:
                </div>
                <div style={{ fontWeight: 700, fontSize: '15px', marginTop: '2px' }}>
                  {name || '—'}
                </div>
                {(streetAndNumber || postalAndCity) && (
                  <div style={{ color: colors.textSecondary || '#666' }}>
                    {[streetAndNumber, postalAndCity].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>

              {aktiverAbsender && String(aktiverAbsender.name || '').trim() && (
                <div style={{ borderTop: `1px dashed ${colors.darkMode ? '#374151' : '#fcd34d'}`, paddingTop: '8px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: colors.textSecondary || '#888' }}>
                    📥 {t('plzCheck.currentSender', 'Erkannter Absender auf dem Paket')}:
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '15px', marginTop: '2px', color: plzAbweichung.absenderPasstZumShop ? '#16a34a' : colors.text }}>
                    {aktiverAbsender.name}
                    {plzAbweichung.absenderPasstZumShop && (
                      <span style={{ marginLeft: '6px', fontSize: '11px', padding: '1px 6px', borderRadius: '6px', background: '#dcfce7', color: '#166534' }}>
                        ✓ PLZ {plzAbweichung.absenderPlz} passt!
                      </span>
                    )}
                  </div>
                  {(aktiverAbsender.street || aktiverAbsender.strasse || aktiverAbsender.postal || aktiverAbsender.plz || aktiverAbsender.city || aktiverAbsender.ort) && (
                    <div style={{ color: colors.textSecondary || '#666' }}>
                      {[
                        aktiverAbsender.street || aktiverAbsender.strasse,
                        [aktiverAbsender.postal || aktiverAbsender.plz, aktiverAbsender.city || aktiverAbsender.ort].filter(Boolean).join(' ')
                      ].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {aktiverAbsender && String(aktiverAbsender.name || '').trim() ? (
                <button
                  type="button"
                  data-testid="plz-modal-swap-save"
                  onClick={() => {
                    const swapped = tauscheEmpfaengerUndAbsender();
                    if (swapped) fuehreSpeichernAus(swapped);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#f59e0b',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  🔄 {t('plzCheck.swapAndSave', {
                    sender: String(aktiverAbsender.name).trim(),
                    defaultValue: `Tauschen & Speichern — Empfänger ist „${String(aktiverAbsender.name).trim()}"`,
                  })}
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="plz-modal-move-to-sender"
                  onClick={verschiebeAktuellenInAbsenderUndLeereEmpfaenger}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#f59e0b',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  ✏️ {t('plzCheck.isSenderEnterRecipient', {
                    name: name || '—',
                    defaultValue: `„${name || '—'}" ist der Absender — Empfänger jetzt eingeben`,
                  })}
                </button>
              )}

              <button
                type="button"
                data-testid="plz-modal-confirm-save"
                onClick={() => {
                  setPlzCheckBestaetigt(true);
                  setShowPlzModal(false);
                  fuehreSpeichernAus();
                }}
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  borderRadius: '10px',
                  border: `1.5px solid ${colors.darkMode ? '#4b5563' : '#cbd5e1'}`,
                  background: colors.darkMode ? '#2d333b' : '#f8fafc',
                  color: colors.text,
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                ✓ {t('plzCheck.keepAndSave', {
                  name: name || '—',
                  defaultValue: `Stimmt so — „${name || '—'}" ist wirklich der Empfänger (Speichern)`,
                })}
              </button>

              <button
                type="button"
                data-testid="plz-modal-cancel"
                onClick={() => setShowPlzModal(false)}
                style={{
                  width: '100%',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'transparent',
                  color: colors.textSecondary || '#888',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel', 'Abbrechen & im Formular prüfen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🛡️ Eigene Datenschutzerklärung / AGB-Bestätigung durch den Kunden vor dem Speichern */}
      {showPrivacyModal && (
        <div
          data-testid="privacy-consent-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowPrivacyModal(false); }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.78)',
            zIndex: 1250,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '460px',
              background: colors.darkMode ? '#22272e' : '#ffffff',
              border: '2px solid #16a34a',
              borderRadius: '14px',
              padding: '20px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.38)',
              color: colors.text,
            }}
          >
            <div style={{ fontSize: '18px', fontWeight: 800, color: colors.darkMode ? '#86efac' : '#166534', marginBottom: '8px' }}>
              🛡️ {t('ui.tr.d31') !== 'ui.tr.d31' ? t('ui.tr.d31') : ({ de: 'Datenschutz & Aufbewahrungsbedingungen', nl: 'Privacy & Bewaarvoorwaarden' }[lang] || 'Privacy & Storage Terms')}
            </div>
            <div style={{
              fontSize: '14px',
              lineHeight: 1.5,
              marginBottom: '16px',
              padding: '12px 14px',
              borderRadius: '10px',
              background: colors.darkMode ? '#1b1f24' : '#f8fafc',
              border: `1px solid ${colors.darkMode ? '#374151' : '#cbd5e1'}`,
              whiteSpace: 'pre-wrap',
            }}>
              {getPrivacyConsentText(lang)}
            </div>
            <div style={{
              fontSize: '13px',
              marginBottom: '14px',
              color: colors.textSecondary || '#666',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span>👤 <strong>{name || '—'}</strong></span>
              {regalplatz && <span>🗄️ {t('ui.modal.shelfLabel')} <strong>{regalplatz}</strong></span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                type="button"
                data-testid="privacy-consent-accept"
                onClick={() => {
                  setPrivacyConfirmed(true);
                  setShowPrivacyModal(false);
                  fuehreSpeichernAus({ ...(pendingSaveOverrides || {}), _privacyBypass: true });
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#16a34a',
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: '15px',
                  cursor: 'pointer',
                }}
              >
                ✅ {t('ui.tr.d32') !== 'ui.tr.d32' ? t('ui.tr.d32') : ({ de: 'Ich akzeptiere — Jetzt speichern', nl: 'Ik ga akkoord — Nu opslaan' }[lang] || 'I accept — Save now')}
              </button>
              <button
                type="button"
                data-testid="privacy-consent-cancel"
                onClick={() => setShowPrivacyModal(false)}
                style={{
                  width: '100%',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'transparent',
                  color: colors.textSecondary || '#888',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel', 'Abbrechen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📐 RegionSelector Overlay */}
      {showRegionCorrector && mlResult && imageUrl && (
        <RegionSelector
          imageUrl={imageUrl}
          initialBox={mlResult.regions?.find(r => r.type === 'empfaenger')?.box || null}
          imageSize={mlResult.imageSize || null}
          onConfirm={handleRegionCorrection}
          onCancel={() => setShowRegionCorrector(false)}
          colors={colors}
          loading={regionCorrecting}
          allRegions={mlResult.regions || []}
        />
      )}
    </div>
  );
}

export default NewPackageModal;